
import React, { useState, useMemo, useRef, useEffect, Suspense, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../services/localeHelpers';
import { MapContainer, TileLayer, Marker, useMapEvents, Pane, ZoomControl, Polyline, Circle, Popup, WMSTileLayer, useMap, GeoJSON, Tooltip, CircleMarker } from 'react-leaflet';
import { CampingSpot, Coordinates, MapBounds, AppUser, MountainRoad, GpxRoute, RouteRegion, ChargingStation, WikiSpot, RoadBarrier, HikingTrail, ElevationPoint, SvvCamera, WastePoint, WastePointType, BathingPlace, RouteType } from '../types';
import { MAP_LAYERS, TERRAIN_META, isInPrescannedCounty, APP_URL, TILE_CACHE_ENABLED, TILE_PREFETCH_ENABLED } from '../constants';
import { BRAND, BRAND_DARK } from '../constants/mapColors';
import { Zap, Navigation2, Crosshair, ZoomIn, MapPin, Lock, AlertCircle, Phone, Building2, Sparkles, Globe, CreditCard, Home, Terminal, Mountain, Route, Loader2, RefreshCcw, ExternalLink, Image as ImageIcon, Plus, MousePointerClick, Snowflake, Calendar, Key, Ban, X, Share2, Download, Radio, Users, Trash2, ChevronDown, ChevronUp, Camera, Orbit, Fish, Search, MoreVertical, Compass, Flame, ShieldAlert, Star, Navigation, AlertTriangle, Footprints, Bike } from 'lucide-react';
import { MapFlyHandler, FitActiveTrail, createCustomIcon, createMixedClusterIcon, junctionIcon, ForceMapResize, barrierIcon, chargerIcon, wikiIcon } from './map/MapUtils';
import { markProgrammaticMove, isProgrammaticMove } from '../utils/mapMoveOrigin';
import { isSpotInEnabledCountry } from '../services/firebaseService';
import ClusteredSpotMarkers from './map/ClusteredSpotMarkers';
import PopupLocationImage from './map/PopupLocationImage';
import ClusteredChargerMarkers from './map/ClusteredChargerMarkers';
import ClusteredWikiMarkers from './map/ClusteredWikiMarkers';
import ClusteredWildlifeMarkers from './map/ClusteredWildlifeMarkers';
import MapLayersControl from './map/MapLayersControl';
import MobileMapToolsSheet from './map/MobileMapToolsSheet';
import OfflineMapManager from './map/OfflineMapManager';
import CachedTileLayer from './map/CachedTileLayer';
import { cachedIcon, getPointPosition } from './map/markerIconCache';
import TilePrefetcher from './map/TilePrefetcher';
import HotRegionWarmer from './map/HotRegionWarmer';
import OfflineSuggestNudge from './map/OfflineSuggestNudge';
import RouteLayer from './map/RouteLayer';
import CoverageControl, { CoverageProvider } from './map/CoverageControl';
import CoverageLayer from './map/CoverageLayer';
import { useDenmarkParking } from '../hooks/useDenmarkParking';
import { useRoadBarriers } from '../hooks/useRoadBarriers';
import { useChargingStations } from '../hooks/useChargingStations';
import { useMountainRoads } from '../hooks/useMountainRoads';
import { useWinterRoads } from '../hooks/useWinterRoads';
import { useWildlifeLayer, WildlifeMapObservation } from '../hooks/useWildlifeLayer';
import { useFishingLayer } from '../hooks/useFishingLayer';
import { useWasteStations } from '../hooks/useWasteStations';
import { useBathingPlaces } from '../hooks/useBathingPlaces';
import { useTrafficEvents } from '../hooks/useTrafficEvents';
import { useSwedishRoadNetwork } from '../hooks/useSwedishRoadNetwork';
import { fetchSwedishBathingProfile, isTemperatureStale } from '../services/bathingService';
import { useLayerErrorTracker } from '../hooks/useLayerErrorTracker';
import ExternalSourceErrorBadge from './map/ExternalSourceErrorBadge';
import type { FishAbundanceCluster } from '../services/fishingService';
import L from 'leaflet';
import { getDistance, isPointNearPolyline, getPolylineBbox, calculateGeoJsonLength, getGeoJsonLabelPoint } from '../services/geometryUtils';
import { getVehicleMarkerHtml, ensureVehicleMarkerStyles, getTowVehicleMarkerHtml, getTrailerMarkerHtml, getVehicleDrivingMarkerHtml } from '../utils/vehicleMarkerHtml';
import { fetchElevation } from '../services/elevationService';
import { fetchTrailInfoAtPoint, TrailClickInfo, fetchGeoNorgeNearbyRoutes } from '../services/geonorgeRoutesService';
import { fetchNearbyForestRoad } from '../services/nvdbService';
import { hasPaidAccess, hasTierAccess } from '../services/accessControl';
import { usePremiumNudge } from '../contexts/PremiumNudgeContext';
import ElevationProfile from './routes/ElevationProfile';
import { fetchCamerasNearRoute } from '../services/svvCameraService';
import DrawingActiveLayerComponent from './map/DrawingActiveLayer';
import DrawingLayerComponent from './map/DrawingLayer';
import DrawingEditLayerComponent from './map/DrawingEditLayer';
import UserContentLayerComponent from './map/UserContentLayer';
import TrailPhotoLayer from './map/TrailPhotoLayer';
import NordicComingSoonOverlay from './map/NordicComingSoonOverlay';
import { Capacitor } from '@capacitor/core';
import RoutePlanPreview from './map/RoutePlanPreview';
import { useViewportCountry } from '../hooks/useViewportCountry';
import { useFireDanger } from '../hooks/useFireDanger';
import { useContextualAlerts } from '../hooks/useContextualAlerts';
import FireDangerModal from './map/FireDangerModal';
import { getHazardIcon } from './map/hazardIcon';
import SpotPreviewOverlay from './map/SpotPreviewOverlay';
import { pickPhoto } from '../services/cameraService';
import { useToast } from '../contexts/ToastContext';
import { useLocationGate } from '../contexts/LocationGateContext';
import { isLayerSupportedInCountry } from '../utils/countryDetection';
import { isPointNearViewportEdge } from '../utils/isPointNearViewportEdge';
import { isAutocamperSpot } from '../utils/autocamperSpots';
import { openExternalNavigation } from '../services/navigationService';
import { recordTileLoad } from '../services/performanceService';

const Route3DFlyover = React.lazy(() => import('./routes/Route3DFlyover'));

/**
 * Baselag-flislag: bruker den plattform-uavhengige cachen når feature-flagget
 * er på (ambient-caching som virker også på iOS), ellers vanlig Leaflet-lag.
 * Props er identiske (CachedTileLayer godtar TileLayerProps).
 */
const BaseTileLayer = TILE_CACHE_ENABLED ? CachedTileLayer : TileLayer;

/**
 * Delt, tom mengde for `activeAnalysisIds`-defaulten. Som `= new Set()` i
 * destruktureringen ble det et NYTT objekt hver render, som slo ut
 * React.memo(ClusteredSpotMarkers) — se map/markerIconCache.ts.
 */
const EMPTY_ANALYSIS_IDS: ReadonlySet<string> = new Set<string>();

// Under dette zoom-nivået regnes kartet som «oversikt» → sekundære markør-badges
// (vann/foto/rasteplass, klasse fc-marker-secondary) skjules via CSS så nålene er
// rolige på avstand. Fra og med dette nivået («detalj») vises alt igjen. Klassen
// fc-zoom-overview settes på leaflet-container i MapEventsHandler. Ren CSS-toggle →
// ingen re-generering av markør-ikoner ved zoom. Se docs/design-marker-signal-simplification.md.
const MARKER_DETAIL_ZOOM = 13;
const syncMarkerZoomClass = (map: L.Map) => {
  try { map.getContainer().classList.toggle('fc-zoom-overview', map.getZoom() < MARKER_DETAIL_ZOOM); } catch {}
};

interface MapProps {
  spots: CampingSpot[];
  center: Coordinates;
  mapCommand?: { center: Coordinates; zoom: number; bounds?: [[number, number], [number, number]]; offsetY?: number; id: number } | null;
  currentViewBounds: MapBounds | null;
  zoom: number;
  selectedSpotId: string | null;
  activeHikingTrailId: string | null;
  onSelectSpot: (id: string) => void;
  onSelectHikingTrail: (id: string | null) => void;
  onMapMove: (coords: Coordinates, zoom: number, viewBounds: MapBounds, userDragged?: boolean) => void;
  onMapClick: (coords: Coordinates | null) => void;
  onMapLongPress?: (coords: Coordinates) => void;
  onMapContextMenu?: (coords: Coordinates) => void;
  onSearchThisArea: () => void;
  /** Sirkelen vi sist søkte plasser i. Tegnes så brukeren ser hvilket område som
   *  faktisk ble søkt — radien har et hardt tak, så ved lav zoom er den mindre enn
   *  utsnittet, og da må det være synlig. */
  searchCircle?: { lat: number; lon: number; radiusKm: number } | null;
  onDeepAnalyzeViewport?: () => void;
  layers: {
    topo: boolean; satellite: boolean; protected: boolean; warnings: boolean;
    hiking: boolean; hikingFoot: boolean; hikingSki: boolean; hikingBike: boolean; hikingOther: boolean;
    restAreas: boolean; osmAmenities: boolean;
    commercialCampsites: boolean; barriers: boolean; chargers: boolean; dumpStations: boolean; coastal: boolean;
    // `commercialRvParks` og `playgrounds` ble lest i komponenten uten å stå i
    // prop-typen — begge ga TS2339 lenge før autocamper-laget kom til.
    commercialRvParks: boolean; playgrounds: boolean;
    mountainRoads: boolean; gpxRoutes: boolean; winterRoads: boolean;
    algorithmic: boolean;
    wildlife: boolean;
    fishing: boolean;
    vehicleFiltered: boolean;
    propertyBoundaries: boolean;
    precipitationRadar: boolean;
    hillshade: boolean;
    webcams: boolean;
    trafficEvents: boolean;
    wasteStations: boolean;
    parkingDk: boolean;
    bathingPlaces: boolean;
    discoverAttractions: boolean;
    forestRoads: boolean;
    tractorRoads: boolean;
    serviceRoads: boolean;
    svvVegnett: boolean;
    svvVegdekke: boolean;
    svvHoyde: boolean;
    svvTrafikk: boolean;
    svvHoydekontur?: boolean;
    svvFartsgrense?: boolean;
  };
  onUpdateLayers: (layers: MapProps['layers']) => void;
  isLoading: boolean;
  isFetchingBounds?: boolean;
  spotsReady?: boolean;
  isFirstTimeLoad?: boolean;
  scanProgress?: { phase: string; current: number; total: number } | null;
  onOpenDebug?: () => void;
  onOpenAuth: () => void;
  currentUser?: AppUser | null;
  activeAnalysisIds?: Set<string>;
  /**
   * Kartet er dekket av en fullskjerm-modal (legg til plass) og skal ikke tegnes.
   *
   * Kartet blir MOUNTED — vi vil ikke miste senter/zoom, ruter, sporing eller
   * auto-nav-tilstand — men `display: none` gjør at nettleseren hopper over layout
   * og paint for opptil flere tusen skyggelagte markør-noder og hundrevis av
   * satellittfliser. Uten dette tegnet Android to satellittkart samtidig mens
   * brukeren registrerte en plass (AddSpotModal har sitt eget MapContainer).
   *
   * `ForceMapResize` har allerede en ResizeObserver på containeren, så
   * `invalidateSize()` kjører av seg selv når kartet vises igjen.
   */
  isCoveredByModal?: boolean;
  isDev: boolean;
  onAnalyzeRoute?: (coords: Coordinates[], routeId: string) => void;
  onLoadCachedRouteSpots?: (spotIds: string[]) => void;
  onPeakClick?: (coords: Coordinates) => void;
  onRouteActivated?: (coords: Coordinates[] | null) => void;
  isAnalyzingRoute?: boolean;
  onSelectRoute?: (routeId: string) => void;

  routes?: GpxRoute[];
  activeRouteIds?: Set<string>;
  setActiveRouteIds?: (ids: Set<string>) => void;
  previewRegion?: RouteRegion | null;
  previewFilteredRouteIds?: Set<string> | null;
  onRegionLoading?: (loading: boolean) => void;

  activeRoutePath?: Coordinates[] | null;
  followRouteCoords?: Coordinates[] | null;
  followRouteNearestIndex?: number | null;
  routeCategoryFilters?: Set<string>;
  routeVehicleFilterOff?: boolean;
  onToggleRouteVehicleFilter?: () => void;

  loadingState?: {
    restAreas?: boolean;
    osmAmenities?: boolean;
    commercialCampsites?: boolean;
    barriers?: boolean;
    chargers?: boolean;
    coastal?: boolean;
    mountainRoads?: boolean;
    winterRoads?: boolean;
    wildlife?: boolean;
    webcams?: boolean;
    trafficEvents?: boolean;
    wasteStations?: boolean;
    parkingDk?: boolean;
    bathingPlaces?: boolean;
    dumpStations?: boolean;
    routeFricamp?: boolean;
    routeRestAreas?: boolean;
    routeCampsites?: boolean;
    routeChargers?: boolean;
    routeWildlife?: boolean;
  };
  errorState?: Partial<Record<string, boolean>>;

  wikiSpots?: WikiSpot[];

  highlightedGeoJson?: any;
  draftRouteGeoJson?: any[];

  isMobileListOpen?: boolean;
  sidebarWidthPx?: number;
  onCancelLoading?: () => void;
  isPaidUser?: boolean;
  userTier?: number;
  onOpenUpgrade?: () => void;
  topUiOffsetPx?: number;
  shareContextText?: string;

  // Turlogg
  trackPoints?: import('../types').TrackPoint[];
  trackingStatus?: 'idle' | 'recording' | 'paused';
  onStartTracking?: () => void;
  onOpenMyTracks?: () => void;
  isFollowingRoute?: boolean;
  followMapOrientation?: 'north_up' | 'heading_up';
  onToggleFollowMapOrientation?: () => void;
  nearestRoutePoint?: Coordinates | null;
  activeFollowTarget?: {
    id: string;
    location: Coordinates;
    name: string;
    source: 'spot' | 'route_poi';
    spotId: string | null;
  } | null;

  // Brukerposisjon (GPS-prikk)
  userPosition?: Coordinates | null;
  userHeading?: number | null;
  trailerHeading?: number | null;
  gpsLastUpdated?: number;
  onGpsPositionAcquired?: (coords: Coordinates) => void;
  // Auto-nav: appen følger GPS-prikken automatisk (idle + i bevegelse). Når true
  // sentrerer kartet én gang, og panorerer deretter kun når prikken nærmer seg
  // kanten — på samme zoom, slik at kun nye kant-tiles lastes.
  autoFollowUser?: boolean;
  // Kandidat-fangst under auto-nav: kart-trykk → «Fricamp?»-boble → onDropCandidate.
  onDropCandidate?: (coords: Coordinates) => void;
  autonavCandidates?: Array<{ id: string; location: Coordinates }>;

  // Sanntids-posisjonsdeling (gruppe-scoped)
  nearbyUsers?: import('../types').LiveLocation[];
  isSharing?: boolean;
  onStartSharing?: (groupId: string, groupName: string) => void;
  onStopSharing?: () => void;
  shareError?: string | null;

  collaborativeMaps?: import('../types').CollaborativeMap[];
  activeGroupId?: string | null;
  activeGroupName?: string | null;
  groupViewers?: import('../types').LiveViewer[];

  myContentLines?: any[];
  myContentAreas?: any[];
  myContentWaypoints?: any[];
  /** Waypoints to highlight after "show folder on map" — rendered unconditionally. */
  focusedWaypoints?: any[];
  drawingState?: any;
  drawingToolType?: any;
  drawingPoints?: any[];
  onDrawingMapClick?: (coords: [number, number]) => void;
  onDeleteContent?: (id: string, type: string) => void;
  onEditContent?: (id: string, type: string) => void;
  drawingEditPoints?: any[];
  drawingEditToolType?: any;
  onDrawingEditUpdatePoint?: (idx: number, coords: Coordinates) => void;
  onDrawingEditInsertPoint?: (idx: number, coords: Coordinates) => void;
  onDrawingEditRemovePoint?: (idx: number) => void;
  isMyContentOpen?: boolean;
  coordinatePin?: Coordinates | null;
  onClearCoordinatePin?: () => void;
  is3DMode?: boolean;
  onToggle3D?: () => void;
  onToggleRecorder?: () => void;
  recorderVisible?: boolean;
  onPromoteBathingPlace?: (spot: any) => void;
  onEnsureSpotPersisted?: (spotId: string) => Promise<void>;
  onRequestCountry?: (country: string) => void;
  nordicEnabledCountries?: Partial<Record<string, boolean>>;
  nordicConfigReady?: boolean;
  isAdmin?: boolean;
  adminBadgeCount?: number;
  onOpenAdmin?: () => void;
  onOpenAdminInbox?: () => void;
  authReady?: boolean;

  selectedRouteId?: string | null;

  // Route planning (on-map)
  barrierHighlightSpot?: CampingSpot | null;
  onClearBarrierHighlight?: () => void;
  routePlanAlternatives?: import('../types').RoutePlanAlternative[];
  routePlanSelectedIndex?: number | null;
  onRoutePlanSelectIndex?: (idx: number) => void;
  isRoutePlanning?: boolean;
  onAddRouteStop?: (coords: Coordinates) => void;
  routePlanStartCoords?: Coordinates | null;
  routePlanEndCoords?: Coordinates | null;
  routePlanStopCoords?: Coordinates[];
  onRemoveRouteStop?: (idx: number) => void;
  /** Reise-copilot: trykk på nummerert overnattingsstopp åpner plasskortet i stedet
   *  for å fjerne det (fjerning skjer via ×-knappen i RoutePlanBar). */
  onSelectRouteStop?: (idx: number) => void;
  routeHighlightWebcams?: SvvCamera[];
  /** Reise-copilot: kuraterte severdigheter langs planlagt rute — kategoriserte,
   *  trykkbare markører (utforskningsmodus). Strukturell type (unngår import). */
  routeAttractions?: { name: string; latitude: number; longitude: number; url?: string; category?: string; imageUrl?: string; description?: string; matchedInterests?: string[] }[];
  /** Trykk på severdighet-markør → åpne EKTE plasskort (promotering skjer i App). */
  onRouteAttractionTap?: (a: { name: string; latitude: number; longitude: number; url?: string; category?: string; imageUrl?: string; description?: string; matchedInterests?: string[] }) => void;

  discoverItems?: any[];
  discoverHighlight?: { latitude: number; longitude: number } | null;
  onClearHighlight?: () => void;
  onDiscoverMarkerClick?: (id: string, lat: number, lon: number) => void;
  onContextualAlertsChange?: (alerts: any[]) => void;
  nearestSpot?: CampingSpot | null;
  nearestSpotDistanceM?: number | null;
  onShowSpotDetails?: (id: string) => void;
  onDownloadSpotArea?: (id: string) => void;
  onCreateFromPhoto?: (files: File[], coords?: Coordinates | null) => void;
  showLayerTip?: boolean;
  onDismissLayerTip?: () => void;
}

const MIN_ZOOM_FOR_SPOTS = 9;
// Speiles fra App.tsx FREE_ZOOM_MAX. Programmatic zoom-handlinger (cluster-klikk,
// "fly til posisjon", etc.) skal ikke kunne sende ikke-betalte brukere forbi denne.
const FREE_ZOOM_MAX = 13;
// Zoom-nivå der kontekstuelle POI-lag (fiske, bading, dyreliv, avfall) auto-aktiveres
// uten at brukeren trenger å slå på lag manuelt.
const DETAIL_ZOOM_THRESHOLD = 16;
const capZoomForFree = (target: number, isPaid: boolean): number =>
  isPaid ? target : Math.min(target, FREE_ZOOM_MAX);

const reduceSpotsForViewport = (
  spots: CampingSpot[],
  bounds: MapBounds | null,
  zoom: number,
  selectedSpotId: string | null
): CampingSpot[] => {
  if (!bounds || zoom >= 14 || spots.length <= 100) return spots;

  // Brukerens egne private waypoints skal aldri filtreres bort av viewport-reduksjon —
  // de er eierens personlige data og må alltid være synlige hvor som helst.
  const privateWaypointSpots: CampingSpot[] = [];
  const reducible: CampingSpot[] = [];
  for (const s of spots) {
    if (s.id.startsWith('wpcard-')) privateWaypointSpots.push(s);
    else reducible.push(s);
  }
  spots = reducible;

  const latSpan = Math.max(bounds.latMax - bounds.latMin, 0.01);
  const lonSpan = Math.max(bounds.lonMax - bounds.lonMin, 0.01);
  const targetCols = zoom <= 9 ? 8 : zoom === 10 ? 10 : zoom === 11 ? 12 : zoom === 12 ? 14 : 16;
  const targetRows = zoom <= 9 ? 6 : zoom === 10 ? 7 : zoom === 11 ? 8 : zoom === 12 ? 10 : 12;
  const maxPerCell = zoom <= 9 ? 1 : zoom === 10 ? 2 : zoom === 11 ? 2 : zoom === 12 ? 3 : 4;
  const maxTotal = zoom <= 9 ? 48 : zoom === 10 ? 70 : zoom === 11 ? 120 : zoom === 12 ? 180 : 250;
  const cellLat = latSpan / targetRows;
  const cellLon = lonSpan / targetCols;
  const buckets = new Map<string, CampingSpot[]>();

  const getPriority = (spot: CampingSpot) => {
    if (spot.id === selectedSpotId) return Number.MAX_SAFE_INTEGER;
    let score = Number(spot.aiResult?.score ?? spot.score ?? 0);
    if (spot.isVerified) score += 50;
    if (spot.sourceLevel === 4 || spot.spotType === 'confirmed') score += 40;
    if (spot.spotType === 'commercial_campsite') score += 20;
    if (spot.spotType === 'rasteplass') score += 10;
    return score;
  };

  for (const spot of spots) {
    const row = Math.max(0, Math.min(targetRows - 1, Math.floor((spot.location.latitude - bounds.latMin) / cellLat)));
    const col = Math.max(0, Math.min(targetCols - 1, Math.floor((spot.location.longitude - bounds.lonMin) / cellLon)));
    const key = `${row}:${col}`;
    const cell = buckets.get(key);
    if (cell) {
      cell.push(spot);
    } else {
      buckets.set(key, [spot]);
    }
  }

  const reduced: CampingSpot[] = [];
  for (const cell of buckets.values()) {
    cell.sort((a, b) => getPriority(b) - getPriority(a));
    reduced.push(...cell.slice(0, maxPerCell));
  }

  reduced.sort((a, b) => getPriority(b) - getPriority(a));
  return [...privateWaypointSpots, ...reduced.slice(0, maxTotal)];
};

const getSatelliteUrl = (lat: number, lon: number): string => {
  const z = 15;
  const n = Math.pow(2, z);
  const x = Math.floor(((lon + 180) / 360) * n);
  const y = Math.floor(((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * n);
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;
};

const getSpotMiniIcon = (spot: CampingSpot) => {
  if (spot.spotType === 'rasteplass') return <MapPin size={11} className="text-blue-500" />;
  if (spot.spotType === 'commercial_campsite') return <MapPin size={11} className="text-amber-500" />;
  if (spot.spotType === 'coastal') return <MapPin size={11} className="text-cyan-500" />;
  return <MapPin size={11} className="text-emerald-500" />;
};

const targetPinIcon = L.divIcon({
  className: 'target-pin',
  html: `
    <div style="position: relative; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; transform: translateY(-20px);">
      <div style="position: absolute; width: 40px; height: 40px; border: 3px solid ${BRAND}; border-radius: 50%; animation: ping 1.5s infinite; opacity: 0.6;"></div>
      <div style="position: absolute; width: 20px; height: 20px; background: ${BRAND}; border: 3px solid white; border-radius: 50%; box-shadow: 0 5px 15px rgba(0,0,0,0.4);"></div>
    </div>
  `,
  iconSize: [40, 40],
  iconAnchor: [20, 20]
});

const _userCarIconCache: Record<string, L.DivIcon> = {};
const getUserCarMarkerIcon = (color: string, pulseColor: string) => {
  const key = `${color}_${pulseColor}`;
  if (_userCarIconCache[key]) return _userCarIconCache[key];
  const icon = L.divIcon({
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    html: `<div style="width:40px;height:40px;display:flex;align-items:center;justify-content:center">
      <div style="position:absolute;inset:0;border-radius:50%;background:${pulseColor};animation:user-car-pulse 2s ease-in-out infinite"></div>
      <div style="width:32px;height:32px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>
      </div>
    </div>
`,
  });
  _userCarIconCache[key] = icon;
  return icon;
};

const _vehicleAvatarCache: Record<string, L.DivIcon> = {};
const getVehicleAvatarIcon = (category: string, freshnessColor: string, _pulseColor: string, _photoUrl?: string | null, _aiAvatarSvg?: string | null, customColor?: string | null) => {
  const key = `driving_${category}_${freshnessColor}_${customColor || ''}`;
  if (_vehicleAvatarCache[key]) return _vehicleAvatarCache[key];
  const size = 48;
  const icon = L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: getVehicleDrivingMarkerHtml({ category: category as any, customColor, size, freshnessColor }),
  });
  _vehicleAvatarCache[key] = icon;
  return icon;
};

const _towIconCache: Record<string, L.DivIcon> = {};
const getTowIcon = (freshnessColor?: string) => {
  const key = freshnessColor || 'default';
  if (_towIconCache[key]) return _towIconCache[key];
  const h = Math.round(36 * 0.65);
  const icon = L.divIcon({
    className: '',
    iconSize: [36, h],
    iconAnchor: [18, Math.round(h / 2)],
    html: getTowVehicleMarkerHtml(36, freshnessColor),
  });
  _towIconCache[key] = icon;
  return icon;
};

const _trailerIconCache: Record<string, L.DivIcon> = {};
const getTrailerIcon = (category: string, customColor?: string | null) => {
  const key = `${category}_${customColor || ''}`;
  if (_trailerIconCache[key]) return _trailerIconCache[key];
  const icon = L.divIcon({
    className: '',
    iconSize: [34, 29],
    iconAnchor: [17, 14],
    html: getTrailerMarkerHtml(category as any, 34, customColor),
  });
  _trailerIconCache[key] = icon;
  return icon;
};

const coordinatePinIcon = L.divIcon({
  className: 'coordinate-pin',
  html: `
    <div style="position: relative; width: 32px; height: 44px; transform: translate(-16px, -44px);">
      <svg width="32" height="44" viewBox="0 0 32 44" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 28 16 28s16-16 16-28C32 7.163 24.837 0 16 0z" fill="#ef4444" stroke="#fff" stroke-width="2"/>
        <circle cx="16" cy="16" r="7" fill="#fff"/>
        <circle cx="16" cy="16" r="3" fill="#ef4444"/>
      </svg>
    </div>
  `,
  iconSize: [0, 0],
  iconAnchor: [0, 0]
});

const discoverHighlightIcon = L.divIcon({
  className: 'discover-highlight-pin',
  html: `
    <div style="position: relative; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; cursor: pointer;">
      <div style="position: absolute; width: 40px; height: 40px; border-radius: 50%; background: rgba(245,158,11,0.3); animation: discoverPing 1.5s infinite;"></div>
      <div style="position: absolute; width: 20px; height: 20px; background: #f59e0b; border: 3px solid white; border-radius: 50%; box-shadow: 0 4px 12px rgba(0,0,0,0.3);"></div>
    </div>
  `,
  iconSize: [40, 40],
  iconAnchor: [20, 20]
});

const labelIcon = L.divIcon({
  className: 'label-icon',
  html: '', // Transparent
  iconSize: [1, 1]
});

// «Fricamp?»-boble for kandidat-fangst under auto-nav. Anker nederst-midt (peker på
// punktet), stor tap-flate. Trykk → lagre kandidat (håndteres i Marker-eventHandler).
const candidateBubbleIcon = L.divIcon({
  className: 'autonav-candidate-bubble',
  html: `
    <div style="display:flex;flex-direction:column;align-items:center;cursor:pointer;filter:drop-shadow(0 3px 8px rgba(0,0,0,0.35));">
      <div style="display:flex;align-items:center;gap:6px;background:#10b981;color:#fff;padding:8px 14px;border-radius:9999px;border:2px solid #fff;font-weight:900;font-size:13px;white-space:nowrap;">
        <i class="fa-solid fa-tent"></i>Fricamp?
      </div>
      <div style="width:0;height:0;border-left:7px solid transparent;border-right:7px solid transparent;border-top:9px solid #fff;margin-top:-1px;"></div>
    </div>
  `,
  iconSize: [120, 48],
  iconAnchor: [60, 48],
});

const followNearestPointIcon = L.divIcon({
  className: 'follow-nearest-point',
  html: `
    <div style="position:relative;width:26px;height:26px;display:flex;align-items:center;justify-content:center;">
      <div style="position:absolute;width:26px;height:26px;border-radius:50%;background:rgba(59,130,246,0.22);animation:followPointPulse 1.1s infinite;"></div>
      <div style="position:absolute;width:12px;height:12px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 0 0 2px rgba(37,99,235,0.25);"></div>
    </div>
  `,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

const activeFollowTargetIcon = L.divIcon({
  className: 'active-follow-target',
  html: `
    <div style="position:relative;width:34px;height:34px;display:flex;align-items:center;justify-content:center;">
      <div style="position:absolute;width:34px;height:34px;border-radius:50%;background:rgba(16,185,129,0.18);animation:activeFollowPulse 2.5s ease-in-out infinite;"></div>
      <div style="position:absolute;width:18px;height:18px;border-radius:50%;background:${BRAND};border:3px solid #fff;box-shadow:0 0 0 2px rgba(16,185,129,0.2);"></div>
    </div>
  `,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

const wildlifeMarkerIcon = (species: string) => {
  const s = species.toLowerCase();
  const emoji =
    s.includes('elg') || s.includes('hjort') || s.includes('radyr') || s.includes('rein') ? '🦌' :
      s.includes('rev') ? '🦊' :
        s.includes('grevling') ? '🦡' :
          s.includes('oter') ? '🦦' :
            s.includes('bever') ? '🦫' :
              s.includes('havorn') || s.includes('kongeorn') ? '🦅' :
                s.includes('ugle') ? '🦉' :
                  s.includes('trane') ? '🕊️' : '🐾';

  return L.divIcon({
    className: 'wildlife-marker',
    html: `<div style="width:32px;height:32px;border-radius:999px;background:#4f46e5;color:white;display:flex;align-items:center;justify-content:center;border:2px solid rgba(255,255,255,0.7);box-shadow:0 4px 12px rgba(0,0,0,0.35);font-size:16px;">${emoji}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16]
  });
};

const wildlifeClusterIcon = (count: number) => L.divIcon({
  className: 'wildlife-cluster',
  html: `<div style="width:38px;height:38px;border-radius:999px;background:#4338ca;color:white;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.35);font-size:12px;font-weight:900;">🐾 ${count}</div>`,
  iconSize: [38, 38],
  iconAnchor: [19, 19]
});

// Colours per route type, matching the menu legend (foot=red, ski=blue, bike=green, other=violet).
const ROUTE_COLORS: Record<string, string> = {
  foot: '#ef4444',
  ski: '#0ea5e9',
  bike: BRAND_DARK,
  other: '#8b5cf6',
};
const routeColor = (routeType?: string) => ROUTE_COLORS[routeType || ''] || '#f59e0b';

const trailEndpointIcon = (type: 'start' | 'end') => L.divIcon({
  className: `trail-endpoint-${type}`,
  html: `<div style="width:28px;height:28px;border-radius:999px;display:flex;align-items:center;justify-content:center;background:${type === 'start' ? '#16a34a' : '#dc2626'};color:white;border:2px solid white;box-shadow:0 4px 10px rgba(0,0,0,0.35);font-size:11px;font-weight:900;">${type === 'start' ? 'S' : 'E'}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});

const webcamMarkerIcon = L.divIcon({
  className: 'webcam-marker',
  html: `<div style="width:32px;height:32px;border-radius:999px;background:#0891b2;color:white;display:flex;align-items:center;justify-content:center;border:2px solid rgba(255,255,255,0.8);box-shadow:0 4px 12px rgba(0,0,0,0.35);font-size:15px;">📷</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16]
});

const getRouteWebcamMarkerIcon = (index: number) => L.divIcon({
  className: 'route-webcam-marker',
  html: `<div style="width:34px;height:34px;border-radius:999px;background:#0891b2;color:white;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 8px 20px rgba(0,0,0,0.45);font-size:13px;font-weight:900;position:relative;">📷<div style="position:absolute;top:-8px;right:-8px;min-width:18px;height:18px;border-radius:999px;background:#0f172a;color:#fff;font-size:9px;font-weight:900;display:flex;align-items:center;justify-content:center;border:2px solid #fff;padding:0 4px;">${index + 1}</div></div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17]
});

// Per-category SVGs matching lucide icons used in DiscoverCard
const DISCOVER_SVGS: Record<string, string> = {
  // Eye
  viewpoint: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>`,
  // Droplets
  waterfall: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"/></svg>`,
  // Landmark
  museum: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="3" x2="21" y1="22" y2="22"/><line x1="6" x2="6" y1="18" y2="11"/><line x1="10" x2="10" y1="18" y2="11"/><line x1="14" x2="14" y1="18" y2="11"/><line x1="18" x2="18" y1="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>`,
  // Star
  monument: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
  memorial: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
  attraction: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
  // Castle
  castle: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 20v-9H2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2Z"/><path d="M18 11V4H6v7"/><path d="M15 22v-4a3 3 0 0 0-6 0v4"/><path d="M22 11V9"/><path d="M2 11V9"/><path d="M6 4V2"/><path d="M18 4V2"/><path d="M10 4V2"/><path d="M14 4V2"/></svg>`,
  // Skull
  ruins: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/><path d="M8 20v2h8v-2"/><path d="m12.5 17-.5-1-.5 1h1z"/><path d="M16 20a2 2 0 0 0 1.56-3.25 8 8 0 1 0-11.12 0A2 2 0 0 0 8 20"/></svg>`,
  // Info
  information: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`,
  // Mountain
  peak: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"/><path d="M4.14 15.08c2.62-1.57 5.24-1.43 7.86.42 2.74 1.94 5.49 2 8.23.19"/></svg>`,
  // Landmark (heritage fallback)
  cultural_heritage: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="3" x2="21" y1="22" y2="22"/><line x1="6" x2="6" y1="18" y2="11"/><line x1="10" x2="10" y1="18" y2="11"/><line x1="14" x2="14" y1="18" y2="11"/><line x1="18" x2="18" y1="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>`,
  // Umbrella (strand)
  beach: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12a10.06 10.06 0 0 0-20 0Z"/><path d="M12 12v8a2 2 0 0 0 4 0"/><path d="M12 2v1"/></svg>`,
  // ShoppingBag
  shopping: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>`,
};

// Background colors matching DiscoverCard CATEGORY_COLORS
const DISCOVER_BG_COLORS: Record<string, string> = {
  viewpoint: BRAND,
  waterfall: '#06b6d4',
  museum: '#f59e0b',
  monument: '#f43f5e',
  memorial: '#f43f5e',
  castle: '#f59e0b',
  ruins: '#78716c',
  attraction: '#f43f5e',
  information: '#3b82f6',
  peak: BRAND,
  cultural_heritage: '#d97706',
  beach: '#0ea5e9',
  shopping: '#8b5cf6',
};

const discoverCategoryIconCache: Record<string, L.DivIcon> = {};
function getDiscoverIcon(category: string): L.DivIcon {
  if (discoverCategoryIconCache[category]) return discoverCategoryIconCache[category];
  const svg = DISCOVER_SVGS[category] || DISCOVER_SVGS.museum;
  const bg = DISCOVER_BG_COLORS[category] || '#f59e0b';
  const icon = L.divIcon({
    className: `discover-${category}`,
    html: `<div style="width:30px;height:30px;border-radius:999px;background:${bg};color:white;display:flex;align-items:center;justify-content:center;border:2px solid rgba(255,255,255,0.8);box-shadow:0 4px 12px rgba(0,0,0,0.35);">${svg}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15]
  });
  discoverCategoryIconCache[category] = icon;
  return icon;
}

// --- PRECIPITATION RADAR ---

const RAIN_VIEWER_API = 'https://api.rainviewer.com/public/weather-maps.json';

const PrecipitationRadarLayer: React.FC = () => {
  const { t } = useTranslation();
  const [frames, setFrames] = useState<{ time: number; path: string }[]>([]);
  const [frameIndex, setFrameIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [host, setHost] = useState('https://tilecache.rainviewer.com');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetch(RAIN_VIEWER_API)
      .then(r => r.json())
      .then(data => {
        if (data.host) setHost(data.host);
        const past: { time: number; path: string }[] = data.radar?.past ?? [];
        if (past.length > 0) {
          setFrames(past.slice(-6));
          setFrameIndex(past.slice(-6).length - 1);
        }
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (!playing || frames.length < 2) return;
    timerRef.current = setInterval(() => {
      setFrameIndex(i => (i + 1) % frames.length);
    }, 800);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [playing, frames.length]);

  const currentFrame = frames[frameIndex];
  const nextIdx = frames.length > 1 ? (frameIndex + 1) % frames.length : frameIndex;
  const nextFrame = frames[nextIdx];

  return (
    <Pane name="radar-pane" style={{ zIndex: 700 }}>
      {currentFrame && (
        <TileLayer
          key={currentFrame.path}
          url={`${host}${currentFrame.path}/256/{z}/{x}/{y}/2/1_1.png`}
          opacity={0.7}
          attribution={'&copy; <a href="https://www.rainviewer.com">{t("auto.map.rainviewer", "RainViewer")}</a>'}
          zIndex={700}
          maxNativeZoom={7}
        />
      )}
      {nextFrame && nextFrame.path !== currentFrame?.path && (
        <TileLayer
          key={`preload-${nextFrame.path}`}
          url={`${host}${nextFrame.path}/256/{z}/{x}/{y}/2/1_1.png`}
          opacity={0}
          zIndex={700}
          maxNativeZoom={7}
        />
      )}
      {/* Playback controls */}
      <div
        style={{ position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, pointerEvents: 'auto' }}
        className="flex items-center gap-2 bg-black/90 text-white text-2xs font-bold px-3 py-1.5 rounded-xl shadow-lg"
      >
        <button
          onClick={() => setPlaying(p => !p)}
          className="hover:text-cyan-300 transition-colors"
          title={playing ? t('map.pause') : t('map.play')}
        >
          {playing ? '⏸' : '▶'}
        </button>
        <span className="text-cyan-300">🌧</span>
        {frames.length > 0 && frames[frameIndex] && (
          <span>
            {new Date(frames[frameIndex].time * 1000).toLocaleTimeString(getDateLocale(), { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        <div className="flex gap-0.5">
          {frames.map((_, i) => (
            <button
              key={i}
              onClick={() => { setFrameIndex(i); setPlaying(false); }}
              className={`w-2 h-2 rounded-full transition-colors ${i === frameIndex ? 'bg-cyan-300' : 'bg-white/40 hover:bg-white/70'}`}
            />
          ))}
        </div>
      </div>
    </Pane>
  );
};

// --- SUBCOMPONENTS ---

const TranslationWrapper: React.FC<{ children: (t: any) => React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  return <>{children(t)}</>;
};

// Returns true if (lat, lng) is within `threshold` (0–1) of any viewport edge.
// Use to avoid re-centering when the marker is already safely inside the map.
function isNearMapEdge(map: L.Map, lat: number, lng: number, threshold = 0.20): boolean {
  const b = map.getBounds();
  return isPointNearViewportEdge(
    { south: b.getSouth(), north: b.getNorth(), west: b.getWest(), east: b.getEast() },
    lat,
    lng,
    threshold,
  );
}

// Auto-sentrer kart på brukerposisjon under rute-følging
// Pauser auto-sentrering i 10s når brukeren drar/panorerer kartet manuelt
const FollowRouteAutoCenter: React.FC<{
  trackPoints: import('../types').TrackPoint[];
  isFollowingRoute: boolean;
  userPosition?: import('../types').Coordinates | null;
  onUserPannedChange?: (panned: boolean) => void;
}> = ({ trackPoints, isFollowingRoute, userPosition, onUserPannedChange }) => {
  const map = useMap();
  const lastPanRef = useRef(0);
  const initialZoomDoneRef = useRef(false);
  const userDraggedRef = useRef(false);
  const returnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Lytt på brukerens manuelle kartinteraksjon
  useEffect(() => {
    if (!isFollowingRoute) return;
    const onDragStart = () => {
      userDraggedRef.current = true;
      onUserPannedChange?.(true);
      if (returnTimerRef.current) clearTimeout(returnTimerRef.current);
      returnTimerRef.current = setTimeout(() => {
        userDraggedRef.current = false;
        onUserPannedChange?.(false);
      }, 10_000);
    };
    map.on('dragstart', onDragStart);
    return () => {
      map.off('dragstart', onDragStart);
      if (returnTimerRef.current) clearTimeout(returnTimerRef.current);
    };
  }, [map, isFollowingRoute, onUserPannedChange]);

  useEffect(() => {
    if (!isFollowingRoute) { initialZoomDoneRef.current = false; userDraggedRef.current = false; onUserPannedChange?.(false); return; }
    // Ikke auto-sentrer mens brukeren har dratt kartet manuelt
    if (userDraggedRef.current) return;
    const pos = trackPoints.length > 0
      ? { lat: trackPoints[trackPoints.length - 1].latitude, lon: trackPoints[trackPoints.length - 1].longitude }
      : userPosition
        ? { lat: userPosition.latitude, lon: userPosition.longitude }
        : null;
    if (!pos) return;
    if (Date.now() - lastPanRef.current < 250) return;
    lastPanRef.current = Date.now();
    if (!initialZoomDoneRef.current) {
      initialZoomDoneRef.current = true;
      map.setView([pos.lat, pos.lon], Math.min(map.getZoom(), 14), { animate: true, duration: 0.5 });
    } else {
      if ((map as any)._loaded && isNearMapEdge(map, pos.lat, pos.lon)) {
        map.panTo([pos.lat, pos.lon], { animate: true, duration: 0.35 });
      }
    }
  }, [trackPoints, isFollowingRoute, userPosition, map, onUserPannedChange]);
  return null;
};

// FitBounds på hele ruten én gang ved navigasjonsstart — tett rundt ruten
const FitFollowRoute: React.FC<{
  routeCoords: Coordinates[] | null;
  isFollowingRoute: boolean;
}> = ({ routeCoords, isFollowingRoute }) => {
  const map = useMap();
  const fittedRef = useRef(false);
  useEffect(() => {
    if (!isFollowingRoute) { fittedRef.current = false; return; }
    if (fittedRef.current || !routeCoords || routeCoords.length < 2) return;
    fittedRef.current = true;
    const bounds = L.latLngBounds(routeCoords.map(c => [c.latitude, c.longitude] as [number, number]));
    // paddingTopLeft: [left(rail+overlay), top(header+overlay)]  paddingBottomRight: [right, bottom(trackingPanel)]
    map.fitBounds(bounds, {
      paddingTopLeft: [380, 100],
      paddingBottomRight: [20, 80],
      maxZoom: 16,
      animate: true,
      duration: 1.0,
    });
  }, [isFollowingRoute, routeCoords, map]);
  return null;
};

const MapEventHandler: React.FC<{
  onMapMove: (coords: Coordinates, zoom: number, viewBounds: MapBounds, userDragged?: boolean) => void,
  onMapClick: (coords: Coordinates | null) => void,
  onMapLongPress?: (coords: Coordinates) => void,
  onMapContextMenu?: (coords: Coordinates) => void,
  setClickCoords: (coords: [number, number] | null) => void,
  setShowSearchButton: (show: boolean) => void,
  mapRef: React.MutableRefObject<L.Map | null>,
  isDrawing?: boolean,
  onDrawingMapClick?: (coords: [number, number]) => void,
  isRoutePlanning?: boolean,
  onAddRouteStop?: (coords: Coordinates) => void,
  captureMode?: boolean,
  onCapture?: (coords: Coordinates) => void,
}> = ({ onMapMove, onMapClick, onMapLongPress, onMapContextMenu, setClickCoords, setShowSearchButton, mapRef, isDrawing, onDrawingMapClick, isRoutePlanning, onAddRouteStop, captureMode, onCapture }) => {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressCoords = useRef<L.LatLng | null>(null);
  const longPressFired = useRef(false);

  const moveDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Leaflet fyrer `dragend` FØR `moveend` for ekte brukerpanorering.
  //
  // MEN DRAG ER IKKE DEN ENESTE MÅTEN BRUKEREN FLYTTER KARTET PÅ. Zoom med
  // hjulet, +/− knappene, dobbeltklikk eller knip gir `moveend` UTEN `dragend`,
  // og ble derfor regnet som appens eget flytt — da hentet vi automatisk, og
  // søkesirkelen dukket opp uten at brukeren hadde trykket «Søk i dette
  // området». Rapportert 15.08.2026.
  //
  // Derfor er sannheten nå snudd: appen markerer sine egne flytt
  // (`markProgrammaticMove`), og alt annet er brukeren. `dragend` beholdes som
  // et ekstra ja-signal, siden et drag ALLTID er brukeren uansett timing.
  const userDragged = useRef(false);

  useEffect(() => {
    return () => { if (moveDebounce.current) clearTimeout(moveDebounce.current); };
  }, []);

  const map = useMapEvents({
    dragend: () => { userDragged.current = true; },
    moveend: () => {
      if (moveDebounce.current) clearTimeout(moveDebounce.current);
      moveDebounce.current = setTimeout(() => {
        const c = map.getCenter();
        const z = map.getZoom();
        const b = map.getBounds();
        const viewBounds = {
          latMin: b.getSouth(), lonMin: b.getWest(), latMax: b.getNorth(), lonMax: b.getEast()
        };
        const draggedNå = userDragged.current;
        userDragged.current = false;
        // Brukeren flyttet kartet med mindre appen sa fra at den gjorde det selv.
        const brukerFlyttet = draggedNå || !isProgrammaticMove();
        onMapMove({ latitude: c.lat, longitude: c.lng }, z, viewBounds, brukerFlyttet);
        if (!isInPrescannedCounty(c.lat, c.lng)) {
          setShowSearchButton(true);
        } else {
          setShowSearchButton(false);
        }
      }, 150);
    },
    zoomend: () => { syncMarkerZoomClass(map); },
    click: (e) => {
      if ((e as any)._stopped || longPressFired.current) { longPressFired.current = false; return; }
      const originalEvent = e.originalEvent;
      const target = originalEvent.target as HTMLElement;
      const isInteractive = target.closest('.leaflet-interactive') || target.tagName === 'path' || target.tagName === 'IMG' || target.classList.contains('leaflet-route-line');
      if (isInteractive) return;
      if (isDrawing) {
        onDrawingMapClick?.([e.latlng.lat, e.latlng.lng]);
        return;
      }
      if (isRoutePlanning) {
        onAddRouteStop?.({ latitude: e.latlng.lat, longitude: e.latlng.lng });
        return;
      }
      // Kandidat-fangst under auto-nav: et trykk viser en «Fricamp?»-boble i stedet
      // for å åpne det vanlige legg-til-panelet (som er uegnet under kjøring).
      if (captureMode) {
        onCapture?.({ latitude: e.latlng.lat, longitude: e.latlng.lng });
        return;
      }
      setClickCoords([e.latlng.lat, e.latlng.lng]);
      onMapClick({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
    contextmenu: (e) => {
      if (isDrawing) return;
      onMapContextMenu?.({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
    mousedown: (e) => {
      // Skip mouse-based long press on touch devices — handled by touch events below
      if ('ontouchstart' in window) return;
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      longPressFired.current = false;
      longPressCoords.current = e.latlng;
      longPressTimer.current = setTimeout(() => {
        if (longPressCoords.current && onMapLongPress) {
          longPressFired.current = true;
          onMapLongPress({ latitude: longPressCoords.current.lat, longitude: longPressCoords.current.lng });
        }
      }, 500);
    },
    mouseup: () => {
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    },
    mousemove: () => {
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    }
  });

  // Touch events for mobile long-press — uses shared ref so timer survives effect re-runs
  useEffect(() => {
    if (!map) return;
    const container = map.getContainer();

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      longPressFired.current = false;
      const touch = e.touches[0];
      const rect = container.getBoundingClientRect();
      const point = map.containerPointToLatLng(L.point(touch.clientX - rect.left, touch.clientY - rect.top));
      longPressCoords.current = point;
      longPressTimer.current = setTimeout(() => {
        if (longPressCoords.current && onMapLongPress) {
          longPressFired.current = true;
          onMapLongPress({ latitude: longPressCoords.current.lat, longitude: longPressCoords.current.lng });
          if (navigator.vibrate) navigator.vibrate(30);
        }
      }, 500);
    };
    const cancelTimer = () => {
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchend', cancelTimer);
    container.addEventListener('touchmove', cancelTimer);
    return () => {
      cancelTimer();
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', cancelTimer);
      container.removeEventListener('touchmove', cancelTimer);
    };
  }, [map, onMapLongPress]);

  useEffect(() => {
    if (!map) return;
    mapRef.current = map;

    const c = map.getCenter();
    const z = map.getZoom();
    const b = map.getBounds();
    onMapMove(
      { latitude: c.lat, longitude: c.lng },
      z,
      { latMin: b.getSouth(), lonMin: b.getWest(), latMax: b.getNorth(), lonMax: b.getEast() }
    );
    syncMarkerZoomClass(map); // sett riktig oversikt/detalj-klasse ved oppstart

    // Track tile load performance
    const onTileLoadStart = (e: any) => { if (e.tile) e.tile._loadStart = performance.now(); };
    const onTileLoad = (e: any) => { if (e.tile?._loadStart) recordTileLoad(performance.now() - e.tile._loadStart); };
    map.on('tileloadstart', onTileLoadStart);
    map.on('tileload', onTileLoad);
    return () => { map.off('tileloadstart', onTileLoadStart); map.off('tileload', onTileLoad); };
  }, [map]);

  return null;
};

// ClusteredMarkers removed — replaced by components/map/ClusteredSpotMarkers.tsx
const _OLD_ClusteredMarkers: React.FC<{
  spots: CampingSpot[],
  zoom: number,
  onSelectSpot: (id: string) => void,
  selectedSpotId: string | null,
  activeAnalysisIds: Set<string>,
  currentViewBounds: MapBounds | null,
  isMobile: boolean
}> = ({ spots, zoom, onSelectSpot, selectedSpotId, activeAnalysisIds, currentViewBounds, isMobile }) => {
  const map = useMap();
  const isConfirmedSpot = (spot: CampingSpot) =>
    spot.sourceLevel === 4 || spot.spotType === 'confirmed' || spot.isVerified === true;

  const clusters = useMemo(() => {
    const visibleSpots = currentViewBounds
      ? spots.filter(s =>
        s.location.latitude >= currentViewBounds.latMin &&
        s.location.latitude <= currentViewBounds.latMax &&
        s.location.longitude >= currentViewBounds.lonMin &&
        s.location.longitude <= currentViewBounds.lonMax
      )
      : spots;

    visibleSpots.sort((a, b) => {
      if (a.id === selectedSpotId) return -1;
      if (b.id === selectedSpotId) return 1;
      return (b.aiResult?.score || b.score) - (a.aiResult?.score || a.score);
    });

    if (visibleSpots.length < 5) {
      return visibleSpots.map(spot => ({ spot, count: 1, isCluster: false, isMixed: false }));
    }

    const threshold = 0.0005 * Math.pow(2, 18 - zoom);
    const grouped: { spot: CampingSpot, count: number, isCluster: boolean, isMixed: boolean }[] = [];
    const usedIndices = new Set<number>();

    visibleSpots.forEach((spot, i) => {
      if (usedIndices.has(i)) return;

      if (spot.id === selectedSpotId) {
        grouped.push({ spot, count: 1, isCluster: false, isMixed: false });
        usedIndices.add(i);
        return;
      }

      let potentialClusterIndices = [i];
      let types = new Set<string>();
      types.add(spot.spotType);
      const clusterConfirmed = isConfirmedSpot(spot);

      for (let j = i + 1; j < visibleSpots.length; j++) {
        if (usedIndices.has(j)) continue;
        const neighbor = visibleSpots[j];
        if (neighbor.id === selectedSpotId) continue;
        if (isConfirmedSpot(neighbor) !== clusterConfirmed) continue;

        const latDiff = Math.abs(spot.location.latitude - neighbor.location.latitude);
        const lonDiff = Math.abs(spot.location.longitude - neighbor.location.longitude);

        if (latDiff < threshold && lonDiff < threshold) {
          potentialClusterIndices.push(j);
          types.add(neighbor.spotType);
        }
      }

      const isMixed = !clusterConfirmed && types.size > 1;

      if (potentialClusterIndices.length >= 5) {
        potentialClusterIndices.forEach(idx => usedIndices.add(idx));
        grouped.push({ spot, count: potentialClusterIndices.length, isCluster: true, isMixed });
      } else {
        usedIndices.add(i);
        grouped.push({ spot, count: 1, isCluster: false, isMixed: false });
      }
    });

    return grouped;
  }, [spots, zoom, selectedSpotId, currentViewBounds]);

  return (
    <>
      {clusters.map((group, idx) => {
        const { spot, count, isCluster, isMixed } = group;
        const isConfirmed = isConfirmedSpot(spot);

        if (spot.isPremiumGated) {
          const size = isCluster ? 18 : 14;
          return (
            <Marker
              key={`gated-${spot.id}-${idx}`}
              position={[spot.location.latitude, spot.location.longitude]}
              icon={L.divIcon({
                className: 'gated-dot',
                html: `<div style="width:${size}px;height:${size}px;background:#34d399;border-radius:50%;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.2);opacity:0.85;display:flex;align-items:center;justify-content:center;color:white;font-size:8px;font-weight:bold;">${isCluster ? count : ''}</div>`,
                iconSize: [size, size],
                iconAnchor: [size/2, size/2]
              })}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stop(e);
                  onSelectSpot(spot.id);
                }
              }}
              zIndexOffset={50}
            />
          );
        }

        

        if (isCluster) {
          const clusterIcon = isMixed
            ? createMixedClusterIcon(count)
            : createCustomIcon(spot, false, false, count);

          return (
            <Marker
              key={`cluster-${idx}`}
              position={[spot.location.latitude, spot.location.longitude]}
              icon={clusterIcon}
              zIndexOffset={isConfirmed ? 700 : 100}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stop(e);
                  if ((map as any)._loaded) map.flyTo([spot.location.latitude, spot.location.longitude], zoom + 2, { duration: 0.5 });
                }
              }}
            />
          );
        }

        return (
          <Marker
            key={spot.id}
            position={[spot.location.latitude, spot.location.longitude]}
            icon={createCustomIcon(spot, selectedSpotId === spot.id, activeAnalysisIds.has(spot.id))}
            zIndexOffset={selectedSpotId === spot.id ? 1200 : isConfirmed ? 700 : 0}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stop(e);
                onSelectSpot(spot.id);
              }
            }}
          >
            {spot.spotType === 'commercial_campsite' && !isMobile && (
              <Popup className="custom-popup">
                <TranslationWrapper>
                  {(t) => (
                    <div className="p-3 space-y-2 min-w-[200px]">
                      <div className="flex items-center gap-2 text-blue-600 font-black text-3xs uppercase tracking-widest">
                        <Home size={12} fill="currentColor" /> {t('map.commercial.campsite')}
                      </div>
                      <div className="text-stone-900 font-black text-xs leading-tight">{spot.name}</div>
                      <div className="flex flex-col gap-1.5 pt-1 border-t border-stone-100 mt-1">
                        {spot.commercialMetadata?.website && (
                          <a href={spot.commercialMetadata.website.startsWith('http') ? spot.commercialMetadata.website : `https://${spot.commercialMetadata.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-2xs font-bold text-blue-500 hover:underline">
                            <Globe size={12} /> {t('map.commercial.website')}
                          </a>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openExternalNavigation(spot.location.latitude, spot.location.longitude); }} className="mt-2 w-full bg-blue-600 text-white py-1.5 rounded-lg text-3xs font-black uppercase flex items-center justify-center gap-2">
                          <Navigation2 size={12} className="rotate-45" /> {t('map.charger.navigate')}
                        </button>
                      </div>
                    </div>
                  )}
                </TranslationWrapper>
              </Popup>
            )}
          </Marker>
        );
      })}
    </>
  );
};

// ... Rest of MapComponent remains largely similar, just needs ClusteredChargers etc injected if I overwrote the file ...
// Actually, to be safe, I must provide the full MapComponent since I'm overwriting the file.

// Markør med popup som viser et stedsnøyaktig bilde (Mapillary / Street View)
// øverst. Bildet hentes KUN når popup-en åpnes (enabled=open) — ellers ville
// hooken fyre for hver markør på skjermen. Brukes av lag uten egne bilder
// (ladere, avfallspunkter). Children = eksisterende popup-innhold.
const ImagePopupMarker: React.FC<{
  lat: number; lng: number; icon: L.DivIcon | L.Icon; zIndexOffset?: number;
  onInteract: () => void; maxWidth?: number; children: React.ReactNode;
}> = ({ lat, lng, icon, zIndexOffset, onInteract, maxWidth, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Marker
      position={[lat, lng]}
      icon={icon}
      zIndexOffset={zIndexOffset}
      eventHandlers={{
        click: (e) => { L.DomEvent.stop(e); onInteract(); },
        popupopen: () => setOpen(true),
        popupclose: () => setOpen(false),
      }}
    >
      <Popup className="custom-popup" maxWidth={maxWidth}>
        <div className="p-3 space-y-2 min-w-[200px]">
          <PopupLocationImage lat={lat} lng={lng} enabled={open} />
          {children}
        </div>
      </Popup>
    </Marker>
  );
};

// Re-including the omitted components to ensure file integrity.
const ClusteredChargers: React.FC<{
  chargers: ChargingStation[],
  zoom: number,
  currentViewBounds: MapBounds | null,
  onInteract: () => void,
  isMobile: boolean,
  isPaidUser?: boolean
}> = ({ chargers, zoom, currentViewBounds, onInteract, isMobile, isPaidUser = true }) => {
  const map = useMap();
  const { t } = useTranslation();

  const clusters = useMemo(() => {
    const visibleChargers = currentViewBounds
      ? chargers.filter(c =>
        c.location.latitude >= currentViewBounds.latMin &&
        c.location.latitude <= currentViewBounds.latMax &&
        c.location.longitude >= currentViewBounds.lonMin &&
        c.location.longitude <= currentViewBounds.lonMax
      )
      : chargers;

    if (visibleChargers.length < 5) {
      return visibleChargers.map(c => ({ charger: c, count: 1, isCluster: false }));
    }

    const threshold = 0.0005 * Math.pow(2, 18 - zoom);
    const grouped: { charger: ChargingStation, count: number, isCluster: boolean }[] = [];
    const usedIndices = new Set<number>();

    visibleChargers.forEach((c, i) => {
      if (usedIndices.has(i)) return;
      let potentialCluster = [i];

      for (let j = i + 1; j < visibleChargers.length; j++) {
        if (usedIndices.has(j)) continue;
        const neighbor = visibleChargers[j];
        const latDiff = Math.abs(c.location.latitude - neighbor.location.latitude);
        const lonDiff = Math.abs(c.location.longitude - neighbor.location.longitude);
        if (latDiff < threshold && lonDiff < threshold) {
          potentialCluster.push(j);
        }
      }

      if (potentialCluster.length >= 3) {
        potentialCluster.forEach(idx => usedIndices.add(idx));
        grouped.push({ charger: c, count: potentialCluster.length, isCluster: true });
      } else {
        usedIndices.add(i);
        grouped.push({ charger: c, count: 1, isCluster: false });
      }
    });

    return grouped;
  }, [chargers, zoom, currentViewBounds]);

  return (
    <>
      {clusters.map((group, idx) => {
        const { charger: c, count, isCluster } = group;
        if (isCluster) {
          return (
            <Marker
              key={`c-cluster-${idx}`}
              position={[c.location.latitude, c.location.longitude]}
              icon={chargerIcon(c, count)}
              zIndexOffset={50}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stop(e);
                  if ((map as any)._loaded) map.flyTo([c.location.latitude, c.location.longitude], capZoomForFree(zoom + 2, isPaidUser), { duration: 0.5 });
                }
              }}
            />
          );
        }
        return (
          <ImagePopupMarker
            key={c.id}
            lat={c.location.latitude}
            lng={c.location.longitude}
            icon={chargerIcon(c)}
            onInteract={onInteract}
          >
            <div className="flex items-center gap-2 text-yellow-600 font-black text-3xs uppercase tracking-widest">
              <Zap size={12} fill="currentColor" /> {t('map.charger.station')}
            </div>
            <div className="text-stone-900 font-black text-xs leading-tight">{c.name}</div>
            <div className="flex flex-col gap-1 text-2xs text-[var(--text-muted)]">
              <span>{t('map.charger.points', { count: c.chargingPoints })}</span>
              {c.connectors.length > 0 && <span className="text-3xs opacity-70 truncate">{c.connectors.join(', ')}</span>}
            </div>
            <button onClick={(e) => { e.stopPropagation(); openExternalNavigation(c.location.latitude, c.location.longitude); }} className="mt-2 w-full bg-yellow-600 text-white py-1.5 rounded-lg text-3xs font-black uppercase flex items-center justify-center gap-2">
              <Navigation2 size={12} className="rotate-45" /> {t('map.charger.navigate')}
            </button>
          </ImagePopupMarker>
        );
      })}
    </>
  );
};

const ClusteredBarriers: React.FC<{
  barriers: RoadBarrier[],
  zoom: number,
  currentViewBounds: MapBounds | null,
  onInteract: () => void,
  isMobile: boolean
}> = ({ barriers, zoom, onInteract }) => {
  const { t } = useTranslation();

  return (
    <>
      {barriers.map((b) => {
        const isPaid = b.openingMethod && b.openingMethod.toLowerCase().includes("betaling");
        const isKey = b.openingMethod && b.openingMethod.toLowerCase().includes("nøkkel");
        const isManual = !isPaid && !isKey && b.openingMethod && (
          b.openingMethod.toLowerCase().includes("manuell") || b.openingMethod.toLowerCase().includes("automatisert")
        );

        return (
          <Marker
            key={b.id}
            position={[b.location.latitude, b.location.longitude]}
            icon={barrierIcon(b, zoom)}
            zIndexOffset={10}
            eventHandlers={{ click: (e) => { L.DomEvent.stop(e); onInteract(); } }}
          >
            <Popup className="custom-popup" maxWidth={300}>
              <div className="p-3 space-y-2">
                <div className="flex items-center gap-2 text-red-600 font-black text-3xs uppercase tracking-widest">
                  <Lock size={12} /> {t('map.barrier.title')}
                </div>
                <div className="text-stone-900 font-bold text-xs">{b.type || t('map.barrier.default_type')}</div>

                <div className="text-2xs text-[var(--text-muted)] font-medium">
                  {b.openingMethod === 'Ukjent' ? t('map.barrier.closed_public') : b.openingMethod}
                </div>

                {b.description && (
                  <div className="bg-stone-50 p-2 rounded-lg border border-stone-100 text-2xs text-[var(--text-muted)] italic leading-relaxed">
                    "{b.description}"
                  </div>
                )}

                {(b.purpose || b.owner) && (
                  <div className="mt-2 pt-2 border-t border-stone-100 flex flex-col gap-1">
                    {b.purpose && (
                      <div className="flex justify-between items-center text-3xs">
                        <span className="text-[var(--text-muted)] font-bold uppercase">{t('map.barrier.purpose')}:</span>
                        <span className="text-[var(--text-muted)]">{b.purpose}</span>
                      </div>
                    )}
                    {b.owner && (
                      <div className="flex justify-between items-center text-3xs">
                        <span className="text-[var(--text-muted)] font-bold uppercase">{t('map.barrier.owner')}:</span>
                        <span className="text-[var(--text-muted)]">{b.owner}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-1 flex-wrap mt-1">
                  {isPaid && (
                    <div className="flex items-center gap-1.5 text-emerald-700 text-3xs font-bold bg-emerald-50 border border-emerald-100 px-2 py-1 rounded">
                      <CreditCard size={10} /> {t('map.barrier.toll')}
                    </div>
                  )}
                  {isKey && (
                    <div className="flex items-center gap-1.5 text-amber-700 text-3xs font-bold bg-amber-50 border border-amber-100 px-2 py-1 rounded">
                      <Key size={10} /> {t('map.barrier.locked_key')}
                    </div>
                  )}
                  {isManual && (
                    <div className="flex items-center gap-1.5 text-amber-700 text-3xs font-bold bg-amber-50 border border-amber-100 px-2 py-1 rounded">
                      <Key size={10} /> {t('map.barrier.manual_opening')}
                    </div>
                  )}
                  {!isPaid && !isKey && !isManual && (
                    <div className="flex items-center gap-1.5 text-red-700 text-3xs font-bold bg-red-50 border border-red-100 px-2 py-1 rounded">
                      <Ban size={10} /> {t('map.barrier.blocked')}
                    </div>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

const ClusteredWikiSpots: React.FC<{
  wikiSpots: WikiSpot[],
  zoom: number,
  onInteract: () => void,
  isMobile: boolean,
  isPaidUser?: boolean
}> = ({ wikiSpots, zoom, onInteract, isMobile, isPaidUser = true }) => {
  const map = useMap();
  const { t } = useTranslation();
  const [openSpotId, setOpenSpotId] = useState<number | null>(null);

  const clusters = useMemo(() => {
    const spotsToCluster = openSpotId
      ? wikiSpots.filter(w => w.pageid !== openSpotId)
      : wikiSpots;

    const openSpot = openSpotId ? wikiSpots.find(w => w.pageid === openSpotId) : null;

    if (spotsToCluster.length < 5) {
      const all = spotsToCluster.map(w => ({ spot: w, count: 1, isCluster: false }));
      if (openSpot) all.push({ spot: openSpot, count: 1, isCluster: false });
      return all;
    }

    const threshold = 0.0005 * Math.pow(2, 18 - zoom);
    const grouped: { spot: WikiSpot, count: number, isCluster: boolean }[] = [];
    const usedIndices = new Set<number>();

    spotsToCluster.forEach((w, i) => {
      if (usedIndices.has(i)) return;
      let potentialCluster = [i];

      for (let j = i + 1; j < spotsToCluster.length; j++) {
        if (usedIndices.has(j)) continue;
        const neighbor = spotsToCluster[j];
        const latDiff = Math.abs(w.location.latitude - neighbor.location.latitude);
        const lonDiff = Math.abs(w.location.longitude - neighbor.location.longitude);
        if (latDiff < threshold && lonDiff < threshold) {
          potentialCluster.push(j);
        }
      }

      if (potentialCluster.length >= 3) {
        potentialCluster.forEach(idx => usedIndices.add(idx));
        grouped.push({ spot: w, count: potentialCluster.length, isCluster: true });
      } else {
        usedIndices.add(i);
        grouped.push({ spot: w, count: 1, isCluster: false });
      }
    });

    if (openSpot) {
      grouped.push({ spot: openSpot, count: 1, isCluster: false });
    }

    return grouped;
  }, [wikiSpots, zoom, openSpotId]);

  return (
    <>
      {clusters.map((group) => {
        const { spot: w, count, isCluster } = group;
        const isClusterMarker = isCluster;
        const isOpen = w.pageid === openSpotId;

        const key = isClusterMarker
          ? `wiki-cluster-${w.pageid}-${count}`
          : `wiki-marker-${w.pageid}`;

        if (isClusterMarker) {
          return (
            <Marker
              key={key}
              position={[w.location.latitude, w.location.longitude]}
              icon={wikiIcon(null, count)}
              zIndexOffset={60}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stop(e);
                  if ((map as any)._loaded) map.flyTo([w.location.latitude, w.location.longitude], capZoomForFree(zoom + 2, isPaidUser), { duration: 0.5 });
                }
              }}
            />
          );
        }

        return (
          <Marker
            key={key}
            position={[w.location.latitude, w.location.longitude]}
            icon={wikiIcon(w)}
            zIndexOffset={isOpen ? 1000 : 70}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stop(e);
                setOpenSpotId(w.pageid);
                onInteract();
              },
              popupclose: () => {
                setOpenSpotId(null);
              }
            }}
          >
            <Popup className="custom-popup" minWidth={w.thumbnail ? 280 : 200} maxWidth={320}>
              <div className="flex flex-col overflow-hidden rounded-t-xl -m-[1px] relative">
                {w.thumbnail ? (
                  <div className="relative w-full h-40 bg-stone-100 group">
                    <img src={w.thumbnail.source} className="w-full h-full object-cover" alt={w.title} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                    <div className="absolute bottom-3 left-3 right-12">
                      <h3 className="text-white font-black text-sm leading-tight drop-shadow-md">{w.title}</h3>
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); window.open(w.url, '_blank'); }}
                      className="absolute bottom-3 right-3 w-8 h-8 bg-white text-stone-900 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-10"
                      title={t('map.wiki.read_article')}
                    >
                      <ExternalLink size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="p-4 pb-2">
                    <div className="flex items-center gap-2 text-violet-600 font-black text-3xs uppercase tracking-widest mb-1">
                      {t('map.wiki.attraction')}
                    </div>
                    <h3 className="text-stone-900 font-black text-sm leading-tight">{w.title}</h3>
                  </div>
                )}

                {!w.thumbnail && (
                  <div className="px-4 pb-4 pt-1 space-y-3">
                    <div className="text-tiny text-[var(--text-muted)] line-clamp-4 leading-relaxed font-medium">
                      {w.extract}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); window.open(w.url, '_blank'); }} className="w-full bg-violet-100 text-violet-700 hover:bg-violet-200 py-2 rounded-lg text-2xs font-black uppercase flex items-center justify-center gap-2 transition-colors">
                      <ExternalLink size={12} /> {t('map.wiki.read_article')}
                    </button>
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

// --- Waste markers with collision offset (sidestilling) ---

const WASTE_ICON_CONFIG: Record<WastePointType, { icon: string; color: string; size: number; iconSize: number }> = {
  waste_disposal: { icon: 'fa-solid fa-dumpster', color: BRAND_DARK, size: 30, iconSize: 14 },
  waste_basket:   { icon: 'fa-solid fa-trash-can', color: '#6b7280', size: 24, iconSize: 11 },
  nvdb_container: { icon: 'fa-solid fa-trash-can', color: '#0369a1', size: 28, iconSize: 13 },
};

const createWasteMarkerIcon = (wasteType: WastePointType, offset: [number, number] = [0, 0]): L.DivIcon => {
  const { icon, color, size, iconSize } = WASTE_ICON_CONFIG[wasteType];
  return L.divIcon({
    className: 'custom-fa-marker',
    html: `<div style="position:relative;width:${size}px;height:${size}px;background:${color};border:2px solid rgba(255,255,255,0.7);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"><i class="${icon}" style="font-size:${iconSize}px;"></i></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2 - offset[0], size / 2 - offset[1]],
  });
};

interface WasteMarkerData {
  point: WastePoint;
  offset: [number, number];
}

function computeWasteOffsets(points: WastePoint[], zoom: number): WasteMarkerData[] {
  const collisionThreshold = 0.0003 * Math.pow(2, 15 - zoom);
  const OFFSET_PX = 18;
  const result: WasteMarkerData[] = [];
  const processed = new Set<number>();

  for (let i = 0; i < points.length; i++) {
    if (processed.has(i)) continue;

    const group = [i];
    for (let j = i + 1; j < points.length; j++) {
      if (processed.has(j)) continue;
      const latDiff = Math.abs(points[i].location.latitude - points[j].location.latitude);
      const lonDiff = Math.abs(points[i].location.longitude - points[j].location.longitude);
      if (latDiff < collisionThreshold && lonDiff < collisionThreshold) {
        group.push(j);
      }
    }

    if (group.length === 1) {
      result.push({ point: points[i], offset: [0, 0] });
      processed.add(i);
    } else {
      const n = group.length;
      group.forEach((idx, positionIndex) => {
        let offsetX = 0, offsetY = 0;
        if (n <= 4) {
          // Horizontal row
          const startX = -((n - 1) * OFFSET_PX) / 2;
          offsetX = startX + positionIndex * OFFSET_PX;
        } else {
          // Circular for 5+
          const angle = (2 * Math.PI * positionIndex) / n - Math.PI / 2;
          const radius = OFFSET_PX * 1.2;
          offsetX = Math.round(radius * Math.cos(angle));
          offsetY = Math.round(radius * Math.sin(angle));
        }
        result.push({ point: points[idx], offset: [offsetX, offsetY] });
        processed.add(idx);
      });
    }
  }
  return result;
}

const WasteMarkers: React.FC<{
  wastePoints: WastePoint[];
  zoom: number;
  currentViewBounds: MapBounds | null;
  onInteract: () => void;
}> = ({ wastePoints, zoom, currentViewBounds, onInteract }) => {
  const { t } = useTranslation();

  const markersWithOffsets = useMemo(() => {
    const visible = currentViewBounds
      ? wastePoints.filter(wp =>
          wp.location.latitude >= currentViewBounds.latMin &&
          wp.location.latitude <= currentViewBounds.latMax &&
          wp.location.longitude >= currentViewBounds.lonMin &&
          wp.location.longitude <= currentViewBounds.lonMax
        )
      : wastePoints;

    return computeWasteOffsets(visible, zoom);
  }, [wastePoints, zoom, currentViewBounds]);

  return (
    <>
      {markersWithOffsets.map(({ point, offset }) => {
        const cfg = WASTE_ICON_CONFIG[point.type];
        return (
          <ImagePopupMarker
            key={point.id}
            lat={point.location.latitude}
            lng={point.location.longitude}
            icon={cachedIcon(`waste-${point.type}-${offset}`, () => createWasteMarkerIcon(point.type, offset))}
            zIndexOffset={30}
            onInteract={onInteract}
            maxWidth={280}
          >
            <div className="flex items-center gap-2 font-black text-3xs uppercase tracking-widest" style={{ color: cfg.color }}>
              <i className={cfg.icon} style={{ fontSize: 12 }} />
              {t(`map.waste.type_${point.type}`)}
            </div>
            {point.name && (
              <div className="text-stone-900 font-bold text-xs">{point.name}</div>
            )}
            {point.description && (
              <div className="bg-stone-50 p-2 rounded-lg border border-stone-100 text-2xs text-[var(--text-muted)] italic">
                &ldquo;{point.description}&rdquo;
              </div>
            )}
            {point.operator && (
              <div className="text-2xs text-[var(--text-muted)]">
                {t('map.waste.operator')}: {point.operator}
              </div>
            )}
            <div className="text-4xs text-[var(--text-secondary)] font-bold pt-1 border-t border-stone-100">
              {t(`map.waste.source_${point.source}`)}
            </div>
          </ImagePopupMarker>
        );
      })}
    </>
  );
};

// --- Bathing markers with cross-layer collision handling ---

const createBathingMarkerIcon = (hasWarning: boolean, offset: [number, number] = [0, 0], temperature?: number): L.DivIcon => {
  const color = hasWarning ? '#ef4444' : '#0ea5e9';
  const size = 30;
  const warningBadge = hasWarning
    ? `<div style="position:absolute;top:-4px;right:-4px;width:14px;height:14px;background:#ef4444;border:1.5px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:900;color:white;">!</div>`
    : '';
  const tempBadge = (!hasWarning && temperature != null && temperature > 0)
    ? `<div style="position:absolute;bottom:-6px;right:-8px;background:white;border:1.5px solid ${color};border-radius:8px;padding:0 3px;font-size:8px;font-weight:900;color:${color};line-height:14px;white-space:nowrap;">\u{1F321}${Math.round(temperature)}\u00B0</div>`
    : '';
  return L.divIcon({
    className: 'custom-fa-marker',
    html: `<div style="position:relative;width:${size}px;height:${size}px;background:${color};border:2px solid rgba(255,255,255,0.7);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"><i class="fa-solid fa-person-swimming" style="font-size:14px;"></i>${warningBadge}${tempBadge}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2 - offset[0], size / 2 - offset[1]],
  });
};

interface BathingMarkerData {
  point: BathingPlace;
  offset: [number, number];
}

function computeBathingOffsets(
  bathingPoints: BathingPlace[],
  wastePoints: WastePoint[],
  zoom: number,
): BathingMarkerData[] {
  const collisionThreshold = 0.0003 * Math.pow(2, 15 - zoom);
  const OFFSET_PX = 18;
  const result: BathingMarkerData[] = [];

  for (const bp of bathingPoints) {
    // Check proximity to waste points (cross-layer)
    const nearWaste = wastePoints.some(wp =>
      Math.abs(bp.location.latitude - wp.location.latitude) < collisionThreshold &&
      Math.abs(bp.location.longitude - wp.location.longitude) < collisionThreshold
    );

    // Check proximity to other bathing points (same-layer)
    const nearBathing = bathingPoints.filter(other =>
      other.id !== bp.id &&
      Math.abs(bp.location.latitude - other.location.latitude) < collisionThreshold &&
      Math.abs(bp.location.longitude - other.location.longitude) < collisionThreshold
    );

    let offsetX = nearWaste ? OFFSET_PX : 0;
    if (nearBathing.length > 0) {
      const idx = bathingPoints.indexOf(bp);
      const groupIdx = nearBathing.findIndex(n => bathingPoints.indexOf(n) < idx);
      if (groupIdx === -1) {
        // First in group — keep offset from waste
      } else {
        offsetX += OFFSET_PX * (groupIdx + 1);
      }
    }
    result.push({ point: bp, offset: [offsetX, 0] });
  }
  return result;
}

const BathingMarkers: React.FC<{
  bathingPlaces: BathingPlace[];
  wastePoints: WastePoint[];
  zoom: number;
  currentViewBounds: MapBounds | null;
  onInteract: () => void;
  onPromote?: (bp: BathingPlace) => void;
}> = ({ bathingPlaces, wastePoints, zoom, currentViewBounds, onInteract, onPromote }) => {
  const { t } = useTranslation();
  const [profileData, setProfileData] = useState<Record<string, Partial<BathingPlace>>>({});
  const [loadingProfiles, setLoadingProfiles] = useState<Set<string>>(new Set());

  const markersWithOffsets = useMemo(() => {
    const visible = currentViewBounds
      ? bathingPlaces.filter(bp =>
          bp.location.latitude >= currentViewBounds.latMin &&
          bp.location.latitude <= currentViewBounds.latMax &&
          bp.location.longitude >= currentViewBounds.lonMin &&
          bp.location.longitude <= currentViewBounds.lonMax
        )
      : bathingPlaces;
    return computeBathingOffsets(visible, wastePoints, zoom);
  }, [bathingPlaces, wastePoints, zoom, currentViewBounds]);

  const loadProfile = useCallback(async (profileId: string) => {
    if (profileData[profileId] || loadingProfiles.has(profileId)) return;
    setLoadingProfiles(prev => new Set(prev).add(profileId));
    try {
      const data = await fetchSwedishBathingProfile(profileId);
      setProfileData(prev => ({ ...prev, [profileId]: data }));
    } finally {
      setLoadingProfiles(prev => { const n = new Set(prev); n.delete(profileId); return n; });
    }
  }, [profileData, loadingProfiles]);

  return (
    <>
      {markersWithOffsets.map(({ point, offset }) => {
        const profile = point.profileId ? profileData[point.profileId] : undefined;
        const hasWarning = point.waterQualityWarning || profile?.waterQualityWarning || false;
        const rawTemp = profile?.waterTemperature ?? point.waterTemperature;
        const stale = isTemperatureStale(point.temperatureTime);
        const temp = stale ? undefined : rawTemp;

        return (
          <Marker
            key={point.id}
            position={getPointPosition(point)}
            icon={cachedIcon(`bath-${hasWarning}-${offset}-${temp ?? 'x'}`, () => createBathingMarkerIcon(hasWarning, offset, temp ?? undefined))}
            zIndexOffset={35}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stop(e);
                onInteract();
                if (onPromote) { onPromote(point); return; }
              },
              popupopen: () => { if (point.source === 'hav' && point.profileId) loadProfile(point.profileId); },
            }}
          >
            {!onPromote && <Popup className="custom-popup" maxWidth={300}>
              <div className="p-3 space-y-2 min-w-[200px]">
                <div className="flex items-center gap-2 font-black text-3xs uppercase tracking-widest" style={{ color: hasWarning ? '#ef4444' : '#0ea5e9' }}>
                  <i className="fa-solid fa-person-swimming" style={{ fontSize: 12 }} />
                  {t('map.bathing.title')}
                </div>
                <div className="text-stone-900 font-bold text-xs">{point.name}</div>

                {hasWarning && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-start gap-2">
                    <i className="fa-solid fa-triangle-exclamation text-red-500 mt-0.5" style={{ fontSize: 12 }} />
                    <div className="text-2xs text-red-700 font-semibold">
                      {t('map.bathing.water_quality_warning')}
                      {(profile?.algalBloom || point.algalBloom) && (
                        <div className="font-normal mt-0.5">{t('map.bathing.algal_bloom')}</div>
                      )}
                      {(profile?.bathingRestriction || point.bathingRestriction) && (
                        <div className="font-normal mt-0.5">{t('map.bathing.bathing_restriction')}</div>
                      )}
                    </div>
                  </div>
                )}

                {point.municipality && (
                  <div className="text-2xs text-[var(--text-muted)]">
                    {t('map.bathing.municipality')}: {point.municipality}
                  </div>
                )}

                {rawTemp != null && rawTemp > 0 && (
                  <div className="text-2xs text-[var(--text-muted)]">
                    {stale
                      ? <span className="italic">{t('map.bathing.temperature_outdated')}</span>
                      : <>{t('map.bathing.temperature')}: {rawTemp.toFixed(1)}°C</>
                    }
                  </div>
                )}

                {point.description && (
                  <div className="bg-stone-50 p-2 rounded-lg border border-stone-100 text-2xs text-[var(--text-muted)] italic">
                    &ldquo;{point.description}&rdquo;
                  </div>
                )}

                {point.source === 'hav' && point.profileId && loadingProfiles.has(point.profileId) && (
                  <div className="text-3xs text-[var(--text-muted)] italic">
                    {t('map.bathing.loading_profile')}
                  </div>
                )}

                <div className="text-4xs text-[var(--text-secondary)] font-bold pt-1 border-t border-stone-100">
                  {t(`map.bathing.source_${point.source}`)}
                </div>
              </div>
            </Popup>}
          </Marker>
        );
      })}
    </>
  );
};

const ClusteredWildlife: React.FC<{
  wildlife: WildlifeMapObservation[],
  zoom: number,
  currentViewBounds: MapBounds | null,
  onInteract: () => void,
  isPaidUser?: boolean
}> = ({ wildlife, zoom, currentViewBounds, onInteract, isPaidUser = true }) => {
  const map = useMap();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';

  const clusters = useMemo(() => {
    const visible = currentViewBounds
      ? wildlife.filter(w =>
        w.location.latitude >= currentViewBounds.latMin &&
        w.location.latitude <= currentViewBounds.latMax &&
        w.location.longitude >= currentViewBounds.lonMin &&
        w.location.longitude <= currentViewBounds.lonMax
      )
      : wildlife;

    if (zoom >= 18) {
      const byCoord = new Map<string, WildlifeMapObservation[]>();
      visible.forEach((w) => {
        const key = `${w.location?.latitude?.toFixed(5) || 0},${w.location?.longitude?.toFixed(5) || 0}`;
        const prev = byCoord.get(key) || [];
        prev.push(w);
        byCoord.set(key, prev);
      });

      return Array.from(byCoord.values()).map((members) => ({
        item: members[0],
        count: members.length,
        isCluster: members.length > 1,
        members
      }));
    }

    if (visible.length < 5) {
      return visible.map(w => ({ item: w, count: 1, isCluster: false, members: [w] }));
    }

    const threshold = 0.0005 * Math.pow(2, 18 - zoom);
    const grouped: { item: WildlifeMapObservation, count: number, isCluster: boolean, members: WildlifeMapObservation[] }[] = [];
    const used = new Set<number>();

    visible.forEach((w, i) => {
      if (used.has(i)) return;
      const group = [i];
      for (let j = i + 1; j < visible.length; j++) {
        if (used.has(j)) continue;
        const n = visible[j];
        const latDiff = Math.abs(w.location.latitude - n.location.latitude);
        const lonDiff = Math.abs(w.location.longitude - n.location.longitude);
        if (latDiff < threshold && lonDiff < threshold) group.push(j);
      }
      if (group.length >= 3) {
        group.forEach(idx => used.add(idx));
        grouped.push({ item: w, count: group.length, isCluster: true, members: group.map((idx) => visible[idx]) });
      } else {
        used.add(i);
        grouped.push({ item: w, count: 1, isCluster: false, members: [w] });
      }
    });

    return grouped;
  }, [wildlife, zoom, currentViewBounds]);

  return (
    <>
      {clusters.map((g, idx) => {
        const w = g.item;
        if (g.isCluster) {
          const groupedSpecies = Array.from(
            g.members.reduce((acc, curr) => {
              acc.set(curr.species, (acc.get(curr.species) || 0) + 1);
              return acc;
            }, new Map<string, number>())
          );

          return (
            <Marker
              key={`w-cluster-${idx}`}
              position={[w.location.latitude, w.location.longitude]}
              icon={wildlifeClusterIcon(g.count)}
              zIndexOffset={65}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stop(e);
                  if (zoom < 18) {
                    if ((map as any)._loaded) map.flyTo([w.location.latitude, w.location.longitude], capZoomForFree(Math.min(18, zoom + 2), isPaidUser), { duration: 0.5 });
                    return;
                  }
                  onInteract();
                }
              }}
            >
              <Popup className="custom-popup">
                <div className="p-3 space-y-2 min-w-[220px]">
                  <div className="flex items-center gap-2 text-violet-600 font-black text-3xs uppercase tracking-widest">
                    {t('map.wildlife.cluster', { count: g.count })}
                  </div>
                  {groupedSpecies.map(([species, count]) => (
                    <div key={`${species}-${count}`} className="text-tiny text-stone-800 font-semibold">
                      {species} ({count} stk)
                    </div>
                  ))}
                  <p className="text-3xs text-[var(--text-muted)] leading-relaxed">
                    {t('map.wildlife.same_location')}
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        }

        return (
          <Marker
            key={`wildlife-${w.id}-${idx}`}
            position={[w.location.latitude, w.location.longitude]}
            icon={wildlifeMarkerIcon(w.species)}
            zIndexOffset={70}
            eventHandlers={{ click: (e) => { L.DomEvent.stop(e); onInteract(); } }}
          >
            <Popup className="custom-popup">
              <div className="p-3 space-y-2 min-w-[240px] max-w-[280px]">
                <div className="flex items-center gap-2 text-violet-600 font-black text-3xs uppercase tracking-widest">
                  {t('map.wildlife.single')}
                </div>
                <div className="text-stone-900 font-black text-sm leading-tight">
                  {w.species}
                </div>
                <div className="text-2xs text-[var(--text-muted)] font-bold">
                  {t('map.wildlife.last_seen', { date: w.observedAt ? w.observedAt.toLocaleDateString(locale) : t('map.wildlife.unknown_date') })}
                </div>
                {!!w.observedCount && (
                  <div className="text-2xs text-[var(--text-muted)]">{t('map.wildlife.count_seen')} <span className="font-bold">{w.observedCount}</span></div>
                )}
                {(w.locality || w.municipality || w.county) && (
                  <div className="text-2xs text-[var(--text-muted)]">
                    {t('map.wildlife.location')} {[w.locality, w.municipality, w.county].filter(Boolean).join(', ')}
                  </div>
                )}
                {!!w.behavior && (
                  <div className="text-2xs text-[var(--text-muted)]">
                    {t('map.wildlife.behavior')} <span className="font-semibold">{w.behavior}</span>
                  </div>
                )}
                {!!w.habitat && (
                  <div className="text-2xs text-[var(--text-muted)]">
                    {t('map.wildlife.habitat')} <span className="font-semibold">{w.habitat}</span>
                  </div>
                )}
                {!!w.precisionMeters && (
                  <div className="text-2xs text-[var(--text-muted)]">
                    {t('map.wildlife.precision', { meters: w.precisionMeters })}
                  </div>
                )}
                {w.thumbnailUrl && (
                  <img
                    src={w.thumbnailUrl}
                    alt={`${t('map.wildlife.observation_of')} ${w.species}`}
                    className="w-full h-28 object-cover rounded-lg border border-stone-200"
                    loading="lazy"
                  />
                )}
                {(w.datasetName || w.institution) && (
                  <div className="text-3xs text-[var(--text-muted)]">
                    {t('map.wildlife.source')} {[w.datasetName, w.institution].filter(Boolean).join(' - ')}
                  </div>
                )}
                {w.detailUrl && (
                  <a
                    href={w.detailUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-2xs font-black text-violet-700 hover:underline"
                  >
                    {t('map.wildlife.view_source')}
                  </a>
                )}
                <p className="text-3xs text-[var(--text-muted)] leading-relaxed">
                  {t('map.wildlife.warning')}
                </p>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

// --- TurMenu: kombinert turlogg + posisjonsdeling ---

interface TurMenuProps {
  isPaidUser: boolean;
  hasGpsAccess?: boolean;
  trackingStatus: 'idle' | 'recording' | 'paused';
  onStartTracking: () => void;
  onOpenMyTracks: () => void;
  isSharing: boolean;
  nearbyUsers: import('../types').LiveLocation[];
  onStartSharing?: (groupId: string, groupName: string) => void;
  onStopSharing?: () => void;
  shareError?: string | null;
  currentUser?: AppUser | null;
  collaborativeMaps?: import('../types').CollaborativeMap[];
  activeGroupId?: string | null;
  activeGroupName?: string | null;
  groupViewers?: import('../types').LiveViewer[];
}

const TurMenu: React.FC<TurMenuProps> = ({
  isPaidUser, hasGpsAccess: hasGpsAccessProp, trackingStatus, onStartTracking, onOpenMyTracks,
  isSharing, nearbyUsers, onStartSharing, onStopSharing, shareError, currentUser,
  collaborativeMaps = [], activeGroupId, activeGroupName, groupViewers = [],
}) => {
  const hasGpsAccess = hasGpsAccessProp ?? isPaidUser;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showGroupPicker) setExpandedGroupId(null);
  }, [showGroupPicker]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isActive = trackingStatus !== 'idle' || isSharing;
  const activeDot = isActive ? 'bg-accent animate-pulse' : null;

  return (
    <div ref={menuRef} className="relative md:static pointer-events-auto">
      <button
        onClick={() => setOpen(v => !v)}
        title={t("auto.map.title.tur_logg_og_posisjon", "Tur: logg og posisjon")}
        className={`relative w-11 h-11 rounded-xl flex items-center justify-center shadow-xl active:scale-95 transition-all border ${
          isActive
            ? 'bg-drab-2 border-accent text-accent'
            : 'bg-drab border-drab-line text-[var(--text-secondary)]'
          }`}
      >
        {trackingStatus === 'recording'
          ? <>
              <div className="w-3 h-3 rounded-sm bg-accent animate-pulse md:hidden" />
              <Radio size={18} className="text-accent hidden md:block animate-pulse" />
            </>
          : trackingStatus === 'paused'
            ? <><Radio size={14} className="text-warn md:hidden" /><Radio size={18} className="text-warn hidden md:block" /></>
            : <><Radio size={14} className="md:hidden" /><Radio size={18} className="hidden md:block" /></>
        }
        {/* Multi-state indicator dots */}
        {activeDot && (
          <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${activeDot}`} />
        )}
        {nearbyUsers.length > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent text-[var(--ground)] text-4xs font-black rounded-full flex items-center justify-center leading-none">
            {nearbyUsers.length}
          </span>
        )}
        {!hasGpsAccess && trackingStatus === 'idle' && !isSharing && (
          <Lock size={8} className="absolute bottom-1 right-1 text-warn" />
        )}
      </button>

      {open && (
        <div className="fixed right-2 top-[calc(var(--safe-area-top,0px)+60px)] md:absolute md:inset-auto md:top-0 md:right-full md:mr-3 md:bottom-auto w-56 bg-white/97 backdrop-blur-xl border border-stone-200 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-top-2 z-[9999] max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between px-3 pt-3 pb-1">
            <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t("auto.map.tur_posisjon", "Tur & posisjon")}</p>
            <button onClick={() => setOpen(false)} className="text-[var(--text-secondary)] hover:text-[var(--text-muted)] transition-colors">
              <X size={14} />
            </button>
          </div>
          {/* Turlogg section */}
          <div className="px-3 pt-1 pb-1">
            <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">{t("auto.map.turlogg", "Turlogg")}</p>
            {trackingStatus === 'idle' ? (
              <button
                onClick={() => { onStartTracking(); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-bold text-[var(--text-muted)] hover:bg-stone-50 transition-colors"
              >
                <div className="w-7 h-7 rounded-lg bg-drab/15 flex items-center justify-center flex-shrink-0">
                  <div className="w-3 h-3 rounded-full bg-drab" />
                </div>
                Start turlogg
                {!hasGpsAccess && <Lock size={10} className="text-warn ml-auto" />}
              </button>
            ) : (
              <button
                onClick={() => { onOpenMyTracks(); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-bold text-[var(--text-muted)] hover:bg-stone-50 transition-colors"
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${trackingStatus === 'recording' ? 'bg-accent/15' : 'bg-warn/15'}`}>
                  <Radio size={14} className={trackingStatus === 'recording' ? 'text-accent animate-pulse' : 'text-warn'} />
                </div>
                <span>
                  {trackingStatus === 'recording' ? 'Sporer...' : 'Satt på pause'}
                  <span className="block text-2xs font-normal text-[var(--text-secondary)]">{t("auto.map.trykk_for_mine_turer", "Trykk for mine turer")}</span>
                </span>
                <div className={`ml-auto w-2 h-2 rounded-full ${trackingStatus === 'recording' ? 'bg-accent animate-pulse' : 'bg-warn'}`} />
              </button>
            )}
          </div>

          {/* Del posisjon section — gruppe-scoped */}
          {currentUser && (
            <>
              <div className="h-px bg-stone-100 mx-3" />
              <div className="px-3 pb-3 pt-1">
                <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">{t('profile.live_sharing.heading', 'Posisjon')}</p>

                {isSharing && activeGroupId ? (
                  /* --- Aktiv deling: vis gruppe + medlemmer --- */
                  <div className="space-y-2">
                    <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-accent/10 border border-accent/25">
                      <div className="w-7 h-7 rounded-lg bg-accent/15 flex items-center justify-center flex-shrink-0">
                        <Radio size={14} className="text-accent animate-pulse" />
                      </div>
                      <span className="flex-1 text-left text-[12px]">
                        <span className="font-bold text-accent-deep">{t('profile.live_sharing.sharing_in', 'Deler i')} {activeGroupName}</span>
                        {nearbyUsers.length > 0 && (
                          <span className="block text-2xs font-normal text-accent">{nearbyUsers.length} {t('profile.live_sharing.members_active', 'aktive')}</span>
                        )}
                      </span>
                    </div>

                    {/* Medlemsliste med statusindikator */}
                    {(() => {
                      const activeMap = collaborativeMaps.find(m => m.id === activeGroupId);
                      if (!activeMap) return null;
                      const sharingUids = new Set(nearbyUsers.map(u => u.userId));
                      const viewingUids = new Set(groupViewers.map(v => v.userId));
                      return (
                        <div className="px-1 space-y-0.5">
                          {activeMap.members
                            .filter(m => m.uid !== currentUser?.uid)
                            .map(m => {
                              const isSharingPos = sharingUids.has(m.uid);
                              const isViewing = viewingUids.has(m.uid);
                              return (
                                <div key={m.uid} className="flex items-center gap-2 px-2 py-1 rounded-lg">
                                  <div className="relative w-5 h-5 rounded-full bg-stone-200 flex items-center justify-center text-3xs font-black text-stone-500 overflow-hidden flex-shrink-0">
                                    {m.photoURL
                                      ? <img src={m.photoURL} className="w-full h-full object-cover" alt="" />
                                      : (m.displayName || '?').charAt(0).toUpperCase()
                                    }
                                  </div>
                                  <span className="text-tiny text-[var(--text-muted)] flex-1 truncate">{m.displayName}</span>
                                  {isSharingPos
                                    ? <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0" title={t('profile.live_sharing.status_sharing', 'Deler posisjon')} />
                                    : isViewing
                                      ? <span className="w-2 h-2 rounded-full bg-accent/50 flex-shrink-0" title={t('profile.live_sharing.status_viewing', 'Ser på kartet')} />
                                      : <span className="w-2 h-2 rounded-full bg-stone-300 flex-shrink-0" title={t('profile.live_sharing.status_offline', 'Offline')} />
                                  }
                                </div>
                              );
                            })}
                        </div>
                      );
                    })()}

                    <button
                      onClick={(e) => { e.stopPropagation(); onStopSharing?.(); }}
                      type="button"
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl text-tiny font-bold text-red-500 hover:bg-red-50 active:bg-red-100 transition-colors"
                    >
                      {t('profile.live_sharing.stop_sharing', 'Stopp deling')}
                    </button>
                  </div>
                ) : showGroupPicker ? (
                  /* --- Gruppevelger: vis samarbeidsmapper --- */
                  <div className="space-y-1">
                    <p className="text-2xs text-[var(--text-secondary)] px-1 mb-1">{t('profile.live_sharing.select_group', 'Velg gruppe')}</p>
                    {collaborativeMaps.length === 0 ? (
                      <p className="text-2xs text-[var(--text-secondary)] px-1 py-2">{t('profile.live_sharing.no_groups', 'Opprett eller bli med i en samarbeidsmappe for å dele posisjon.')}</p>
                    ) : (
                      collaborativeMaps.map(m => {
                        const isExpanded = expandedGroupId === m.id;
                        return (
                          <div key={m.id} className="rounded-xl overflow-hidden hover:bg-stone-50 transition-colors">
                            <div className="flex items-stretch">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onStartSharing?.(m.id, m.name);
                                  setShowGroupPicker(false);
                                }}
                                type="button"
                                className="flex-1 flex items-center gap-2.5 px-3 py-2 text-[12px] font-bold text-[var(--text-muted)] active:bg-stone-100 transition-colors"
                              >
                                <div className="w-7 h-7 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                                  <Users size={14} className="text-[var(--text-muted)]" />
                                </div>
                                <span className="flex-1 text-left">
                                  {m.name}
                                  <span className="block text-2xs font-normal text-[var(--text-secondary)]">{m.members.length} {t('profile.live_sharing.members', 'medlemmer')}</span>
                                </span>
                                {/* Mini-avatarer */}
                                <div className="flex -space-x-1.5">
                                  {m.members.slice(0, 4).map(mb => (
                                    <div key={mb.uid} className="w-4 h-4 rounded-full bg-stone-200 border border-white flex items-center justify-center text-[7px] font-black text-stone-500 overflow-hidden">
                                      {mb.photoURL
                                        ? <img src={mb.photoURL} className="w-full h-full object-cover" alt="" />
                                        : (mb.displayName || '?').charAt(0).toUpperCase()
                                      }
                                    </div>
                                  ))}
                                  {m.members.length > 4 && (
                                    <div className="w-4 h-4 rounded-full bg-stone-300 border border-white flex items-center justify-center text-[7px] font-black text-white">
                                      +{m.members.length - 4}
                                    </div>
                                  )}
                                </div>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedGroupId(isExpanded ? null : m.id);
                                }}
                                type="button"
                                aria-expanded={isExpanded}
                                aria-label={t('profile.live_sharing.toggle_members', 'Vis medlemmer')}
                                className="px-2 flex items-center text-[var(--text-secondary)] hover:text-[var(--text-muted)] active:bg-stone-100 transition-colors"
                              >
                                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            </div>
                            {isExpanded && (
                              <div className="px-3 pb-2 pt-1 space-y-0.5 border-t border-stone-100 bg-stone-50/50">
                                {m.members.map(mb => (
                                  <div key={mb.uid} className="flex items-center gap-2 px-1 py-1">
                                    <div className="w-5 h-5 rounded-full bg-stone-200 flex items-center justify-center text-3xs font-black text-stone-500 overflow-hidden flex-shrink-0">
                                      {mb.photoURL
                                        ? <img src={mb.photoURL} className="w-full h-full object-cover" alt="" />
                                        : (mb.displayName || '?').charAt(0).toUpperCase()
                                      }
                                    </div>
                                    <span className="text-tiny text-[var(--text-muted)] flex-1 truncate font-normal">
                                      {mb.displayName}
                                      {mb.uid === currentUser?.uid && (
                                        <span className="text-[var(--text-secondary)]"> ({t('profile.live_sharing.you', 'du')})</span>
                                      )}
                                    </span>
                                    {mb.role === 'owner' && (
                                      <span className="text-4xs font-black text-amber-600 uppercase tracking-widest">{t('profile.live_sharing.role_owner', 'Eier')}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowGroupPicker(false); }}
                      type="button"
                      className="w-full text-center text-2xs text-[var(--text-secondary)] py-1 hover:text-[var(--text-muted)]"
                    >
                      {t('common.cancel', 'Avbryt')}
                    </button>
                  </div>
                ) : (
                  /* --- Inaktiv: vis toggle-knapp --- */
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowGroupPicker(true); }}
                      type="button"
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-bold text-[var(--text-muted)] hover:bg-stone-50 active:bg-stone-100 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                        <Radio size={14} className="text-[var(--text-muted)]" />
                      </div>
                      <span className="flex-1 text-left">
                        {t('profile.live_sharing.share_button', 'Del posisjon live')}
                        <span className="block text-2xs font-normal text-[var(--text-secondary)]">{t('profile.live_sharing.explanation')}</span>
                      </span>
                    </button>
                    {shareError && (
                      <p className="text-2xs text-red-500 px-3 pb-1 leading-tight">{shareError}</p>
                    )}
                  </>
                )}
              </div>
            </>
          )}

        </div>
      )}
    </div>
  );
};

const MapComponent: React.FC<MapProps> = ({
  spots, center, mapCommand, currentViewBounds, zoom, selectedSpotId, activeHikingTrailId, onSelectSpot, onSelectHikingTrail, onMapMove, onMapClick, onMapLongPress, onMapContextMenu, onSearchThisArea, onDeepAnalyzeViewport, searchCircle,
  layers, onUpdateLayers, isLoading, isFetchingBounds, spotsReady = true, isFirstTimeLoad = false, scanProgress, onOpenAuth, isPaidUser: isPaidUserFromApp, userTier = 0, onOpenUpgrade, currentUser, activeAnalysisIds = EMPTY_ANALYSIS_IDS as Set<string>, isCoveredByModal = false, isDev, onAnalyzeRoute, onLoadCachedRouteSpots, onPeakClick, onRouteActivated, isAnalyzingRoute,
  routes, activeRouteIds, setActiveRouteIds, previewRegion, previewFilteredRouteIds, onRegionLoading,
  activeRoutePath, followRouteCoords = null, followRouteNearestIndex = null, routeCategoryFilters, routeVehicleFilterOff, onToggleRouteVehicleFilter, loadingState, errorState: errorStateProp, wikiSpots,
  highlightedGeoJson, draftRouteGeoJson, isMobileListOpen = false, sidebarWidthPx = 0, onSelectRoute, onCancelLoading, topUiOffsetPx = 0, shareContextText = '',
  trackPoints = [], trackingStatus = 'idle', onStartTracking, onOpenMyTracks, isFollowingRoute = false,
  followMapOrientation = 'north_up', onToggleFollowMapOrientation, nearestRoutePoint = null, activeFollowTarget = null,
  userPosition: userPositionProp = null,
  userHeading = null,
  trailerHeading = null,
  gpsLastUpdated = 0,
  onGpsPositionAcquired,
  autoFollowUser = false,
  onDropCandidate,
  autonavCandidates = [],
  nearbyUsers = [], isSharing = false, onStartSharing, onStopSharing, shareError = null,
  collaborativeMaps = [], activeGroupId = null, activeGroupName = null, groupViewers = [],
  myContentLines, myContentAreas, myContentWaypoints, focusedWaypoints,
  drawingState, drawingToolType, drawingPoints, onDrawingMapClick, onDeleteContent, onEditContent,
  drawingEditPoints, drawingEditToolType, onDrawingEditUpdatePoint, onDrawingEditInsertPoint, onDrawingEditRemovePoint,
  isMyContentOpen,
  coordinatePin,
  onClearCoordinatePin,
  is3DMode,
  onToggle3D,
  onToggleRecorder,
  recorderVisible,
  onPromoteBathingPlace,
  onEnsureSpotPersisted,
  onRequestCountry,
  nordicEnabledCountries,
  nordicConfigReady,
  isAdmin,
  adminBadgeCount = 0,
  onOpenAdmin,
  onOpenAdminInbox,
  authReady,
  selectedRouteId: selectedRouteIdProp,
  barrierHighlightSpot,
  onClearBarrierHighlight,
  routePlanAlternatives, routePlanSelectedIndex, onRoutePlanSelectIndex,
  isRoutePlanning = false, onAddRouteStop,
  routePlanStartCoords, routePlanEndCoords, routePlanStopCoords, onRemoveRouteStop, onSelectRouteStop,
  routeHighlightWebcams = [],
  routeAttractions,
  onRouteAttractionTap,
  discoverItems,
  discoverHighlight,
  onClearHighlight,
  onDiscoverMarkerClick,
  onContextualAlertsChange,
  nearestSpot,
  nearestSpotDistanceM,
onShowSpotDetails,
  onDownloadSpotArea,
  onCreateFromPhoto,
  showLayerTip = false,
  onDismissLayerTip,
}) => {
  const { t } = useTranslation();
  const isPaidUser = typeof isPaidUserFromApp === 'boolean' ? isPaidUserFromApp : hasPaidAccess(currentUser);
  const showNudge = usePremiumNudge();
  const leafletMapRef = useRef<L.Map | null>(null);
  const [showSearchButton, setShowSearchButton] = useState(false);
  const [mobileControlsExpanded, setMobileControlsExpanded] = useState(false);
  const [showMobileToolsSheet, setShowMobileToolsSheet] = useState(false);
  const [showOfflineManager, setShowOfflineManager] = useState(false);
  const [clickCoords, setClickCoords] = useState<[number, number] | null>(null);

  // Re-evaluate GPS freshness every 15s so the icon color transitions from green→red
  const [, setGpsTick] = useState(0);
  useEffect(() => {
    if (!gpsLastUpdated) return;
    const id = setInterval(() => setGpsTick(n => n + 1), 15000);
    return () => clearInterval(id);
  }, [gpsLastUpdated]);
  const [shareCopied, setShareCopied] = useState(false);
  const [showNavSharePicker, setShowNavSharePicker] = useState(false);
  const [navExpandedGroupId, setNavExpandedGroupId] = useState<string | null>(null);

  // --- Kandidat-fangst under auto-nav (kjapp markering av fricamp mens man kjører) ---
  // Første trykk viser en efemer «Fricamp?»-boble (kun lokal state, ingen skriv).
  // Trykk boblen → lagre kandidat. Ingen bekreftelse innen 5 s → forkast (så feiltrykk
  // ikke lager søppel). All kvalitetssikring skjer i «Til gjennomgang» i ettertid.
  const [pendingCandidate, setPendingCandidate] = useState<Coordinates | null>(null);
  const candidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCandidateCapture = useCallback((coords: Coordinates) => {
    setPendingCandidate(coords);
    if (candidateTimerRef.current) clearTimeout(candidateTimerRef.current);
    candidateTimerRef.current = setTimeout(() => setPendingCandidate(null), 5000);
  }, []);
  const confirmCandidate = useCallback(() => {
    if (candidateTimerRef.current) clearTimeout(candidateTimerRef.current);
    setPendingCandidate((c) => { if (c) onDropCandidate?.(c); return null; });
  }, [onDropCandidate]);
  useEffect(() => {
    // Forkast en ubekreftet boble når auto-nav avsluttes.
    if (!autoFollowUser && candidateTimerRef.current) {
      clearTimeout(candidateTimerRef.current);
      setPendingCandidate(null);
    }
  }, [autoFollowUser]);
  useEffect(() => () => { if (candidateTimerRef.current) clearTimeout(candidateTimerRef.current); }, []);
  useEffect(() => {
    if (!showNavSharePicker) setNavExpandedGroupId(null);
  }, [showNavSharePicker]);
  const [followUserPosition, setFollowUserPosition] = useState(false);
  const followUserRef = useRef(false);
  const [showMapControlsHint, setShowMapControlsHint] = useState(false);
  const [showTrailInfo, setShowTrailInfo] = useState(false);
  const [showTrailFlyover, setShowTrailFlyover] = useState(false);
  const [trailFlyoverProfile, setTrailFlyoverProfile] = useState<ElevationPoint[] | null>(null);
  const [loadingTrailFlyover, setLoadingTrailFlyover] = useState(false);
  const [trailElevationById, setTrailElevationById] = useState<Record<string, ElevationPoint[]>>({});
  const [trailPeakById, setTrailPeakById] = useState<Record<string, { coord: Coordinates, ele: number } | null>>({});
  const [loadingTrailElevationId, setLoadingTrailElevationId] = useState<string | null>(null);
  const [activeRouteGeometry, setActiveRouteGeometry] = useState<Coordinates[] | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isCoarsePointer = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  const isMobilePhone = isCoarsePointer && Math.min(window.innerWidth, window.innerHeight) < 600;

  /**
   * Hvor mange flisrader Leaflet beholder UTENFOR viewport (`keepBuffer`).
   *
   * Sto hardkodet til 6 mot Leaflets default 2. Begrunnelsen står ved
   * `autoFollowActiveRef` nedenfor og er god: under kjøring vil man ha en ring av
   * ferdiglastede fliser rundt skjermen, så kartet ikke blinker mens man ruller.
   *
   * Men kostnaden er ikke den samme på telefon. Et telefon-viewport er ~2×4 fliser,
   * så buffer 6 gir 14×16 = 224 fliser i DOM mot 8 synlige. Med satellitt er det TO
   * lag (bilde + stedsnavn), altså 448 <img>-noder med fotografisk innhold — og hver
   * flis går gjennom Cache API + IndexedDB (se services/tileCache.ts). Det var en
   * målbar del av treghets-rapporten 23.08.2026.
   *
   * Nå: full ring når den faktisk brukes til noe (kjøremodus, eller skjerm med plass),
   * Leaflets default ellers.
   */
  const baseKeepBuffer = isMobilePhone && !autoFollowUser ? 2 : 6;
  const [isPhoneLandscape, setIsPhoneLandscape] = useState(
    () => isMobilePhone && window.innerWidth > window.innerHeight
  );
  useEffect(() => {
    const update = () => {
      const mobile = window.matchMedia('(pointer: coarse)').matches && Math.min(window.innerWidth, window.innerHeight) < 600;
      setIsPhoneLandscape(mobile && window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => { window.removeEventListener('resize', update); window.removeEventListener('orientationchange', update); };
  }, []);
  const isNavigatingMobile = isFollowingRoute === true && (isMobile || isCoarsePointer);
  // During navigation on mobile/tablet: DON'T collapse — show reduced set of buttons directly
  const shouldCollapseControls = isNavigatingMobile ? false : (isMobile || (isFollowingRoute === true && isCoarsePointer));

  // Auto-collapse controls when route following starts on tablet
  useEffect(() => {
    if (isFollowingRoute && isCoarsePointer && !isMobile) {
      setMobileControlsExpanded(false);
    }
  }, [isFollowingRoute]);

  const [activeCoverageProvider, setActiveCoverageProvider] = useState<CoverageProvider>(null);
  const [userPannedAway, setUserPannedAway] = useState(false);
  const [fishingFilters, setFishingFilters] = useState({ rare: true, registered: true, good: true, spawning: true, protected: true });
  const [fishLegendOpen, setFishLegendOpen] = useState(!isMobile);
  const [mapLegendOpen, setMapLegendOpen] = useState(false);
  const [isMarineLegendOpen, setIsMarineLegendOpen] = useState(false);
  const isMarineLegendZoom = zoom >= 13;
  const isInNorway = center.latitude >= 57.5 && center.latitude <= 71.5 && center.longitude >= 4.5 && center.longitude <= 31.5;

  // Click-to-identify for the hiking WMS layers: tapping the map while trail layers are
  // visible queries GetFeatureInfo, then fetches the full route geometry via WFS so the
  // clicked trail gets the same rich treatment as spot trails (polyline, elevation, flyover).
  const [trailInfoPopup, setTrailInfoPopup] = useState<{ position: [number, number]; loading?: boolean; features: TrailClickInfo[] } | null>(null);
  const [clickedTrails, setClickedTrails] = useState<HikingTrail[]>([]);
  const [clickedTrailId, setClickedTrailId] = useState<string | null>(null);
  const trailProbeSeq = useRef(0);
  const probeTrailInfo = useCallback(async (coords: Coordinates) => {
    const active: RouteType[] = [];
    if (layers.hikingFoot) active.push('foot');
    if (layers.hikingSki) active.push('ski');
    if (layers.hikingBike) active.push('bike');
    if (layers.hikingOther) active.push('other');
    if (active.length === 0 || !isInNorway) return;
    const seq = ++trailProbeSeq.current;
    const displayMpp = (156543.03392 * Math.cos((coords.latitude * Math.PI) / 180)) / Math.pow(2, zoom);
    try {
      const infos = await fetchTrailInfoAtPoint(coords, active, displayMpp * 4);
      if (trailProbeSeq.current !== seq || infos.length === 0) return;
      // We know a route was hit — show a loading popup while the WFS geometry loads.
      setTrailInfoPopup({ position: [coords.latitude, coords.longitude], loading: true, features: infos });
      const trails = await fetchGeoNorgeNearbyRoutes(coords).catch(() => [] as HikingTrail[]);
      if (trailProbeSeq.current !== seq) return;

      // Pair each identified route with its stitched WFS geometry (by name, else nearest
      // same-type trail) and merge the richer GetFeatureInfo attributes onto it.
      const matched: HikingTrail[] = [];
      for (const info of infos) {
        const trail = trails.find(tr =>
          tr.routeType === info.routeType &&
          !matched.some(m => m.id === tr.id) &&
          (info.name ? tr.name === info.name : tr.distanceToStart < 60)
        );
        if (trail) {
          matched.push({
            ...trail,
            difficulty: trail.difficulty || info.grading,
            symbol: info.marking,
            maintainer: info.maintainer,
          });
        }
      }
      if (matched.length > 0) {
        matched.sort((a, b) => a.distanceToStart - b.distanceToStart);
        setTrailInfoPopup(null);
        setClickedTrails(matched);
        setClickedTrailId(matched[0].id);
        setShowTrailInfo(true);
        // Drop the add-spot target pin — the click was clearly aimed at the trail.
        setClickCoords(null);
        onMapClick(null);
      } else {
        // No geometry match — fall back to a plain attribute popup.
        setTrailInfoPopup({ position: [coords.latitude, coords.longitude], features: infos });
      }
    } catch { /* identify is best-effort; stay silent on network errors */ }
  }, [layers.hikingFoot, layers.hikingSki, layers.hikingBike, layers.hikingOther, isInNorway, zoom, onMapClick]);

  // A spot-trail selection takes over from a map-click trail.
  useEffect(() => {
    if (activeHikingTrailId) { setClickedTrails([]); setClickedTrailId(null); }
  }, [activeHikingTrailId]);

  // Auto-open marine legend when zoomed in enough to see sea depth
  useEffect(() => {
    if (isMarineLegendZoom && isInNorway && !isMarineLegendOpen) {
      setIsMarineLegendOpen(true);
    } else if (!isMarineLegendZoom && isMarineLegendOpen) {
      setIsMarineLegendOpen(false);
    }
  }, [isMarineLegendZoom, isInNorway]);

  const mapRef = useRef<L.Map | null>(null);
  const [mapKey, setMapKey] = useState(0);
  const activeHeading = trackPoints.length > 0 ? (trackPoints[trackPoints.length - 1].heading ?? 0) : 0;
  const shouldRotateToHeading = isFollowingRoute && followMapOrientation === 'heading_up' && Number.isFinite(activeHeading);
  const mapRotationDeg = shouldRotateToHeading ? -activeHeading : 0;
  const hasBathingAccess = isPaidUser || hasTierAccess({ tier: userTier }, 'bathing_places');

  // Force Leaflet to re-initialize after the container has been painted at full size.
  // Without this, Leaflet reads a 0x0 container and places tiles incorrectly.
  useEffect(() => {
    const t = setTimeout(() => setMapKey(1), 500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobileListOpen) return;
    const hintKey = `fricamp_hint_mapcontrols_v1_${currentUser?.uid || 'anon'}`;
    const dismissed = window.localStorage.getItem(hintKey) === '1';
    if (!dismissed) {
      const delay = Capacitor.isNativePlatform() ? 30000 : 900;
      const timer = window.setTimeout(() => setShowMapControlsHint(true), delay);
      return () => window.clearTimeout(timer);
    }
  }, [currentUser?.uid, isMobileListOpen]);

  const dismissMapControlsHint = () => {
    const hintKey = `fricamp_hint_mapcontrols_v1_${currentUser?.uid || 'anon'}`;
    window.localStorage.setItem(hintKey, '1');
    setShowMapControlsHint(false);
  };

  useEffect(() => {
    if (!showMapControlsHint) return;
    const timer = window.setTimeout(() => dismissMapControlsHint(), 3000);
    return () => window.clearTimeout(timer);
  }, [showMapControlsHint]);

  useEffect(() => {
    if (!isPaidUser) return;
    if (zoom < 18) return;
    if (layers.satellite) return;
    onUpdateLayers({ ...layers, satellite: true });
  }, [zoom, layers, isPaidUser, onUpdateLayers]);

  // During active navigation, disable heavy optional layers for performance
  const navLayers = useMemo(() => {
    if (!isFollowingRoute) return layers;
    return {
      ...layers,
      wildlife: false,
      fishing: false,
      propertyBoundaries: false,
      precipitationRadar: false,
      webcams: false,
      bathingPlaces: false,
      wasteStations: false,
      parkingDk: false,
    };
  }, [layers, isFollowingRoute]);

  const routePlanGeometry = useMemo(() => {
    if (!isRoutePlanning || !routePlanAlternatives?.length || routePlanSelectedIndex === null) return null;
    return routePlanAlternatives[routePlanSelectedIndex]?.fullPolyline || null;
  }, [isRoutePlanning, routePlanAlternatives, routePlanSelectedIndex]);

  // Use navLayers in place of layers for data fetching hooks below
  const hasRouteContext = !!activeRouteGeometry || !!routePlanGeometry?.length || !!highlightedGeoJson || (!!draftRouteGeoJson && draftRouteGeoJson.length > 0);
  const wildlifeRouteGeometry = useMemo(() => {
    const coords: Coordinates[] = [];
    if (activeRouteGeometry?.length) coords.push(...activeRouteGeometry);
    if (activeRoutePath?.length) coords.push(...activeRoutePath);
    if (routePlanGeometry?.length) coords.push(...routePlanGeometry);
    return coords.length > 1 ? coords : null;
  }, [activeRouteGeometry, activeRoutePath, routePlanGeometry]);
  const viewportCountry = useViewportCountry(currentViewBounds);
  const toast = useToast();
  const { gatedGetCurrentPosition } = useLocationGate();

  // Vehicle warning for forest/tractor road layers
  const vehicleWarningLayers = useMemo(() => {
    const set = new Set<string>();
    const eq = currentUser?.activeVehicleEquipment;
    const cat = currentUser?.activeVehicleCategory;
    const hasTow = currentUser?.activeVehicleHasTow;
    if ((eq && !eq.four_wheel_drive) || cat === 'caravan' || cat === 'camper_trailer' || hasTow) {
      set.add('forestRoads');
      set.add('tractorRoads');
      set.add('serviceRoads');
    }
    return set;
  }, [currentUser?.activeVehicleEquipment, currentUser?.activeVehicleCategory, currentUser?.activeVehicleHasTow]);

  const handleVehicleLayerWarning = useCallback((layerKey: string) => {
    const cat = currentUser?.activeVehicleCategory;
    const hasTow = currentUser?.activeVehicleHasTow;
    if (cat === 'caravan' || cat === 'camper_trailer' || hasTow) {
      toast(t('layer_warning.unsuitable_tow'), 'warning');
    } else {
      toast(t('layer_warning.unsuitable_no_awd'), 'warning');
    }
  }, [currentUser?.activeVehicleCategory, currentUser?.activeVehicleHasTow, toast, t]);

  const { fireDanger } = useFireDanger(center, zoom, viewportCountry);
  const hasFireDangerAccess = isPaidUser || hasTierAccess({ tier: userTier }, 'fire_danger');
  const showFireDangerButton = (fireDanger.isInDangerZone || fireDanger.isGeneralBanActive) && hasFireDangerAccess;
  const [showFireDangerModal, setShowFireDangerModal] = useState(false);
  const [show2dSpotPreview, setShow2dSpotPreview] = useState(false);

  // Show toast when a Norway-only layer is toggled on while viewport is outside Norway
  const prevLayersRef = useRef(layers);
  useEffect(() => {
    if (isInNorway) { prevLayersRef.current = layers; return; }
    const NORWAY_ONLY_LAYERS: (keyof typeof layers)[] = [
      'hiking', 'hikingFoot', 'hikingSki', 'hikingBike', 'hikingOther', 'winterRoads',
      'propertyBoundaries', 'webcams',
      'svvVegnett', 'svvVegdekke', 'svvHoyde', 'svvTrafikk',
    ];
    for (const key of NORWAY_ONLY_LAYERS) {
      if (layers[key] && !prevLayersRef.current[key]) {
        toast(t('layer_coverage.enabled_outside_coverage'), 'info');
        break;
      }
    }
    prevLayersRef.current = layers;
  }, [layers, isInNorway, toast, t]);

  // When the Fricamp spots layer is active, other layers wait until spots have loaded first.
  const layersUnblocked = !layers.algorithmic || spotsReady;

  // Auto-aktiver POI-lag ved zoom >= DETAIL_ZOOM_THRESHOLD (deaktiveres under rute-navigering
  // for å unngå ytelsespress). Eksisterende interne debouncer i hookene (800–1500 ms) håndterer
  // query-throttling; boundsOverlapRatio-sjekken i hookene unngår re-fetch ved små panorering.
  const isDetailZoom = zoom >= DETAIL_ZOOM_THRESHOLD && !isFollowingRoute;
  const wildlifeHasAccess = isPaidUser || hasTierAccess({ tier: userTier }, 'wildlife_layer');
  const wildlifeActive = (navLayers.wildlife || !!wildlifeRouteGeometry || (isDetailZoom && wildlifeHasAccess)) && layersUnblocked;
  const forceBarriersByZoom = zoom >= 16;
  const swedenEnabled = !!(nordicEnabledCountries as any)?.sweden;
  const { barriers, isLoading: loadingBarriers, error: errorBarriers } = useRoadBarriers((navLayers.barriers || navLayers.forestRoads || navLayers.tractorRoads || navLayers.serviceRoads || forceBarriersByZoom || hasRouteContext || !!barrierHighlightSpot) && layersUnblocked, zoom, center, currentViewBounds, viewportCountry, swedenEnabled);
  // Danske parkeringsplasser (GeoDanmark). Statiske geocellefiler bak
  // Hosting-CDN-en — null Firestore-lesninger, egen kvote.
  const { parkering: dkParkering } = useDenmarkParking(
    !!navLayers.parkingDk && layersUnblocked, zoom, currentViewBounds);

  const { roads: mountainRoads, isLoading: loadingMountain, error: errorMountain } = useMountainRoads(navLayers.mountainRoads && layersUnblocked, zoom, currentViewBounds);
  const { roads: winterRoads, isLoading: loadingWinterRoads, error: errorWinterRoads } = useWinterRoads(navLayers.winterRoads && layersUnblocked, zoom, currentViewBounds);
  const trafficEventsActive = navLayers.trafficEvents && (isPaidUser || hasTierAccess({ tier: userTier }, 'traffic_events')) && layersUnblocked;
  const { events: trafficEvents } = useTrafficEvents(trafficEventsActive, zoom, center, currentViewBounds, viewportCountry);

  const swedishRoadNetworkActive = (navLayers.forestRoads || navLayers.tractorRoads) && layersUnblocked && viewportCountry === 'SE';
  const { network: swedishRoadNetwork } = useSwedishRoadNetwork(swedishRoadNetworkActive, zoom, currentViewBounds, viewportCountry);
  const { wildlife, isLoading: loadingWildlife, error: errorWildlife } = useWildlifeLayer(wildlifeActive, zoom, center, currentViewBounds, wildlifeRouteGeometry);
  const fishingHasAccess = isPaidUser || hasTierAccess({ tier: userTier }, 'fishing_layer');
  const fishingActive = (navLayers.fishing || (isDetailZoom && fishingHasAccess)) && fishingHasAccess && layersUnblocked;
  const { fishClusters, fishObs, isLoading: loadingFishing, error: errorFishing } = useFishingLayer(fishingActive, zoom, center, currentViewBounds);
  const { wastePoints, isLoading: loadingWaste, error: errorWaste } = useWasteStations((navLayers.wasteStations || isDetailZoom) && layersUnblocked, zoom, center, currentViewBounds);
  const shouldFetchBathing = (navLayers.bathingPlaces || !!activeRouteGeometry || (isDetailZoom && hasBathingAccess)) && layersUnblocked;
  const { bathingPlaces, isLoading: loadingBathing, error: errorBathing } = useBathingPlaces(shouldFetchBathing, zoom, center, currentViewBounds, viewportCountry);

  // Rough heuristic: maximum eastward longitude of Norwegian coastline+fjords per latitude band.
  // Used to hide marine legend/WMS when the viewport is clearly inland.
  const viewportMayShowSea = useMemo(() => {
    if (!currentViewBounds) return true;
    const { latMin, latMax, lonMin } = currentViewBounds;
    const getMaxCoastLon = (lat: number): number => {
      if (lat < 58) return 12;
      if (lat < 60) return 12;    // Skagerrak + Oslofjord
      if (lat < 60.5) return 11;  // Oslofjord transition
      if (lat < 62) return 8;     // Interior gap — fjords only to ~7.5°E
      if (lat < 64) return 12;    // Trondheimsfjorden
      if (lat < 66) return 15;    // Helgeland/Salten
      if (lat < 68) return 17;    // Lofoten/Vesterålen
      if (lat < 70) return 21;    // Troms
      return 31;                   // Finnmark
    };
    // Sample several latitudes across viewport to find max coast longitude
    let maxCoast = 0;
    for (let i = 0; i <= 5; i++) {
      maxCoast = Math.max(maxCoast, getMaxCoastLon(latMin + (latMax - latMin) * (i / 5)));
    }
    return lonMin < maxCoast + 0.5;
  }, [currentViewBounds]);

  const roadMeta = useMemo(() => {
    if (!highlightedGeoJson) return null;
    const length = calculateGeoJsonLength(highlightedGeoJson);
    const center = getGeoJsonLabelPoint(highlightedGeoJson);
    const name = highlightedGeoJson.features?.[0]?.properties?.name || highlightedGeoJson.features?.[0]?.properties?.ref || "Vei";

    return center ? { length: length.toFixed(1), center, name } : null;
  }, [highlightedGeoJson]);

  const shouldFetchChargers = (layers.chargers || !!activeRouteGeometry || (!!activeRoutePath && currentUser?.isEVUser)) && layersUnblocked;
  const { chargers: chargersData, isLoading: loadingChargers, error: errorChargers } = useChargingStations(shouldFetchChargers, zoom, currentViewBounds);

  // Contextual alerts for TrackingPanel (during navigation)
  const contextualAlerts = useContextualAlerts({
    active: isFollowingRoute && trackingStatus !== 'idle',
    userPosition: userPositionProp,
    chargers: chargersData,
    bathingPlaces,
    barriers,
    fireDanger: fireDanger,
  });

  useEffect(() => {
    onContextualAlertsChange?.(contextualAlerts);
  }, [contextualAlerts, onContextualAlertsChange]);

  // Tile/WMS error tracking
  const { tileErrors, reportTileError, clearError: clearTileError } = useLayerErrorTracker();

  const errorState = useMemo(() => ({
    barriers: errorBarriers,
    mountainRoads: errorMountain,
    winterRoads: errorWinterRoads,
    wildlife: errorWildlife,
    fishing: errorFishing,
    wasteStations: errorWaste,
    bathingPlaces: errorBathing,
    chargers: errorChargers,
    hiking: tileErrors['hiking'],
    propertyBoundaries: tileErrors['propertyBoundaries'],
    precipitationRadar: tileErrors['precipitationRadar'],
    hillshade: tileErrors['hillshade'],
    coastal: tileErrors['coastal'],
    ...(errorStateProp || {}),
  }), [errorBarriers, errorMountain, errorWinterRoads, errorWildlife, errorFishing, errorWaste, errorBathing, errorChargers, tileErrors, errorStateProp]);

  const activeLayerErrors = useMemo(() => {
    const LAYER_LABELS: Record<string, string> = {
      barriers: 'Bommer / Sperringer',
      mountainRoads: 'Fjellveier',
      winterRoads: 'Vinterstengte veier',
      wildlife: 'Dyreliv',
      fishing: 'Fiskemuligheter',
      wasteStations: 'Avfallshåndtering',
      bathingPlaces: 'Badeplasser',
      chargers: 'Ladestasjoner',
      hiking: 'Turstier',
      propertyBoundaries: 'Eiendomsgrenser',
      precipitationRadar: 'Nedbørsradar',
      hillshade: 'Terrengskygge',
      coastal: 'Havner & Brygger',
      restAreas: 'Rasteplasser',
      osmAmenities: 'Picnic & Gapahuk',
      commercialCampsites: 'Campingplasser',
      dumpStations: 'Tømmestasjoner',
    };
    return Object.entries(errorState)
      .filter(([key, hasErr]) => hasErr && (layers as Record<string, boolean>)[key])
      .map(([key]) => ({ key, label: LAYER_LABELS[key] || key }));
  }, [errorState, layers]);

  // WMS tile layer loading states
  const [loadingHiking, setLoadingHiking] = useState(false);
  const [loadingPropertyBoundaries, setLoadingPropertyBoundaries] = useState(false);
  useEffect(() => { if (!layers.hiking) setLoadingHiking(false); }, [layers.hiking]);
  useEffect(() => { if (!navLayers.propertyBoundaries) setLoadingPropertyBoundaries(false); }, [navLayers.propertyBoundaries]);

  // SVV Webcam layer
  const [webcams, setWebcams] = useState<SvvCamera[]>([]);
  const [loadingWebcams, setLoadingWebcams] = useState(false);
  const webcamRouteGeometry = useMemo(() => {
    if (activeRouteGeometry?.length) return activeRouteGeometry;
    if (activeRoutePath?.length) return activeRoutePath;
    return null;
  }, [activeRouteGeometry, activeRoutePath]);

  // Stabilize viewport: round to 1 decimal so tiny pans don't re-trigger Firestore queries
  const stableViewportKey = useMemo(() => {
    if (!currentViewBounds) return '';
    return `${currentViewBounds?.latMin?.toFixed(1)}_${currentViewBounds?.latMax?.toFixed(1)}_${currentViewBounds?.lonMin?.toFixed(1)}_${currentViewBounds?.lonMax?.toFixed(1)}`;
  }, [currentViewBounds]);

  useEffect(() => {
    if (!navLayers.webcams) { setWebcams([]); return; }
    const geom = webcamRouteGeometry || (currentViewBounds ? [
      { latitude: currentViewBounds.latMin, longitude: currentViewBounds.lonMin },
      { latitude: currentViewBounds.latMax, longitude: currentViewBounds.lonMax }
    ] : null);
    if (!geom || geom.length < 2) return;

    let cancelled = false;
    setLoadingWebcams(true);
    fetchCamerasNearRoute(geom, webcamRouteGeometry ? 2000 : 5000).then(cams => {
      if (!cancelled) { setWebcams(cams); setLoadingWebcams(false); }
    }).catch(() => { if (!cancelled) { setWebcams([]); setLoadingWebcams(false); } });
    return () => { cancelled = true; };
  }, [layers.webcams, webcamRouteGeometry, stableViewportKey]);

  // Dedup: finn badeplasser som allerede er promotert til CampingSpot
  const promotedBathingIds = useMemo(() => {
    const ids = new Set<string>();
    spots.forEach(s => {
      if (s.externalId && Array.isArray(s.features) && s.features.some(f => String(f).toUpperCase() === 'BADEPLASS')) {
        ids.add(s.externalId);
      }
    });
    return ids;
  }, [spots]);

  const filteredBathingPlaces = useMemo(() =>
    bathingPlaces.filter(bp => !promotedBathingIds.has(bp.id)),
    [bathingPlaces, promotedBathingIds]
  );

  const visibleLayerSpots = useMemo(() => {
    const routeContextActive = !!activeRouteGeometry || !!routePlanGeometry?.length;
    const selectedSpot = selectedSpotId ? spots.find(s => s.id === selectedSpotId) : null;
    // Diagnostikk for private waypoints — de har egne synlighetsregler og har vært
    // borte fra kartet før. Bare aktiv når det faktisk finnes noen.
    const wpcardCount = spots.filter(s => s.id.startsWith('wpcard-')).length;
    if (wpcardCount > 0) {
      console.log('[DEBUG-WPCARD] visibleLayerSpots input: total =', spots.length, '| wpcard- =', wpcardCount);
    }
    const hasBarrierFeature = (spot: CampingSpot) => {
      const f = Array.isArray(spot.features) ? spot.features.map(x => String(x).toUpperCase()) : [];
      return f.includes('BOM') || f.includes('SPERRING') || f.includes('BOMMER');
    };
    const hasAmenityFeature = (spot: CampingSpot) => {
      const f = Array.isArray(spot.features) ? spot.features.map(x => String(x).toUpperCase()) : [];
      return f.includes('PICNIC') || f.includes('GAPAHUK') || f.includes('BÅLPLASS') || f.includes('UTSIKT') || f.includes('UTKIKK') || f.includes('FJELLTOPP');
    };

    return spots.filter(spot => {
      // Always show the selected spot
      if (selectedSpotId && spot.id === selectedSpotId) {
        const upperFeatures = Array.isArray(spot.features) ? spot.features.map(f => String(f).toUpperCase()) : [];
        const isBathingSpot = upperFeatures.includes("BADEPLASS") || upperFeatures.includes("BADEPLASSER");
        if (isBathingSpot && !hasBathingAccess) return false;
        return true;
      }

      // Brukerens egne private waypoints (synthesisert fra Mitt innhold) skal
      // ALLTID vises, uavhengig av lag-toggles. De er eierens personlige data og
      // er ikke en del av lag-systemet, selv om de bruker samme rendering.
      if (spot.id.startsWith('wpcard-')) return true;

      // LAND-GATING KJØRER HER, VED TEGNING — ikke når plassene hentes inn.
      //
      // Den lå før i `filterSafeSpots` (hooks/useSpotManager.ts), altså på
      // ingest. Ved sidelast er `currentUser` null i noen hundre millisekunder
      // mens Firebase Auth svarer, så konfigurasjonen er den anonyme
      // {sweden:true, denmark:false}. Første henting ble filtrert med DEN, og de
      // danske plassene var borte for godt — ingen ny henting skjer, for
      // søkesirkelen er allerede dekket. Målt i prod 15.08.2026 med en innlogget
      // admin: kartet var tomt over Jylland.
      //
      // Her er filteret reaktivt: i det rollen løses, dukker plassene opp uten
      // at noe hentes på nytt.
      if (!isSpotInEnabledCountry(spot, nordicEnabledCountries)) return false;

      // Check if the spot is within 2km of the selected spot
      let isNearbySelected = false;
      if (selectedSpot) {
        if (getDistance(spot.location.latitude, spot.location.longitude, selectedSpot.location.latitude, selectedSpot.location.longitude) <= 2000) {
          isNearbySelected = true;
        }
      }

      // Filter by type/layer

      // Anviste autocamperpladser (Danmark) hører til bobilplass-laget, ikke
      // fricamp-laget. De har `spotType: 'algorithmic'` av historiske grunner, så
      // uten denne regelen falt de gjennom til `layers.algorithmic` nedenfor.
      // Speiler regelen for kommersielle rv_park-plasser. Se utils/autocamperSpots.ts.
      if (isAutocamperSpot(spot)) {
        if (routeContextActive) return true;
        return layers.commercialRvParks;
      }

      if (spot.spotType === 'osm_amenity') {
        const upperFeatures = Array.isArray(spot.features) ? spot.features.map(f => String(f).toUpperCase()) : [];
        const isDumpStation = upperFeatures.includes("DUMP_STATION");
        if (isDumpStation) {
          return ((layers.dumpStations || isDetailZoom) && (isPaidUser || hasTierAccess({ tier: userTier }, 'dump_stations'))) || currentUser?.needsEmptying || isNearbySelected || routeContextActive;
        }
        const isBathingSpot = upperFeatures.includes("BADEPLASS") || upperFeatures.includes("BADEPLASSER");
        if (isBathingSpot) {
          return hasBathingAccess && layers.bathingPlaces;
        }
        const isPlayground = upperFeatures.includes("LEKEPLASS");
        if (isPlayground) {
          return layers.playgrounds || isNearbySelected || routeContextActive;
        }
        // Show if layer is active, detail zoom active, or within 2km of selected spot
        return layers.osmAmenities || isDetailZoom || isNearbySelected || routeContextActive;
      }

      // Some older/imported records are stored as confirmed but should still follow the amenities layer.
      if (hasAmenityFeature(spot)) {
        return layers.osmAmenities || isDetailZoom || isNearbySelected || routeContextActive;
      }

      if (spot.spotType === 'rasteplass') {
        const upperFeatures = Array.isArray(spot.features) ? spot.features.map(f => String(f).toUpperCase()) : [];
        if (upperFeatures.includes('SNUPLASS')) return layers.algorithmic || routeContextActive;
        if (upperFeatures.includes('FRICAMP')) return true; // Alltid synlig — bekreftet som fricamp
        return layers.restAreas || routeContextActive;
      }

      if (spot.spotType === 'attraction') return true; // Always show user-created attractions
      if (spot.spotType === 'commercial_campsite') {
        if (routeContextActive) return true;
        return spot.commercialType === 'rv_park' ? layers.commercialRvParks : layers.commercialCampsites;
      }
      if (spot.spotType === 'coastal') return (layers.coastal && (isPaidUser || hasTierAccess({ tier: userTier }, 'coastal_layer'))) || routeContextActive;
      if (spot.spotType === 'algorithmic') return layers.algorithmic || routeContextActive;
      if (hasBarrierFeature(spot)) return layers.barriers || forceBarriersByZoom || routeContextActive;

      // Default to showing other types (e.g. confirmed spots)
      return true;
    });
  }, [activeRouteGeometry, currentUser?.needsEmptying, forceBarriersByZoom, hasBathingAccess, isDetailZoom, isPaidUser, layers, nordicEnabledCountries, routePlanGeometry, selectedSpotId, spots, userTier]);

  const activeRouteBbox = useMemo(() => {
    return activeRouteGeometry?.length ? getPolylineBbox(activeRouteGeometry) : undefined;
  }, [activeRouteGeometry]);

  const routePlanBbox = useMemo(() => {
    return routePlanGeometry?.length ? getPolylineBbox(routePlanGeometry) : undefined;
  }, [routePlanGeometry]);

  const routeFilteredSpots = useMemo(() => {
    const filterGeometry = activeRouteGeometry?.length ? activeRouteGeometry : routePlanGeometry;
    const filterBbox = activeRouteGeometry?.length ? activeRouteBbox : routePlanBbox;
    if (!filterGeometry?.length) return visibleLayerSpots;
    return visibleLayerSpots.filter(s => isPointNearPolyline(s.location, filterGeometry, 3000, filterBbox));
  }, [visibleLayerSpots, activeRouteGeometry, activeRouteBbox, routePlanGeometry, routePlanBbox]);

  const routeContextGeometry = useMemo(() => {
    const coords: Coordinates[] = [];
    if (activeRouteGeometry?.length) {
      coords.push(...activeRouteGeometry);
    }
    if (routePlanGeometry?.length) {
      coords.push(...routePlanGeometry);
    }

    const appendFromGeoJson = (geo: any) => {
      try {
        const features = Array.isArray(geo?.features) ? geo.features : [];
        features.forEach((f: any) => {
          const g = f?.geometry;
          if (!g) return;
          if (g.type === 'LineString' && Array.isArray(g.coordinates)) {
            g.coordinates.forEach((c: any) => {
              if (Array.isArray(c) && c.length >= 2) coords.push({ latitude: Number(c[1]), longitude: Number(c[0]) });
            });
          } else if (g.type === 'MultiLineString' && Array.isArray(g.coordinates)) {
            g.coordinates.forEach((line: any) => {
              if (!Array.isArray(line)) return;
              line.forEach((c: any) => {
                if (Array.isArray(c) && c.length >= 2) coords.push({ latitude: Number(c[1]), longitude: Number(c[0]) });
              });
            });
          }
        });
      } catch (e) {
        // ignore malformed geometry
      }
    };

    if (highlightedGeoJson) appendFromGeoJson(highlightedGeoJson);
    if (draftRouteGeoJson?.length) draftRouteGeoJson.forEach((g: any) => appendFromGeoJson(g));

    return coords.filter(c => Number.isFinite(c.latitude) && Number.isFinite(c.longitude));
  }, [activeRouteGeometry, highlightedGeoJson, draftRouteGeoJson, routePlanGeometry]);

  const routeContextBbox = useMemo(() => {
    return routeContextGeometry.length ? getPolylineBbox(routeContextGeometry) : undefined;
  }, [routeContextGeometry]);

  const routeFilteredBarriers = useMemo(() => {
    let filtered = barriers;
    // Når vei-lag er aktive men barriers-lag ikke er det, vis bare bommer på ikke-offentlige veier.
    if ((layers.forestRoads || layers.tractorRoads || layers.serviceRoads) && !layers.barriers && !forceBarriersByZoom) {
      filtered = filtered.filter(b => {
        if (!b.vegkategori || b.vegkategori === 'S' || b.vegkategori === 'P') return true;
        if (!layers.serviceRoads) return false;
        const barrierText = `${b.type || ''} ${b.name || ''} ${b.description || ''} ${b.purpose || ''}`.toLowerCase();
        return barrierText.includes('service') || barrierText.includes('adkomst') || barrierText.includes('drift');
      });
    }
    if (routeContextGeometry.length) {
      filtered = filtered.filter(b => isPointNearPolyline(b.location, routeContextGeometry, 3000, routeContextBbox));
    }
    return filtered;
  }, [barriers, routeContextGeometry, routeContextBbox, layers.forestRoads, layers.tractorRoads, layers.serviceRoads, layers.barriers, forceBarriersByZoom]);

  // Find nearest barrier when a spot's barrier warning is clicked
  const barrierHighlightLine = useMemo<{ spot: Coordinates; barrier: RoadBarrier } | null>(() => {
    if (!barrierHighlightSpot || barriers.length === 0) return null;
    const loc = barrierHighlightSpot.location;
    let nearest: RoadBarrier | null = null;
    let minDist = Infinity;
    for (const b of barriers) {
      const d = getDistance(loc.latitude, loc.longitude, b.location.latitude, b.location.longitude);
      if (d < minDist) { minDist = d; nearest = b; }
    }
    if (!nearest || minDist > 10000) return null; // Only show if within 10km
    return { spot: loc, barrier: nearest };
  }, [barrierHighlightSpot, barriers]);

  // Fetch actual road geometry when barrier highlight is active (works regardless of active layers)
  const [barrierRoadGeometry, setBarrierRoadGeometry] = useState<Coordinates[] | null>(null);
  const [barrierRoadCategory, setBarrierRoadCategory] = useState<string>('');
  useEffect(() => {
    if (!barrierHighlightSpot) { setBarrierRoadGeometry(null); setBarrierRoadCategory(''); return; }
    let cancelled = false;
    // Søk fra PLASSEN — finn nærmeste skogsbilveg/privatveg
    fetchNearbyForestRoad(barrierHighlightSpot.location).then(result => {
      if (cancelled) return;
      if (result) {
        setBarrierRoadGeometry(result.roadGeometry);
        setBarrierRoadCategory(result.vegkategoriName);
      } else {
        setBarrierRoadGeometry(null);
        setBarrierRoadCategory('');
      }
    });
    return () => { cancelled = true; };
  }, [barrierHighlightSpot]);

  // Auto-fitBounds when barrier highlight is active
  useEffect(() => {
    if (!barrierHighlightLine || !leafletMapRef.current) return;
    const { spot, barrier } = barrierHighlightLine;
    if (barrierRoadGeometry && barrierRoadGeometry.length > 0) {
      // Fit to road geometry + spot + barrier
      const allPoints: L.LatLngExpression[] = barrierRoadGeometry.map(c => [c.latitude, c.longitude] as L.LatLngTuple);
      allPoints.push([spot.latitude, spot.longitude]);
      allPoints.push([barrier.location.latitude, barrier.location.longitude]);
      if ((leafletMapRef.current as any)?._loaded) leafletMapRef.current.fitBounds(L.latLngBounds(allPoints).pad(0.3), { animate: true, duration: 0.6 });
    } else {
      const bounds = L.latLngBounds(
        [spot.latitude, spot.longitude],
        [barrier.location.latitude, barrier.location.longitude]
      );
      if ((leafletMapRef.current as any)?._loaded) leafletMapRef.current.fitBounds(bounds.pad(0.4), { animate: true, duration: 0.6 });
    }
  }, [barrierHighlightLine, barrierRoadGeometry]);

  const routeFilteredChargers = useMemo(() => {
    let sourceChargers = chargersData;
    if (routeCategoryFilters && routeCategoryFilters.size > 0) {
      if (!routeCategoryFilters.has('charger')) sourceChargers = [];
    } else if (!layers.chargers && !activeRouteGeometry && !(activeRoutePath && currentUser?.isEVUser)) {
      sourceChargers = [];
    }

    if (!activeRouteGeometry) return sourceChargers;
    return sourceChargers.filter(c => isPointNearPolyline(c.location, activeRouteGeometry, 3000, activeRouteBbox));
  }, [chargersData, activeRouteGeometry, routeCategoryFilters, layers.chargers, activeRoutePath, currentUser]);

  const routeFilteredWiki = useMemo(() => {
    if (!wikiSpots || wikiSpots.length === 0) return [];
    if (routeCategoryFilters && routeCategoryFilters.size > 0 && !routeCategoryFilters.has('attraction')) return [];

    if (!activeRouteGeometry) return wikiSpots;
    return wikiSpots.filter(w => isPointNearPolyline(w.location, activeRouteGeometry, 3000, activeRouteBbox));
  }, [wikiSpots, activeRouteGeometry, routeCategoryFilters]);

  const routeFilteredWildlife = useMemo(() => {
    if (!routeContextGeometry.length) return wildlife;
    return wildlife.filter((w) => isPointNearPolyline(w.location, routeContextGeometry, 200, routeContextBbox));
  }, [wildlife, routeContextGeometry]);

  // Follow route split: driven (muted) + remaining (vivid)
  const followRouteDriven = useMemo(() => {
    if (!followRouteCoords || followRouteNearestIndex == null || followRouteNearestIndex <= 0) return null;
    return followRouteCoords.slice(0, followRouteNearestIndex + 1).map(c => [c.latitude, c.longitude] as [number, number]);
  }, [followRouteCoords, followRouteNearestIndex]);

  const followRouteRemaining = useMemo(() => {
    if (!followRouteCoords || followRouteCoords.length < 2) return null;
    const startIdx = Math.max(0, followRouteNearestIndex ?? 0);
    return followRouteCoords.slice(startIdx).map(c => [c.latitude, c.longitude] as [number, number]);
  }, [followRouteCoords, followRouteNearestIndex]);

  const layerCounts = useMemo(() => {
    const getUpperFeatures = (s: CampingSpot) => Array.isArray(s.features) ? s.features.map(f => String(f).toUpperCase()) : [];
    const isDumpStation = (s: CampingSpot) => getUpperFeatures(s).includes('DUMP_STATION');
    const isBathingSpot = (s: CampingSpot) => { const f = getUpperFeatures(s); return f.includes('BADEPLASS') || f.includes('BADEPLASSER'); };
    const isPlayground = (s: CampingSpot) => getUpperFeatures(s).includes('LEKEPLASS');
    return {
      restAreas: spots.filter(s => s.spotType === 'rasteplass' && !(s.features || []).includes('SNUPLASS')).length,
      commercialCampsites: spots.filter(s => s.spotType === 'commercial_campsite' && s.commercialType !== 'rv_park').length,
      commercialRvParks: spots.filter(s => (s.spotType === 'commercial_campsite' && s.commercialType === 'rv_park') || isAutocamperSpot(s)).length,
      osmAmenities: spots.filter(s => s.spotType === 'osm_amenity' && !isDumpStation(s) && !isBathingSpot(s) && !isPlayground(s)).length,
      dumpStations: spots.filter(s => s.spotType === 'osm_amenity' && isDumpStation(s)).length,
      playgrounds: spots.filter(s => s.spotType === 'osm_amenity' && isPlayground(s)).length,
      coastal: spots.filter(s => s.spotType === 'coastal').length,
      barriers: routeFilteredBarriers.length + spots.filter(s => (s.features || []).map(f => String(f).toUpperCase()).includes('BOM')).length,
      chargers: routeFilteredChargers.length,
      mountainRoads: mountainRoads.length,
      winterRoads: winterRoads.length,
      wildlife: routeFilteredWildlife.length,
      fishing: fishObs.length,
      webcams: webcams.length,
      wasteStations: wastePoints.length,
      bathingPlaces: bathingPlaces.length + spots.filter(s => s.spotType === 'osm_amenity' && isBathingSpot(s)).length
    };
  }, [spots, routeFilteredBarriers, routeFilteredChargers, mountainRoads, winterRoads, routeFilteredWildlife, fishObs, webcams, wastePoints, bathingPlaces]);

  const isDeepZoom = zoom >= 19;
  const isHighResTopo = zoom >= 18;
  const isMarineZoom = zoom >= 15;
  // Zoom sperrer ikke lenger for søk. Sperren fantes fordi et søk ved lav zoom
  // pleide å bli vilkårlig dyrt — utsnittets bbox hadde ingen øvre grense. Nå er
  // søkeområdet en sirkel med hardt tak (MAX_SEARCH_RADIUS_KM = 50), så ett søk
  // koster det samme enten du står på zoom 5 eller 15. Da er det ingen grunn til å
  // svare «Zoom inn» på et trykk brukeren mente helt bokstavelig.
  const isTooZoomedOut = false;

  // Hvilke baselag-maler som er synlige nå (matcher <BaseTileLayer>-betingelsene),
  // brukt av wifi-prefetch. WMS-overlegg prefetches ikke.
  const activeBaseTemplates = React.useMemo(() => {
    if (isDeepZoom || layers.satellite) {
      return [MAP_LAYERS.SATELLITE, MAP_LAYERS.SATELLITE_LABELS];
    }
    const templates: string[] = [];
    if (isInNorway && !layers.topo) templates.push(MAP_LAYERS.NORWAY_TOPO);
    if (layers.topo || !isInNorway) templates.push(MAP_LAYERS.TOPO);
    if (layers.topo && isInNorway) templates.push(MAP_LAYERS.NORWAY_TOPO);
    if (layers.hillshade && isInNorway) templates.push(MAP_LAYERS.HILLSHADE);
    return templates;
  }, [isDeepZoom, layers.satellite, layers.topo, layers.hillshade, isInNorway]);

  // Stabil identitet: sendes som prop til ClusteredSpotMarkers (memoisert) og en
  // rekke lag-komponenter. Som inline-arrow gjorde den React.memo nytteløs, så
  // hver Map-render bygget hver markør på nytt (se map/markerIconCache.ts).
  const handleLayerInteraction = useCallback(() => {
    setClickCoords(null);
  }, []);

  // Markør-trykk. MÅ ha stabil identitet: ClusteredSpotMarkers er React.memo, og en
  // ny funksjon per render slo memoiseringen ut — da kjørte setIcon()+setLatLng() på
  // samtlige markører ved hver panorering. Se map/markerIconCache.ts.
  const handleSelectSpotFromMarker = useCallback((id: string) => {
    handleLayerInteraction();
    onSelectSpot(id);
  }, [handleLayerInteraction, onSelectSpot]);

  const handleRouteActivated = (coords: Coordinates[] | null) => {
    setActiveRouteGeometry(coords);
    if (onRouteActivated) onRouteActivated(coords);
  };

  const selectedSpot = useMemo(() => spots.find(s => s.id === selectedSpotId), [spots, selectedSpotId]);

  const activeTrail = useMemo<HikingTrail | null>(() => {
    const clicked = clickedTrailId ? clickedTrails.find(t => t.id === clickedTrailId) : undefined;
    if (clicked) return clicked;
    if (!selectedSpot?.hikingTrails || !activeHikingTrailId) return undefined;
    const trail = selectedSpot.hikingTrails.find(t => t.id === activeHikingTrailId);
    return trail || null;
  }, [selectedSpot, activeHikingTrailId, clickedTrails, clickedTrailId]);

  const activeTrailGeometry = useMemo(() => {
    return activeTrail?.geometry;
  }, [activeTrail]);

  const activeTrailCoords = useMemo(() => {
    if (!activeTrailGeometry) return null;
    return activeTrailGeometry.map(p => [p.latitude, p.longitude] as [number, number]);
  }, [activeTrailGeometry]);

  const activeTrailStart = useMemo<[number, number] | null>(() => {
    if (!activeTrailCoords || activeTrailCoords.length < 2) return null;
    return activeTrailCoords[0];
  }, [activeTrailCoords]);

  const activeTrailEnd = useMemo<[number, number] | null>(() => {
    if (!activeTrailCoords || activeTrailCoords.length < 2) return null;
    return activeTrailCoords[activeTrailCoords.length - 1];
  }, [activeTrailCoords]);

  useEffect(() => {
    if (!activeTrailIdOrCoords(clickedTrailId || activeHikingTrailId, activeTrailCoords)) {
      setShowTrailInfo(false);
      setShowTrailFlyover(false);
      setTrailFlyoverProfile(null);
    }
  }, [activeHikingTrailId, clickedTrailId, activeTrailCoords]);

  const trailDistanceKm = useMemo(() => {
    if (!activeTrail?.totalLength) return null;
    return (activeTrail.totalLength / 1000).toFixed(1);
  }, [activeTrail]);

  const activeTrailProfile = useMemo(() => {
    if (!activeTrail) return undefined;
    return trailElevationById[activeTrail.id];
  }, [activeTrail, trailElevationById]);

  const activeTrailPeak = useMemo(() => {
    if (!activeTrail) return null;
    return trailPeakById[activeTrail.id];
  }, [activeTrail, trailPeakById]);

  useEffect(() => {
    const buildTrailElevationProfile = async () => {
      if (!showTrailInfo || !activeTrail || !activeTrail.geometry || activeTrail.geometry.length < 2) return;
      if (trailElevationById[activeTrail.id]) return;

      setLoadingTrailElevationId(activeTrail.id);
      try {
        const coords = activeTrail.geometry;
        const sampleCount = Math.min(22, coords.length);
        const sampled: Coordinates[] = [];

        for (let i = 0; i < sampleCount; i++) {
          const idx = Math.round((i / (sampleCount - 1 || 1)) * (coords.length - 1));
          sampled.push(coords[idx]);
        }

        const elevations = await Promise.all(
          sampled.map(async (c) => {
            const ele = await fetchElevation({ latitude: c.latitude, longitude: c.longitude });
            return ele;
          })
        );

        const profile: ElevationPoint[] = [];
        let cumulativeKm = 0;
        let maxEle = -Infinity;
        let maxEleCoord: Coordinates | null = null;

        for (let i = 0; i < sampled.length; i++) {
          if (i > 0) {
            cumulativeKm += getDistance(
              sampled[i - 1].latitude,
              sampled[i - 1].longitude,
              sampled[i].latitude,
              sampled[i].longitude
            ) / 1000;
          }

          const ele = typeof elevations[i] === 'number' ? elevations[i]! : (i > 0 ? profile[i - 1].elevation : 0);

          if (ele > maxEle) {
            maxEle = ele;
            maxEleCoord = sampled[i];
          }

          profile.push({
            distance: Number(cumulativeKm.toFixed(2)),
            elevation: ele
          });
        }

        if (profile.length >= 2) {
          setTrailElevationById(prev => ({ ...prev, [activeTrail.id]: profile }));
          if (maxEleCoord) {
            setTrailPeakById(prev => ({ ...prev, [activeTrail.id]: { coord: maxEleCoord, ele: maxEle } }));
          }
        }
      } catch (e) {
        console.warn('Kunne ikke bygge høydeprofil for tur', e);
      } finally {
        setLoadingTrailElevationId((prev) => (prev === activeTrail.id ? null : prev));
      }
    };

    buildTrailElevationProfile();
  }, [showTrailInfo, activeTrail, trailElevationById]);

  // Build denser elevation profile (80 pts) and launch 3D flyover
  const handleTrailFlyover = useCallback(async () => {
    if (!activeTrail?.geometry || activeTrail.geometry.length < 2) return;
    setLoadingTrailFlyover(true);
    try {
      const coords = activeTrail.geometry;
      const sampleCount = Math.min(80, coords.length);
      const sampled: Coordinates[] = [];
      for (let i = 0; i < sampleCount; i++) {
        const idx = Math.round((i / (sampleCount - 1 || 1)) * (coords.length - 1));
        sampled.push(coords[idx]);
      }
      const elevations: (number | null)[] = [];
      for (let batch = 0; batch < sampled.length; batch += 10) {
        const chunk = sampled.slice(batch, batch + 10);
        const results = await Promise.all(
          chunk.map(c => fetchElevation({ latitude: c.latitude, longitude: c.longitude }))
        );
        elevations.push(...results);
      }
      const profile: ElevationPoint[] = [];
      let cumulativeKm = 0;
      for (let i = 0; i < sampled.length; i++) {
        if (i > 0) {
          cumulativeKm += getDistance(sampled[i - 1].latitude, sampled[i - 1].longitude, sampled[i].latitude, sampled[i].longitude) / 1000;
        }
        const ele = typeof elevations[i] === 'number' ? elevations[i]! : (i > 0 ? profile[i - 1].elevation : 0);
        profile.push({ distance: Number(cumulativeKm.toFixed(3)), elevation: ele });
      }
      if (profile.length >= 2) {
        setTrailFlyoverProfile(profile);
        setShowTrailFlyover(true);
      }
    } catch (e) {
      console.warn('Failed to build flyover elevation profile', e);
    } finally {
      setLoadingTrailFlyover(false);
    }
  }, [activeTrail]);

  const polylinePositions = useMemo(() => {
    if (!selectedSpot?.accessRoute || selectedSpot.accessRoute.length === 0) return null;
    return selectedSpot.accessRoute
      .filter(p => p && typeof p.latitude === 'number' && typeof p.longitude === 'number')
      .map(p => [p.latitude, p.longitude] as [number, number]);
  }, [selectedSpot]);

  const handleGoToMyLocation = () => {
    if (followUserPosition) {
      // Toggle off
      setFollowUserPosition(false);
      followUserRef.current = false;
      return;
    }
    // Toggle on — fly to current position and start following
    setFollowUserPosition(true);
    followUserRef.current = true;
    if (userPositionProp) {
      const map = mapRef.current;
      if (map) if ((map as any)._loaded) map.flyTo([userPositionProp.latitude, userPositionProp.longitude], capZoomForFree(14, isPaidUser), { duration: 1 });
    } else {
      toast('Henter posisjon…', 'info');
      gatedGetCurrentPosition(
        p => {
          const coords = { latitude: p.coords.latitude, longitude: p.coords.longitude };
          const map = mapRef.current;
          if (map) if ((map as any)._loaded) map.flyTo([coords.latitude, coords.longitude], capZoomForFree(14, isPaidUser), { duration: 1.5 });
          onGpsPositionAcquired?.(coords);
        },
        () => {
          toast('Kunne ikke hente posisjon', 'error');
          setFollowUserPosition(false);
          followUserRef.current = false;
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };

  // Auto-center when following user position — only pan if marker is near viewport edge
  useEffect(() => {
    if (!followUserPosition || !userPositionProp) return;
    const map = mapRef.current;
    if (map && (map as any)._loaded) {
      if (isNearMapEdge(map, userPositionProp.latitude, userPositionProp.longitude)) {
        map.panTo([userPositionProp.latitude, userPositionProp.longitude], { animate: true, duration: 0.5 });
      }
    }
  }, [followUserPosition, userPositionProp?.latitude, userPositionProp?.longitude]);

  // Auto-nav follow («kjøremodus»): appen følger GPS-prikken mens man kjører
  // (idle + i bevegelse). Prikken holdes midt på skjermen med en jevn, nær-
  // konstant-fart-panorering per GPS-fix → kartet «flyter» i stedet for å hoppe.
  //
  // Hvorfor dette ikke gir «masse lasting»:
  //  • Tiles: panorering på SAMME zoom laster aldri hele kartet på nytt; Leaflet
  //    beholder eksisterende tiles og henter kun nye kant-tiles. keepBuffer holder
  //    i tillegg en ring rundt skjermen forhåndslastet, så tiles er klare før de
  //    ruller inn.
  //  • Data: bounds-drevet nettverks-fetch (spots/rest/coastal/discover) strupes i
  //    App.handleMapMove mens auto-nav er aktiv, så panoreringen selv ikke trigger
  //    refetch før du har flyttet deg ~1 km.
  const autoFollowActiveRef = useRef(false);
  const autoFollowLastPanRef = useRef(0);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !(map as any)._loaded) return;
    if (!autoFollowUser || !userPositionProp) {
      autoFollowActiveRef.current = false;
      return;
    }
    const target: [number, number] = [userPositionProp.latitude, userPositionProp.longitude];
    const justActivated = !autoFollowActiveRef.current;
    autoFollowActiveRef.current = true;
    if (justActivated) {
      // Sentrer én gang ved oppstart. Snap til et kjørevennlig default-zoomnivå, men
      // bare hvis brukeren er utenfor det bandet — da unngår vi å rykke til noen som
      // allerede ligger fint (f.eks. z16). Etter dette beholder hver panorering
      // gjeldende zoom, så brukeren kan zoome fritt uten at kartet lastes på nytt.
      const cur = map.getZoom();
      const NAV_ZOOM_MIN = 12;
      const NAV_ZOOM_DEFAULT = 14;
      const NAV_ZOOM_MAX = 16;
      const navZoom = cur < NAV_ZOOM_MIN || cur > NAV_ZOOM_MAX ? NAV_ZOOM_DEFAULT : cur;
      map.setView(target, navZoom, { animate: true, duration: 0.6 });
      autoFollowLastPanRef.current = Date.now();
      return;
    }
    // Hopp over sub-meter-jitter — ingen grunn til å animere når prikken står stille.
    const c = map.getCenter();
    if (Math.abs(c.lat - target[0]) < 1e-5 && Math.abs(c.lng - target[1]) < 1e-5) return;
    // Lett struping mot animasjons-thrash hvis fixene kommer svært tett.
    if (Date.now() - autoFollowLastPanRef.current < 300) return;
    autoFollowLastPanRef.current = Date.now();
    // easeLinearity nær 0.5 gir jevnere, mer konstant fart enn default 0.25 (ease-out),
    // slik at kjedede panoreringer per fix smelter sammen til en flytende bevegelse.
    map.panTo(target, { animate: true, duration: 1.0, easeLinearity: 0.5, noMoveStart: true });
  }, [autoFollowUser, userPositionProp?.latitude, userPositionProp?.longitude]);

  // Cancel follow when user pans manually
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onMoveStart = () => {
      // Only cancel if the move wasn't triggered by our auto-center
      if (followUserRef.current) {
        followUserRef.current = false;
        return;
      }
    };
    const onDragStart = () => {
      if (followUserPosition) {
        setFollowUserPosition(false);
      }
    };
    map.on('dragstart', onDragStart);
    return () => { map.off('dragstart', onDragStart); };
  }, [followUserPosition]);

  const handleRecenterNavigation = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const pos = trackPoints.length > 0
      ? [trackPoints[trackPoints.length - 1].latitude, trackPoints[trackPoints.length - 1].longitude] as [number, number]
      : userPositionProp
        ? [userPositionProp.latitude, userPositionProp.longitude] as [number, number]
        : null;
    if (pos) {
      if ((map as any)._loaded) map.flyTo(pos, capZoomForFree(Math.max(map.getZoom(), 14), isPaidUser), { duration: 0.5 });
      setUserPannedAway(false);
    }
  }, [trackPoints, userPositionProp]);

  const handleShareCurrentView = async () => {
    setShowMapControlsHint(false);
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    const z = map.getZoom();
    const b = map.getBounds();
    const payload = [
      c.lat?.toFixed(6) || '0',
      c.lng?.toFixed(6) || '0',
      z?.toFixed(2) || '0',
      b.getSouth()?.toFixed(6) || '0',
      b.getWest()?.toFixed(6) || '0',
      b.getNorth()?.toFixed(6) || '0',
      b.getEast()?.toFixed(6) || '0'
    ].join(',');
    const trimmedContext = (shareContextText || '').trim();
    const searchParam = trimmedContext ? `&q=${encodeURIComponent(trimmedContext.slice(0, 120))}` : '';
    const origin = Capacitor.isNativePlatform() ? APP_URL : window.location.origin;
    const url = `${origin}/share/map/${encodeURIComponent(payload)}?s=${Date.now()}${searchParam}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Fricamp kartutsnitt',
          text: 'Se dette kartutsnittet i Fricamp',
          url
        });
        return;
      }
    } catch (e) {
      // User canceled share dialog or unsupported payload.
    }

    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 1800);
    } catch (e) {
      window.prompt('Kopier kartlenken:', url);
    }
  };

  const displaySpots = useMemo(() => {
    const hasAnyRouteGeometry = !!activeRouteGeometry?.length || !!routePlanGeometry?.length;
    // HER LÅ EN ZOOM-SPERRE. Under zoom 9 returnerte den bare den valgte plassen og
    // kastet alt annet — uansett hva søket nettopp hadde lastet ned. Rapportert
    // 16.08.2026: «kun noen få plasser kommer frem når man søker i et område, og de
    // vises ikke hvis man har zoomet for langt ut. Det skal de.»
    //
    // Sperren var fra før kartet klynget markører, da mange markører på lav zoom var
    // både uleselig og tregt. `ClusteredSpotMarkers` løser begge deler nå: langt ute
    // blir plassene til noen få klyngebobler med tall.
    if (hasAnyRouteGeometry) {
      return routeFilteredSpots;
    }
    // ZOOM ER IKKE LENGER ET PREMISS FOR HVA SOM VISES.
    //
    // Her lå `reduceSpotsForViewport`, som under zoom 14 kappet til 48–250
    // markører avhengig av zoom-nivå. Den kastet altså plasser vi nettopp hadde
    // betalt for å laste ned: målt 16.08.2026 ga ett søk 259 plasser inn, og ved
    // lav zoom var taket 48. Brukeren trykker «Søk i dette området», appen henter
    // hele området, og kartet viser en femtedel — uten å si fra.
    //
    // Tynningen var arvet fra før kartet klynget markører. Nå gjør
    // `ClusteredSpotMarkers` den jobben visuelt, og den gjør den bedre: en klynge
    // viser at det ER noe der, mens tynning bare fjerner det. Alt som lastes,
    // vises — som klynge når det er tett, som markør når det er glissent.
    const reduced = routeFilteredSpots;
    if (selectedSpot && !reduced.some((spot) => spot.id === selectedSpot.id)) {
      return [selectedSpot, ...reduced];
    }
    return reduced;
    // DEPS: `zoom`, `currentViewBounds` og `selectedSpotId` sto her fra da zoom-sperren
    // og `reduceSpotsForViewport` faktisk brukte dem. Etter at de ble fjernet (16.08.2026)
    // leser ikke denne memoen dem lenger — men de ble oppdatert ved HVER panorering, så
    // `displaySpots` fikk ny array-identitet hver gang og tvang en full markør-render.
    // Nå avhenger den bare av det den faktisk bruker.
  }, [activeRouteGeometry, routePlanGeometry, routeFilteredSpots, selectedSpot]);

  // Warn user when too many spots are loaded (performance impact)
  const spotOverloadWarnedRef = useRef(false);
  useEffect(() => {
    const totalSpots = spots.length;
    if (totalSpots > 600 && !spotOverloadWarnedRef.current) {
      spotOverloadWarnedRef.current = true;
      toast(t('map.too_many_markers', 'Mange kartpunkter lastet ({{count}}). Skru av noen kartlag for bedre ytelse.').replace('{{count}}', String(totalSpots)), 'warning');
    }
    // Reset warning when spots drop below threshold
    if (totalSpots < 400) spotOverloadWarnedRef.current = false;
  }, [spots.length]);

  const scanStartRef = useRef<number>(0);
  const lastRealPercentRef = useRef<number>(0);

  // Track scan start time
  useEffect(() => {
    if (scanProgress && scanStartRef.current === 0) {
      scanStartRef.current = Date.now();
      lastRealPercentRef.current = 0;
    }
    if (!scanProgress) {
      scanStartRef.current = 0;
      lastRealPercentRef.current = 0;
    }
  }, [scanProgress]);

  const scanPhaseLabel = useMemo(() => {
    if (!scanProgress) return null;
    const labels: Record<string, string> = {
      overpass: t('map.scan.phase_overpass', 'Henter kartdata'),
      processing: t('map.scan.phase_processing', 'Analyserer kandidater'),
      enriching: t('map.scan.phase_enriching', 'Verifiserer plasser'),
      saving: t('map.scan.phase_saving', 'Lagrer resultater'),
      done: t('map.scan.phase_done', 'Ferdig'),
    };
    return labels[scanProgress.phase] || scanProgress.phase;
  }, [scanProgress, t]);

  const scanPercent = useMemo(() => {
    if (!scanProgress) return 0;
    // Weight phases: overpass=20%, processing=5%, enriching=65%, saving=10%
    const weights: Record<string, [number, number]> = {
      overpass: [0, 20],
      processing: [20, 5],
      enriching: [25, 65],
      saving: [90, 10],
      done: [100, 0],
    };
    const [base, span] = weights[scanProgress.phase] || [0, 0];
    const phaseProgress = scanProgress.total > 0 ? scanProgress.current / scanProgress.total : 0;
    const pct = Math.min(100, Math.round(base + span * phaseProgress));
    lastRealPercentRef.current = pct;
    return pct;
  }, [scanProgress]);

  // Animated percent that creeps forward between real updates
  const [displayPercent, setDisplayPercent] = useState(0);
  useEffect(() => {
    if (!scanProgress) { setDisplayPercent(0); return; }
    // Jump to real percent immediately if it's ahead
    if (scanPercent > displayPercent) {
      setDisplayPercent(scanPercent);
      return;
    }
  }, [scanPercent, scanProgress]);

  // Slowly creep forward when stuck on a phase (max up to next phase boundary minus 2%)
  useEffect(() => {
    if (!scanProgress || scanProgress.phase === 'done') return;
    const phaseCeilings: Record<string, number> = {
      overpass: 18, processing: 24, enriching: 88, saving: 99,
    };
    const ceiling = phaseCeilings[scanProgress.phase] ?? 99;
    const interval = setInterval(() => {
      setDisplayPercent(prev => {
        if (prev >= ceiling || prev >= scanPercent) return prev;
        // Creep 1% every 2s, but never past the ceiling for current phase
        return Math.min(prev + 1, ceiling);
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [scanProgress?.phase, scanPercent]);

  const estimatedTimeLeft = useMemo(() => {
    if (!scanProgress || scanStartRef.current === 0 || displayPercent <= 2) return null;
    const elapsed = (Date.now() - scanStartRef.current) / 1000;
    const remaining = (elapsed / displayPercent) * (100 - displayPercent);
    if (remaining < 5) return null;
    if (remaining < 60) return `~${Math.round(remaining)} sek`;
    return `~${Math.round(remaining / 60)} min`;
  }, [scanProgress, displayPercent]);

  const anyLayerLoading = isLoading || Object.values(loadingState || {}).some(v => v) ||
    loadingBarriers || loadingChargers || loadingWaste || loadingBathing ||
    loadingMountain || loadingWinterRoads || loadingWildlife || loadingFishing ||
    loadingWebcams || loadingHiking || loadingPropertyBoundaries;

  const loadingText = useMemo(() => {
    if (scanProgress && scanPhaseLabel) {
      if (scanProgress.phase === 'enriching' && scanProgress.total > 0) {
        return `${scanPhaseLabel} (${scanProgress.current}/${scanProgress.total})`;
      }
      return scanPhaseLabel;
    }

    const activeLoads = [];
    if (isLoading) activeLoads.push(isFirstTimeLoad ? t('map.loading.spots') + ' — ' + t('map.loading.first_time_hint') : t('map.loading.spots'));
    if (loadingState?.restAreas) activeLoads.push(t('map.loading.rest_areas'));
    if (loadingState?.osmAmenities) activeLoads.push(t('map.loading.amenities'));
    if (loadingState?.commercialCampsites) activeLoads.push(t('map.loading.commercial'));
    if (loadingState?.coastal) activeLoads.push(t('map.loading.coastal'));
    if (loadingBarriers) activeLoads.push(t('map.loading.barriers'));
    if (loadingChargers) activeLoads.push(t('map.loading.chargers'));
    if (loadingWaste) activeLoads.push(t('map.loading.waste'));
    if (loadingBathing) activeLoads.push(t('map.loading.bathing'));
    if (loadingMountain) activeLoads.push(t('map.loading.mountain_roads'));
    if (loadingWinterRoads) activeLoads.push(t('map.loading.winter_roads'));
    if (loadingWildlife) activeLoads.push(t('map.loading.wildlife'));
    if (loadingFishing) activeLoads.push(t('map.loading.fishing'));
    if (loadingWebcams) activeLoads.push(t('map.loading.webcams'));
    if (loadingState?.dumpStations) activeLoads.push(t('map.loading.dump_stations'));
    if (loadingState?.routeFricamp) activeLoads.push(t('map.loading.route_spots'));
    if (loadingState?.routeRestAreas) activeLoads.push(t('map.loading.route_rest'));
    if (loadingState?.routeCampsites) activeLoads.push(t('map.loading.route_campsites'));
    if (loadingState?.routeChargers) activeLoads.push(t('map.loading.route_chargers'));
    if (loadingState?.routeWildlife) activeLoads.push(t('map.loading.route_wildlife'));
    if (loadingHiking) activeLoads.push(t('map.loading.hiking'));
    if (loadingPropertyBoundaries) activeLoads.push(t('map.loading.property'));

    if (activeLoads.length === 0) return `${t('map.fetching')}...`;

    if (activeLoads.length > 3) {
      return `${t('map.fetching')}: ${activeLoads.slice(0, 2).join(', ')} +${activeLoads.length - 2}...`;
    }
    return `${t('map.fetching')}: ${activeLoads.join(', ')}...`;
  }, [isLoading, loadingState, loadingBarriers, loadingChargers, loadingWaste, loadingBathing, loadingMountain, loadingWinterRoads, loadingWildlife, loadingFishing, loadingWebcams, loadingHiking, loadingPropertyBoundaries, scanProgress, scanPhaseLabel, t]);

  return (
    <div className="absolute inset-0 w-full h-full bg-[#f4f1ea]">
      <div
        className={
          isCoveredByModal
            ? 'hidden'
            : is3DMode
              ? 'invisible absolute inset-0 w-full h-full overflow-hidden'
              : 'absolute inset-0 w-full h-full overflow-hidden'
        }
        style={{
          transform: mapRotationDeg !== 0 ? `rotate(${mapRotationDeg}deg) scale(1.10)` : 'none',
          transformOrigin: '50% 50%',
          willChange: mapRotationDeg !== 0 ? 'transform' : 'auto',
        }}
      >
      <MapContainer
        key={mapKey}
        center={[center.latitude, center.longitude]}
        zoom={zoom}
        zoomControl={false}
        className="w-full h-full"
        style={{ height: '100%', width: '100%' }}
        maxZoom={21}
        minZoom={4}
        ref={leafletMapRef}
      >
        <ForceMapResize />
        <MapFlyHandler command={mapCommand} />

        <MapEventHandler
          onMapMove={onMapMove}
          onMapClick={(coords) => { if (coords) probeTrailInfo(coords); onMapClick(coords); }}
          onMapLongPress={onMapLongPress}
          onMapContextMenu={onMapContextMenu}
          setClickCoords={setClickCoords}
          setShowSearchButton={setShowSearchButton}
          mapRef={mapRef}
          isDrawing={drawingState === 'drawing'}
          onDrawingMapClick={onDrawingMapClick}
          isRoutePlanning={isRoutePlanning}
          onAddRouteStop={onAddRouteStop}
          captureMode={autoFollowUser && !!onDropCandidate}
          onCapture={handleCandidateCapture}
        />

        <TilePrefetcher
          enabled={TILE_CACHE_ENABLED && TILE_PREFETCH_ENABLED}
          urlTemplates={activeBaseTemplates}
          autoFollowActive={autoFollowUser}
        />

        <HotRegionWarmer
          enabled={TILE_CACHE_ENABLED && TILE_PREFETCH_ENABLED}
          urlTemplates={activeBaseTemplates}
        />

        <FollowRouteAutoCenter trackPoints={trackPoints} isFollowingRoute={isFollowingRoute} userPosition={userPositionProp} onUserPannedChange={setUserPannedAway} />
        <FitFollowRoute routeCoords={followRouteCoords} isFollowingRoute={isFollowingRoute} />
        <FitActiveTrail geometry={activeTrailGeometry} includePoint={selectedSpot?.location || null} bottomPadPx={isMobile ? Math.round(window.innerHeight * 0.46) : 60} />
        {!isMobile && <ZoomControl position="bottomright" />}

        {isDeepZoom ? (
          <>
            <BaseTileLayer url={MAP_LAYERS.SATELLITE} attribution="&copy; ESRI" maxZoom={21} maxNativeZoom={18} keepBuffer={baseKeepBuffer} updateWhenZooming={false} updateWhenIdle={true} />
            <Pane name="labels-deepzoom" style={{ zIndex: 650 }}>
              <BaseTileLayer url={MAP_LAYERS.SATELLITE_LABELS} attribution="&copy; ESRI" maxZoom={21} maxNativeZoom={18} keepBuffer={baseKeepBuffer} updateWhenZooming={false} updateWhenIdle={true} />
            </Pane>
          </>
        ) : layers.satellite ? (
          <>
            <BaseTileLayer url={MAP_LAYERS.SATELLITE} attribution="&copy; ESRI" maxZoom={21} maxNativeZoom={18} keepBuffer={baseKeepBuffer} updateWhenZooming={false} updateWhenIdle={true} />
            <Pane name="labels" style={{ zIndex: 650 }}>
              <BaseTileLayer url={MAP_LAYERS.SATELLITE_LABELS} attribution="&copy; ESRI" maxZoom={21} maxNativeZoom={18} keepBuffer={baseKeepBuffer} updateWhenZooming={false} updateWhenIdle={true} />
            </Pane>
          </>
        ) : (
          <>
            {/* Default Norway view (Terrain Map off): Kartverket as-is — muted olive basemap */}
            {isInNorway && !layers.topo && <BaseTileLayer key="kartverket-base" className="fc-basemap-muted" url={MAP_LAYERS.NORWAY_TOPO} attribution='&copy; Kartverket' maxZoom={21} maxNativeZoom={18} keepBuffer={baseKeepBuffer} updateWhenZooming={false} updateWhenIdle={true} eventHandlers={{ tileerror: () => reportTileError('topo') }} />}
            {/* Terrain Map or outside Norway: OpenTopoMap as consistent base on both sides of border */}
            {(layers.topo || !isInNorway) && <BaseTileLayer key="opentopomap" className="fc-basemap-muted" url={MAP_LAYERS.TOPO} opacity={1.0} maxZoom={21} maxNativeZoom={17} keepBuffer={baseKeepBuffer} updateWhenZooming={false} updateWhenIdle={true} attribution='&copy; OpenTopoMap' eventHandlers={{ tileerror: () => reportTileError('topo') }} />}
            {/* Terrain Map in Norway: Kartverket overlaid lightly for extra Norwegian detail */}
            {layers.topo && isInNorway && <BaseTileLayer key="kartverket-overlay" className="fc-basemap-muted" url={MAP_LAYERS.NORWAY_TOPO} opacity={0.45} attribution='&copy; Kartverket' maxZoom={21} maxNativeZoom={18} keepBuffer={baseKeepBuffer} updateWhenZooming={false} updateWhenIdle={true} eventHandlers={{ tileerror: () => reportTileError('topo') }} />}
          </>
        )}

        {layers.hillshade && isInNorway && (
          <Pane name="hillshade-pane" style={{ zIndex: 430 }}>
            <BaseTileLayer
              url={MAP_LAYERS.HILLSHADE}
              opacity={0.45}
              attribution='&copy; Kartverket'
              maxZoom={21}
              maxNativeZoom={16}
              keepBuffer={baseKeepBuffer}
              updateWhenZooming={false}
              updateWhenIdle={true}
              eventHandlers={{ tileerror: () => reportTileError('hillshade') }}
            />
          </Pane>
        )}

        {authReady && !isAdmin && nordicConfigReady && <NordicComingSoonOverlay onRequestCountry={onRequestCountry} enabledCountries={nordicEnabledCountries || undefined} />}

        {isInNorway && (layers.hikingFoot || layers.hikingSki || layers.hikingBike || layers.hikingOther) && (
          <Pane name="hiking-wms" style={{ zIndex: 650 }}>
            {([
              ['hikingFoot', 'Fotrute'],
              ['hikingSki', 'Skiloype'],
              ['hikingBike', 'Sykkelrute'],
              ['hikingOther', 'AnnenRute'],
            ] as const).filter(([key]) => layers[key]).map(([key, wmsLayer]) => (
              <WMSTileLayer
                key={wmsLayer}
                url={MAP_LAYERS.GEONORGE_HIKING_WMS}
                layers={wmsLayer}
                format="image/png"
                transparent={true}
                version="1.3.0"
                opacity={0.8}
                attribution="&copy; Kartverket"
                eventHandlers={{ loading: () => setLoadingHiking(true), load: () => setLoadingHiking(false), tileerror: () => reportTileError('hiking') }}
              />
            ))}
          </Pane>
        )}

        {trailInfoPopup && (
          <Popup
            className="custom-popup"
            position={trailInfoPopup.position}
            maxWidth={300}
            eventHandlers={{ remove: () => setTrailInfoPopup(null) }}
          >
            {trailInfoPopup.loading ? (
              <div className="flex items-center gap-2 min-w-[160px] py-1">
                <Loader2 size={14} className="animate-spin text-emerald-500 flex-shrink-0" />
                <span className="text-2xs font-bold text-stone-600">{t('map.trail_info.loading', 'Henter turdetaljer…')}</span>
              </div>
            ) : (
            <div className="min-w-[210px] divide-y divide-stone-200">
              {trailInfoPopup.features.slice(0, 4).map((f, i) => {
                const typeMeta: Record<RouteType, { Icon: React.ComponentType<{ size?: number; className?: string }>; cls: string }> = {
                  foot: { Icon: Footprints, cls: 'text-red-500' },
                  ski: { Icon: Snowflake, cls: 'text-sky-500' },
                  bike: { Icon: Bike, cls: 'text-emerald-600' },
                  other: { Icon: Route, cls: 'text-violet-500' },
                };
                const { Icon: TypeIcon, cls } = typeMeta[f.routeType];
                return (
                  <div key={i} className={`${i > 0 ? 'pt-2.5' : ''} ${i < Math.min(trailInfoPopup.features.length, 4) - 1 ? 'pb-2.5' : ''}`}>
                    <div className="flex items-start gap-2">
                      <TypeIcon size={15} className={`${cls} flex-shrink-0 mt-0.5`} />
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-stone-800 leading-tight">
                          {f.name || t(`route.type.${f.routeType}`)}
                        </p>
                        <p className="text-2xs text-stone-500 mt-0.5">
                          {t(`route.type.${f.routeType}`)}
                          {f.specialType ? ` · ${f.specialType}` : ''}
                        </p>
                      </div>
                    </div>
                    {(f.marking || f.grading) && (
                      <div className="flex flex-wrap gap-1 mt-1.5 ml-[23px]">
                        {f.marking && (
                          <span className="text-3xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                            {f.marking}
                          </span>
                        )}
                        {f.grading && (
                          <span className="text-3xs font-bold text-stone-600 bg-stone-100 border border-stone-200 px-1.5 py-0.5 rounded-full">
                            {f.grading}
                          </span>
                        )}
                      </div>
                    )}
                    {f.maintainer && (
                      <p className="text-3xs text-stone-500 mt-1.5 ml-[23px]">
                        {t('map.trail_info.maintained_by', { name: f.maintainer })}
                      </p>
                    )}
                  </div>
                );
              })}
              <p className="text-4xs text-stone-400 pt-1.5">{t('map.trail_info.source', 'Kilde: Kartverket (Turrutebasen)')}</p>
            </div>
            )}
          </Popup>
        )}

        {layers.forestRoads && isInNorway && (
          <Pane name="forestroads-wms" style={{ zIndex: 630 }}>
            <WMSTileLayer
              url={MAP_LAYERS.FOREST_ROADS_WMS}
              layers="skogsbilveg"
              format="image/png"
              transparent={true}
              version="1.3.0"
              opacity={0.8}
              attribution="&copy; Kartverket"
              eventHandlers={{ tileerror: () => reportTileError('forestRoads') }}
            />
          </Pane>
        )}
        {layers.tractorRoads && isInNorway && (
          <Pane name="tractorroads-wms" style={{ zIndex: 629 }}>
            <WMSTileLayer
              url={MAP_LAYERS.FOREST_ROADS_WMS}
              layers="traktorveg_sti"
              format="image/png"
              transparent={true}
              version="1.3.0"
              opacity={0.55}
              attribution="&copy; Kartverket"
              eventHandlers={{ tileerror: () => reportTileError('tractorRoads') }}
            />
          </Pane>
        )}

        {/* Swedish road network — Trafikverket NVDB. Render forest_road and tractor_road
            features as Polylines with same colour scheme as Norwegian Kartverket WMS. */}
        {viewportCountry === 'SE' && swedishRoadNetwork.features.length > 0 && (
          <Pane name="se-roadnetwork" style={{ zIndex: 630 }}>
            {swedishRoadNetwork.features.map((feat, idx) => {
              const cls = feat.properties.accessClass;
              if (cls !== 'forest_road' && cls !== 'tractor_road') return null;
              if (cls === 'forest_road' && !layers.forestRoads) return null;
              if (cls === 'tractor_road' && !layers.tractorRoads) return null;
              const positions = feat.geometry.coordinates.map(([lon, lat]) => [lat, lon] as [number, number]);
              const color = cls === 'forest_road' ? '#b45309' /* amber-700 */ : '#a16207' /* yellow-700 */;
              return (
                <Polyline
                  key={`se-road-${feat.properties.county}-${feat.properties.roadNumber}-${feat.properties.subNumber}-${idx}`}
                  positions={positions}
                  pathOptions={{ color, weight: 2, opacity: 0.75, dashArray: cls === 'tractor_road' ? '4 6' : undefined }}
                />
              );
            })}
          </Pane>
        )}

        {layers.svvVegnett && isInNorway && (
          <Pane name="svv-vegnett-wms" style={{ zIndex: 628 }}>
            <WMSTileLayer
              url={MAP_LAYERS.SVV_NVDB_WMS}
              layers="vegnett"
              format="image/png"
              transparent={true}
              version="1.3.0"
              opacity={0.7}
              attribution="&copy; Statens vegvesen"
              eventHandlers={{ tileerror: () => reportTileError('svvVegnett') }}
            />
          </Pane>
        )}

        {layers.svvVegdekke && isInNorway && (
          <Pane name="svv-vegdekke-wms" style={{ zIndex: 627 }}>
            <WMSTileLayer
              url={MAP_LAYERS.SVV_NVDB_WMS}
              layers="vegdekke"
              format="image/png"
              transparent={true}
              version="1.3.0"
              opacity={0.75}
              attribution="&copy; Statens vegvesen"
              eventHandlers={{ tileerror: () => reportTileError('svvVegdekke') }}
            />
          </Pane>
        )}

        {layers.svvHoyde && isInNorway && (
          <Pane name="svv-hoyde-wms" style={{ zIndex: 626 }}>
            <WMSTileLayer
              url={MAP_LAYERS.SVV_NVDB_WMS}
              layers="hoydebegrensning"
              format="image/png"
              transparent={true}
              version="1.3.0"
              opacity={0.9}
              attribution="&copy; Statens vegvesen"
              eventHandlers={{ tileerror: () => reportTileError('svvHoyde') }}
            />
          </Pane>
        )}

        {layers.svvTrafikk && isInNorway && (
          <Pane name="svv-trafikk-wms" style={{ zIndex: 625 }}>
            <WMSTileLayer
              url={MAP_LAYERS.SVV_NVDB_WMS}
              layers="trafikkmengde"
              format="image/png"
              transparent={true}
              version="1.3.0"
              opacity={0.65}
              attribution="&copy; Statens vegvesen"
              eventHandlers={{ tileerror: () => reportTileError('svvTrafikk') }}
            />
          </Pane>
        )}

        {layers.propertyBoundaries && (isPaidUser || hasTierAccess({ tier: userTier }, 'property_boundaries')) && (
          <Pane name="property-wms" style={{ zIndex: 640 }}>
            {isInNorway && (
              <WMSTileLayer
                url={MAP_LAYERS.PROPERTY_BOUNDARIES_WMS}
                layers="eiendomsgrense,teig"
                format="image/png"
                transparent={true}
                version="1.3.0"
                opacity={0.7}
                attribution="&copy; Kartverket"
                minZoom={14}
                eventHandlers={{ loading: () => setLoadingPropertyBoundaries(true), load: () => setLoadingPropertyBoundaries(false), tileerror: () => reportTileError('propertyBoundaries') }}
              />
            )}
            {viewportCountry === 'DK' && (
              <WMSTileLayer
                url="https://api.dataforsyningen.dk/cp_inspire?"
                layers="CP.CadastralBoundary"
                format="image/png"
                transparent={true}
                version="1.3.0"
                opacity={0.7}
                attribution="&copy; Klimadatastyrelsen"
                minZoom={14}
                eventHandlers={{ loading: () => setLoadingPropertyBoundaries(true), load: () => setLoadingPropertyBoundaries(false), tileerror: () => reportTileError('propertyBoundaries') }}
              />
            )}
          </Pane>
        )}

        {layers.precipitationRadar && (isPaidUser || hasTierAccess({ tier: userTier }, 'precipitation_radar')) && <PrecipitationRadarLayer />}

        {/* Sanntids-posisjoner */}
        {nearbyUsers.map(u => {
          const icon = L.divIcon({
            className: 'live-user-marker',
            html: `<div style="
              position:relative;
              width:36px;height:36px;
              border-radius:50%;
              background:${u.userPhoto ? 'transparent' : '#3b82f6'};
              border:3px solid #3b82f6;
              box-shadow:0 0 0 3px rgba(59,130,246,0.35),0 4px 12px rgba(0,0,0,0.4);
              overflow:hidden;
              display:flex;align-items:center;justify-content:center;
              color:white;font-weight:900;font-size:13px;
            ">${u.userPhoto
                ? `<img src="${u.userPhoto}" style="width:100%;height:100%;object-fit:cover;"/>`
                : `<span>${(u.userName || '?').charAt(0).toUpperCase()}</span>`
              }
            <div style="
              position:absolute;bottom:0;right:0;
              width:10px;height:10px;
              background:#22c55e;border:2px solid white;border-radius:50%;
            "></div></div>`,
            iconSize: [36, 36],
            iconAnchor: [18, 18],
          });
          return (
            <Marker key={u.userId} position={[u.latitude, u.longitude]} icon={icon} zIndexOffset={500}>
              <Popup>
                <div className="text-sm font-bold text-stone-800">{u.userName}</div>
                {u.speed !== undefined && u.speed > 0.5 && (
                  <div className="text-xs text-[var(--text-muted)]">{(u.speed * 3.6).toFixed(1)} km/t</div>
                )}
                <div className="text-2xs text-[var(--text-secondary)] mt-0.5">{t("auto.map.live_posisjon", "Live posisjon 🟢")}</div>
              </Popup>
            </Marker>
          );
        })}

        {/* GPS-posisjon — alltid synlig bilikon, grønn=fersk / rød=gammel */}
        {userPositionProp && (() => {
          const GPS_STALE_MS = 60_000; // 60 seconds
          const isFresh = gpsLastUpdated > 0 && (Date.now() - gpsLastUpdated) < GPS_STALE_MS;
          const color = isFresh ? '#22c55e' : '#ef4444';
          const pulseColor = isFresh ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)';
          const useVehicleAvatar = !!currentUser?.activeVehicleCategory;
          const isTowable = currentUser?.activeVehicleCategory === 'caravan' || currentUser?.activeVehicleCategory === 'camper_trailer';
          const showArticulated = useVehicleAvatar && isTowable && !!currentUser?.activeVehicleHasTow && zoom >= 15;

          const icon = showArticulated
            ? getTowIcon(color)
            : useVehicleAvatar
              ? getVehicleAvatarIcon(currentUser.activeVehicleCategory!, color, pulseColor, currentUser.activeVehiclePhotoUrl, currentUser.activeVehicleAiAvatarSvg, currentUser.vehicleMarkerColor)
              : getUserCarMarkerIcon(color, pulseColor);

          // Compute trailer position offset behind the tow vehicle (use heading=0 if no heading data)
          let trailerPos: [number, number] | null = null;
          if (showArticulated) {
            const TRAILER_OFFSET_M = 8;
            const heading = userHeading ?? 0;
            const headRad = (heading * Math.PI) / 180;
            const latOff = (TRAILER_OFFSET_M / 111320) * Math.cos(headRad);
            const lngOff = (TRAILER_OFFSET_M / (111320 * Math.cos(userPositionProp.latitude * Math.PI / 180))) * Math.sin(headRad);
            trailerPos = [userPositionProp.latitude - latOff, userPositionProp.longitude - lngOff];
          }

          return (
            <>
              <Marker
                position={[userPositionProp.latitude, userPositionProp.longitude]}
                icon={icon}
                pane="markerPane"
                zIndexOffset={5000}
              />
              {showArticulated && trailerPos && (
                <Marker
                  position={trailerPos}
                  icon={getTrailerIcon(currentUser!.activeVehicleCategory!, currentUser!.vehicleMarkerColor)}
                  pane="markerPane"
                  zIndexOffset={4999}
                />
              )}
            </>
          );
        })()}

        {/* Koordinat-pin fra søk */}
        {coordinatePin && (
          <Marker
            position={[coordinatePin.latitude, coordinatePin.longitude]}
            icon={targetPinIcon}
            zIndexOffset={1500}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stop(e);
              }
            }}
          >
            <Popup className="custom-popup">
              <div className="text-xs font-mono text-[var(--text-primary)]">
                {coordinatePin?.latitude?.toFixed(5)}, {coordinatePin?.longitude?.toFixed(5)}
              </div>
              <button
                className="mt-1 text-2xs text-red-400 hover:text-red-300"
                onClick={() => onClearCoordinatePin?.()}
              >
                {t('common.remove', 'Fjern')}
              </button>
            </Popup>
          </Marker>
        )}

        {/* Discover highlight marker */}
        {discoverHighlight && (
          <Marker
            position={[discoverHighlight.latitude, discoverHighlight.longitude]}
            icon={discoverHighlightIcon}
            zIndexOffset={1600}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stop(e);
                onClearHighlight?.();
              }
            }}
          />
        )}

        {/* My Content — lagrede linjer og omrader */}
        {myContentLines && myContentAreas && (
          <DrawingLayerComponent
            lines={myContentLines}
            areas={myContentAreas}
            onDelete={(id) => onDeleteContent?.(id, myContentAreas?.some(a => a.id === id) ? 'area' : 'line')}
            onEdit={(id) => onEditContent?.(id, myContentAreas?.some(a => a.id === id) ? 'area' : 'line')}
          />
        )}

        {/* My Content — waypoints fra ny samling */}
        {myContentWaypoints && myContentWaypoints.length > 0 && (
          <UserContentLayerComponent
            waypoints={myContentWaypoints}
            onDeleteWaypoint={(id) => onDeleteContent?.(id, 'waypoint')}
          />
        )}

        {/* Fokuserte waypoints — «Vis mappe på kart». Rendres alltid, også
            linkede punkter som ellers filtreres bort av UserContentLayer. */}
        {focusedWaypoints && focusedWaypoints.length > 0 && (
          <UserContentLayerComponent
            waypoints={focusedWaypoints}
            bypassFilter
            onDeleteWaypoint={(id) => onDeleteContent?.(id, 'waypoint')}
          />
        )}

        {/* Trail Photo Layer — fotoprikker langs ruter (supporter) */}
        {isPaidUser && (isFollowingRoute || activeRouteGeometry) && (
          <TrailPhotoLayer
            routeCoords={followRouteCoords || activeRouteGeometry || []}
            spots={spots}
            waypoints={myContentWaypoints}
            zoom={zoom}
          />
        )}

        {/* Aktiv tegning */}
        {drawingState === 'drawing' && drawingPoints && drawingToolType && (
          <DrawingActiveLayerComponent
            points={drawingPoints}
            toolType={drawingToolType}
          />
        )}

        {/* Redigering av eksisterende tegning */}
        {drawingState === 'editing' && drawingEditPoints && drawingEditToolType && onDrawingEditUpdatePoint && onDrawingEditInsertPoint && onDrawingEditRemovePoint && (
          <DrawingEditLayerComponent
            points={drawingEditPoints}
            toolType={drawingEditToolType}
            onUpdatePoint={(idx, lat, lon) => onDrawingEditUpdatePoint?.(idx, { latitude: lat, longitude: lon })}
            onInsertPoint={(idx, lat, lon) => onDrawingEditInsertPoint?.(idx, { latitude: lat, longitude: lon })}
            onRemovePoint={onDrawingEditRemovePoint}
          />
        )}

        <Pane name="route-pane" style={{ zIndex: 550 }}>
          <RouteLayer
            user={currentUser || null}
            onOpenAuth={onOpenAuth}
            active={layers.gpxRoutes}
            onInteraction={handleLayerInteraction}
            onAnalyzeRoute={onAnalyzeRoute}
            onLoadCachedSpots={onLoadCachedRouteSpots}
            onPeakClick={onPeakClick}
            onRouteActivated={handleRouteActivated}
            isAnalyzing={isAnalyzingRoute}
            passedRoutes={routes}
            passedActiveRouteIds={activeRouteIds}
            onSetActiveRouteIds={setActiveRouteIds}
            passedPreviewRegion={previewRegion}
            previewFilteredRouteIds={previewFilteredRouteIds}
            onRegionLoading={onRegionLoading}
            onRouteClick={onSelectRoute}
            selectedRouteId={selectedRouteIdProp}
            isFollowingRoute={isFollowingRoute}
          />
        </Pane>

        {layers.coastal && isInNorway && (isPaidUser || hasTierAccess({ tier: userTier }, 'coastal_layer')) && (
          <Pane name="coastal-wms" style={{ zIndex: 600 }}>
            <WMSTileLayer
              url="https://wms.geonorge.no/skwms1/wms.kystverket_havneanlegg?"
              layers="havneanlegg_flate,fiskerihavn_flate,havneavsnitt_flate"
              format="image/png"
              transparent={true}
              version="1.3.0"
              opacity={0.7}
              attribution="&copy; Kystverket"
              eventHandlers={{ tileerror: () => reportTileError('coastal') }}
            />
          </Pane>
        )}

        {layers.winterRoads && winterRoads.map((road) => (
          <Polyline
            key={road.id}
            positions={road.geometry.map(p => [p.latitude, p.longitude] as [number, number])}
            pathOptions={{ color: '#3b82f6', weight: 4, dashArray: '10, 10', opacity: 0.8 }}
            eventHandlers={{ click: (e) => { L.DomEvent.stop(e); handleLayerInteraction(); } }}
          >
            <Popup>
              <div className="p-2 space-y-1.5 max-w-[200px]">
                <div className="flex items-center gap-2 text-blue-500 font-black text-3xs uppercase tracking-widest">
                  <Snowflake size={12} /> {t('map.winter_road.title')}
                </div>
                <div className="text-stone-900 font-bold text-tiny leading-tight flex items-start gap-2">
                  <Calendar size={12} className="shrink-0 mt-0.5" />
                  {road.closedFrom && road.closedTo ? (
                    <span>{t('map.winter_road.closed_period', { from: road.closedFrom, to: road.closedTo })}</span>
                  ) : (
                    <span>{t('map.winter_road.closed_section')}</span>
                  )}
                </div>
                {road.description && (
                  <p className="text-3xs text-[var(--text-muted)] italic leading-relaxed border-t border-stone-100 pt-1">
                    {road.description}
                  </p>
                )}
              </div>
            </Popup>
          </Polyline>
        ))}

        {layers.mountainRoads && mountainRoads.map((road) => (
          <Polyline
            key={road.id}
            positions={road.geometry.map(p => [p.latitude, p.longitude] as [number, number])}
            pathOptions={{ color: '#6A1B9A', weight: 3, dashArray: '8, 8', opacity: 0.8 }}
            eventHandlers={{ click: (e) => { L.DomEvent.stop(e); handleLayerInteraction(); } }}
          >
            <Popup>
              <div className="p-2 space-y-1.5 max-w-[180px]">
                <div className="flex items-center gap-2 text-purple-600 font-black text-3xs uppercase tracking-widest">
                  <Mountain size={12} /> {t('map.mountain_road.title')}
                </div>
                <div className="text-stone-900 font-bold text-tiny leading-tight">{t('map.mountain_road.high_altitude')}</div>
                <div className="bg-amber-50 border border-amber-200 p-2 rounded-lg text-4xs text-amber-800 font-bold leading-relaxed">
                  {t('map.mountain_road.warning')}
                </div>
              </div>
            </Popup>
          </Polyline>
        ))}

        <ClusteredChargerMarkers
          chargers={routeFilteredChargers}
          onInteract={handleLayerInteraction}
          isMobile={isMobile}
        />

        {routeFilteredWiki && (
          <ClusteredWikiMarkers
            wikiSpots={routeFilteredWiki}
            onInteract={handleLayerInteraction}
            isMobile={isMobile}
          />
        )}

        {(layers.wildlife && wildlifeHasAccess || routeContextGeometry.length > 0 || (isDetailZoom && wildlifeHasAccess)) && (
          <ClusteredWildlifeMarkers
            wildlife={routeFilteredWildlife}
            onInteract={handleLayerInteraction}
          />
        )}

        {/* FishingScout — Ferskvann (CircleMarkers) + Saltvann (WMS) */}
        {fishingActive && fishClusters
          .filter(c => (c.scoreLevel === 1 && fishingFilters.rare) || (c.scoreLevel === 2 && fishingFilters.registered) || (c.scoreLevel === 3 && fishingFilters.good))
          .map((cluster, idx) => {
            const onlyFreshwater = !fishingFilters.spawning && !fishingFilters.protected;
            const boosted = onlyFreshwater || Object.values(fishingFilters).filter(Boolean).length <= 3;
            return (
          <CircleMarker
            key={`fish-${idx}`}
            center={[cluster.center.latitude, cluster.center.longitude]}
            radius={(boosted ? 10 : 8) + cluster.scoreLevel * 4}
            pathOptions={{
              color: cluster.color,
              fillColor: cluster.color,
              fillOpacity: boosted ? 0.55 : 0.35,
              weight: boosted ? 3 : 2,
              opacity: boosted ? 1 : 0.8,
            }}
            eventHandlers={{ click: () => handleLayerInteraction() }}
          >
            <Popup>
              <div className="p-3 space-y-2 min-w-[220px] max-w-[280px]">
                <div className="flex items-center gap-2 text-blue-600 font-black text-3xs uppercase tracking-widest">
                  <Fish size={12} /> {t('map.fishing.freshwater_title')}
                </div>
                <div className="text-stone-900 font-black text-sm">{cluster.scoreLabel}</div>
                <div className="text-2xs text-stone-600">
                  {cluster.totalObservations} {t('map.fishing.observations_suffix')}
                </div>
                {cluster.speciesBreakdown.map(sp => (
                  <div key={sp.species} className="flex justify-between text-2xs text-stone-700">
                    <span>{sp.species}</span>
                    <span className="font-bold">{sp.count}</span>
                  </div>
                ))}
                <p className="text-4xs text-[var(--text-muted)] border-t border-stone-100 pt-1.5 italic">
                  {t('map.fishing.source_freshwater')}
                </p>
              </div>
            </Popup>
          </CircleMarker>
        );
        })}

        {fishingActive && fishingFilters.spawning && viewportMayShowSea && isInNorway && (
            <Pane name="fishing-gytefelt-wms" style={{ zIndex: 585 }}>
              <WMSTileLayer
                url={MAP_LAYERS.FISKERIDIR_GYTEFELT_WMS}
                layers="gyteomraader,gytefelt_torsk_mb"
                format="image/png"
                transparent={true}
                version="1.3.0"
                opacity={fishingFilters.protected || fishingFilters.rare || fishingFilters.registered || fishingFilters.good ? 0.45 : 0.65}
                attribution="&copy; Fiskeridirektoratet"
                eventHandlers={{ tileerror: () => reportTileError('fishing') }}
              />
            </Pane>
        )}
        {fishingActive && fishingFilters.protected && viewportMayShowSea && isInNorway && (
            <Pane name="fishing-fredning-wms" style={{ zIndex: 586 }}>
              <WMSTileLayer
                url={MAP_LAYERS.FISKERIDIR_FREDNING_WMS}
                layers="0,7,8,9,11,13"
                format="image/png"
                transparent={true}
                version="1.3.0"
                opacity={fishingFilters.spawning || fishingFilters.rare || fishingFilters.registered || fishingFilters.good ? 0.45 : 0.65}
                attribution="&copy; Fiskeridirektoratet"
                eventHandlers={{ tileerror: () => reportTileError('fishing') }}
              />
            </Pane>
        )}

        {/* <CoverageLayer activeProvider={activeCoverageProvider} /> */}

        {/* Danske parkeringsplasser fra GeoDanmark. CircleMarker og ikke divIcon:
            et utsnitt kan inneholde noen hundre flater, og en sirkel er den
            billigste markøren Leaflet har. Radien følger arealet, så de store
            plassene skiller seg ut. */}
        {layers.parkingDk && dkParkering.length > 0 && dkParkering.map((p, i) => (
          <CircleMarker
            key={`dkpark-${p.lat.toFixed(5)}-${p.lon.toFixed(5)}-${i}`}
            center={[p.lat, p.lon]}
            radius={p.areaM2 >= 2000 ? 9 : p.areaM2 >= 500 ? 7 : 5}
            pathOptions={{ color: '#0ea5e9', fillColor: '#0ea5e9', fillOpacity: 0.45, weight: 2 }}
            eventHandlers={{ click: () => handleLayerInteraction() }}
          >
            <Tooltip direction="top" offset={[0, -6]}>
              {t('map.parking_dk.tooltip', 'Parkering')} · {p.areaM2.toLocaleString('no')} m²
            </Tooltip>
          </CircleMarker>
        ))}

        {(layers.wasteStations || isDetailZoom) && wastePoints.length > 0 && (
          <WasteMarkers
            wastePoints={wastePoints}
            zoom={zoom}
            currentViewBounds={currentViewBounds}
            onInteract={handleLayerInteraction}
          />
        )}

        {hasBathingAccess && (layers.bathingPlaces || isDetailZoom) && filteredBathingPlaces.length > 0 && (
          <BathingMarkers
            bathingPlaces={filteredBathingPlaces}
            wastePoints={(layers.wasteStations || isDetailZoom) ? wastePoints : []}
            zoom={zoom}
            currentViewBounds={currentViewBounds}
            onInteract={handleLayerInteraction}
            onPromote={onPromoteBathingPlace}
          />
        )}

        {layers.discoverAttractions && (isPaidUser || hasTierAccess({ tier: userTier }, 'discover_attractions')) && discoverItems && discoverItems.length > 0 && discoverItems.map(poi => (
          <Marker
            key={poi.id}
            position={[poi.lat, poi.lon]}
            icon={getDiscoverIcon(poi.category)}
            zIndexOffset={50}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stop(e);
                onDiscoverMarkerClick?.(poi.id, poi.lat, poi.lon);
              }
            }}
          >
            <Tooltip direction="top" offset={[0, -16]} className="custom-tooltip">
              <span className="text-xs font-semibold">{poi.name}</span>
            </Tooltip>
          </Marker>
        ))}

        {/* Reise-copilot: kuraterte severdigheter langs planlagt rute (utforskningsmodus).
            Kategori-ikon (strand/shopping/museum/…); trykk åpner det EKTE plasskortet
            (promotering i App → samme kort + bilde-resolver som andre lag-punkter). */}
        {routeAttractions && routeAttractions.length > 0 && routeAttractions.map((a, i) => (
          <Marker
            key={`route-attr-${a.name}-${i}`}
            position={[a.latitude, a.longitude]}
            icon={getDiscoverIcon(a.category || 'attraction')}
            zIndexOffset={60}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stop(e);
                onRouteAttractionTap?.(a);
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -16]} className="custom-tooltip">
              <span className="text-xs font-semibold">{a.name}</span>
            </Tooltip>
          </Marker>
        ))}

        {(layers.barriers || layers.forestRoads || layers.tractorRoads || layers.serviceRoads || forceBarriersByZoom || routeContextGeometry.length > 0) && (
          <ClusteredBarriers
            barriers={routeFilteredBarriers}
            zoom={zoom}
            currentViewBounds={currentViewBounds}
            onInteract={handleLayerInteraction}
            isMobile={isMobile}
          />
        )}

        {trafficEventsActive && trafficEvents.length > 0 && trafficEvents.map(ev => {
          const colorMap = {
            accident: '#dc2626',
            closure: '#dc2626',
            roadworks: '#ea580c',
            warning: '#f59e0b',
          } as const;
          const iconGlyph = {
            accident: '!',
            closure: '✕',
            roadworks: '⚠',
            warning: 'i',
          } as const;
          const bg = colorMap[ev.category] || '#f59e0b';
          const glyph = iconGlyph[ev.category] || 'i';
          const icon = L.divIcon({
            className: 'traffic-event-marker',
            html: `<div style="width:22px;height:22px;background:${bg};color:#fff;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:12px;box-shadow:0 2px 4px rgba(0,0,0,0.4);line-height:1;">${glyph}</div>`,
            iconSize: [22, 22],
            iconAnchor: [11, 11],
          });
          const startLabel = ev.startTime ? new Date(ev.startTime).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : null;
          const endLabel = ev.endTime ? new Date(ev.endTime).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : null;
          return (
            <Marker
              key={`trafficEvent-${ev.id}`}
              position={[ev.location.latitude, ev.location.longitude]}
              icon={icon}
              zIndexOffset={55}
              eventHandlers={{ click: (e) => { L.DomEvent.stop(e); handleLayerInteraction(); } }}
            >
              <Popup className="custom-popup" maxWidth={320}>
                <div className="p-3 space-y-2 min-w-[240px]">
                  <div className="flex items-center gap-2 font-black text-3xs uppercase tracking-widest" style={{ color: bg }}>
                    <AlertTriangle size={12} /> {t(`map.trafficEvent.category.${ev.category}`)}
                  </div>
                  {ev.roadNumber && (
                    <div className="text-stone-900 font-black text-xs leading-tight">{ev.roadNumber}{ev.roadName ? ` – ${ev.roadName}` : ''}</div>
                  )}
                  {ev.description && (
                    <div className="text-tiny text-stone-700 leading-snug">{ev.description}</div>
                  )}
                  {ev.locationDescription && ev.locationDescription !== ev.description && (
                    <div className="text-2xs text-[var(--text-muted)] font-bold">{ev.locationDescription}</div>
                  )}
                  {(startLabel || endLabel) && (
                    <div className="text-2xs text-[var(--text-secondary)] font-bold">
                      {startLabel && <div>{t('map.trafficEvent.from')}: {startLabel}</div>}
                      {endLabel && <div>{t('map.trafficEvent.to')}: {endLabel}</div>}
                    </div>
                  )}
                  {ev.county && (
                    <div className="text-3xs text-[var(--text-muted)] font-bold uppercase">{ev.county}</div>
                  )}
                  <p className="text-4xs text-[var(--text-secondary)] font-bold">{t('map.trafficEvent.source')}</p>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {layers.webcams && (isPaidUser || hasTierAccess({ tier: userTier }, 'webcams_layer')) && webcams.length > 0 && webcams.map(cam => (
          <Marker
            key={`webcam-${cam.id}`}
            position={[cam.latitude, cam.longitude]}
            icon={webcamMarkerIcon}
            zIndexOffset={60}
            eventHandlers={{ click: (e) => { L.DomEvent.stop(e); handleLayerInteraction(); } }}
          >
            <Popup className="custom-popup" maxWidth={320}>
              <div className="p-3 space-y-2 min-w-[240px]">
                <div className="flex items-center gap-2 text-cyan-600 font-black text-3xs uppercase tracking-widest">
                  <Camera size={12} /> Webkamera
                </div>
                <div className="text-stone-900 font-black text-xs leading-tight">{cam.name}</div>
                {cam.road_name && (
                  <div className="text-2xs text-[var(--text-muted)] font-bold">{cam.road_name}</div>
                )}
                <img
                  src={cam.image_url}
                  alt={cam.name}
                  className="w-full h-36 object-cover rounded-lg border border-stone-200"
                  loading="lazy"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <p className="text-4xs text-[var(--text-secondary)] font-bold">{t("auto.map.kilde_statens_vegvesen_nlod", "Kilde: Statens vegvesen (NLOD)")}</p>
              </div>
            </Popup>
          </Marker>
        ))}

        {routeHighlightWebcams.length > 0 && routeHighlightWebcams.map((cam, index) => (
          <Marker
            key={`route-webcam-${cam.id}-${index}`}
            position={[cam.latitude, cam.longitude]}
            icon={getRouteWebcamMarkerIcon(index)}
            zIndexOffset={2200 + index}
            eventHandlers={{ click: (e) => { L.DomEvent.stop(e); handleLayerInteraction(); } }}
          >
            <Popup className="custom-popup" maxWidth={320}>
              <div className="p-3 space-y-2 min-w-[240px]">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-cyan-600 font-black text-3xs uppercase tracking-widest">
                    <Camera size={12} /> Webkamera #{index + 1}
                  </div>
                </div>
                <div className="text-stone-900 font-black text-xs leading-tight">{cam.name}</div>
                {cam.road_name && (
                  <div className="text-2xs text-[var(--text-muted)] font-bold">{cam.road_name}</div>
                )}
                <img
                  src={cam.image_url}
                  alt={cam.name}
                  className="w-full h-36 object-cover rounded-lg border border-stone-200"
                  loading="lazy"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <p className="text-4xs text-[var(--text-secondary)] font-bold">{t("auto.map.kilde_statens_vegvesen_nlod", "Kilde: Statens vegvesen (NLOD)")}</p>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Søkesirkelen: området plassene faktisk ble hentet fra. Stiplet og uten
            fyll, så den leses som en indikasjon og ikke som en klikkbar flate. */}
        {searchCircle && (
          <Circle
            center={[searchCircle.lat, searchCircle.lon]}
            radius={searchCircle.radiusKm * 1000}
            pathOptions={{ color: '#10b981', weight: 2, dashArray: '6,8', fillOpacity: 0.04, fillColor: '#10b981' }}
            interactive={false}
          />
        )}

        {selectedSpot?.location?.latitude && (selectedSpot.spotType === 'confirmed' || selectedSpot.spotType === 'algorithmic') && (
          <Circle center={[selectedSpot.location.latitude, selectedSpot.location.longitude]} radius={150} pathOptions={{ color: selectedSpot.nearestHouse < 150 ? '#ef4444' : '#f59e0b', fillOpacity: 0.05, weight: 2, dashArray: '5,10' }} interactive={false} />
        )}

        {clickCoords && !selectedSpotId && (
          <>
            <Marker
              position={clickCoords}
              icon={targetPinIcon}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stopPropagation(e);
                  setClickCoords(null);
                  onMapClick(null);
                }
              }}
            />
            <Marker
              position={clickCoords}
              icon={L.divIcon({
                className: 'close-target-btn',
                html: `<div style="background: #ef4444; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(0,0,0,0.3); border: 2px solid white; cursor: pointer; transition: transform 0.2s; transform: scale(1);" onmouseover="this.style.transform='scale(1.1)'" onmouseout="this.style.transform='scale(1)'">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </div>`,
                iconSize: [24, 24],
                iconAnchor: [-12, 38] // Offset to place it top-right of the pin
              })}
              zIndexOffset={3000}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stopPropagation(e);
                  setClickCoords(null);
                  onMapClick(null);
                }
              }}
            />
          </>
        )}

        {highlightedGeoJson && (
          <>
            <GeoJSON
              data={highlightedGeoJson}
              style={{ color: '#3b82f6', weight: 6, opacity: 0.6, lineJoin: 'round', lineCap: 'round' }}
            />
            {roadMeta && (
              <Marker position={[roadMeta.center.latitude, roadMeta.center.longitude]} icon={labelIcon} zIndexOffset={1000}>
                <Tooltip direction="top" permanent offset={[0, -10]} className="road-label-tooltip" opacity={1}>
                  <div className="flex flex-col items-center">
                    <span className="font-black text-xs uppercase tracking-wider text-blue-600">{roadMeta.name}</span>
                    <span className="text-2xs font-bold text-[var(--text-muted)]">{roadMeta.length} km</span>
                  </div>
                </Tooltip>
              </Marker>
            )}
          </>
        )}

        {draftRouteGeoJson && draftRouteGeoJson.map((geo, idx) => (
          <GeoJSON
            key={`draft-route-${idx}`}
            data={geo}
            style={{
              color: '#22c55e',
              weight: 8,
              opacity: 0.9,
              lineCap: 'round',
              lineJoin: 'round'
            }}
            interactive={false}
          />
        ))}

        <ClusteredSpotMarkers
          spots={displaySpots}
          zoom={zoom}
          onSelectSpot={handleSelectSpotFromMarker}
          selectedSpotId={selectedSpotId}
          activeAnalysisIds={activeAnalysisIds}
          isMobile={isMobile}
        />

        {selectedSpotId && selectedSpot && (
          <Marker
            position={[selectedSpot.location.latitude, selectedSpot.location.longitude]}
            icon={L.divIcon({
              className: 'close-spot-btn',
              html: `<div style="background: #ef4444; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(0,0,0,0.3); border: 2px solid white; cursor: pointer; transition: transform 0.2s; transform: scale(1);" onmouseover="this.style.transform='scale(1.1)'" onmouseout="this.style.transform='scale(1)'">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </div>`,
              iconSize: [24, 24],
              iconAnchor: [-12, 38]
            })}
            zIndexOffset={3000}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stopPropagation(e);
                onSelectSpot("");
              }
            }}
          />
        )}

        {/* Barrier highlight: road geometry (or fallback line) between spot and nearest barrier */}
        {barrierHighlightLine && (
          <>
            {barrierRoadGeometry && barrierRoadGeometry.length > 1 ? (
              <Polyline
                positions={barrierRoadGeometry.map(c => [c.latitude, c.longitude] as L.LatLngTuple)}
                pathOptions={{ color: '#d97706', weight: 5, opacity: 0.85 }}
              />
            ) : (
              <Polyline
                positions={[
                  [barrierHighlightLine.spot.latitude, barrierHighlightLine.spot.longitude],
                  [barrierHighlightLine.barrier.location.latitude, barrierHighlightLine.barrier.location.longitude]
                ]}
                pathOptions={{ color: '#ef4444', weight: 4, opacity: 0.8, dashArray: '8 6' }}
              />
            )}
            <Marker
              position={[barrierHighlightLine.barrier.location.latitude, barrierHighlightLine.barrier.location.longitude]}
              icon={L.divIcon({
                className: 'barrier-highlight-pin',
                html: `<div style="background: #ef4444; color: white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(239,68,68,0.5); border: 3px solid white; cursor: pointer; animation: pulse 2s infinite;">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                </div>`,
                iconSize: [32, 32],
                iconAnchor: [16, 16]
              })}
              zIndexOffset={4000}
            >
              <Popup>
                <div className="text-xs space-y-1">
                  <div className="font-bold text-red-500 flex items-center gap-1">
                    <span>{barrierHighlightLine.barrier.type || t('spot.details.barrier.default_type')}</span>
                  </div>
                  {barrierHighlightLine.barrier.name && <div className="text-[var(--text-secondary)]">{barrierHighlightLine.barrier.name}</div>}
                  {barrierHighlightLine.barrier.description && <div className="text-[var(--text-muted)] text-2xs">{barrierHighlightLine.barrier.description}</div>}
                  {barrierRoadCategory && <div className="text-2xs text-amber-600 font-medium">{barrierRoadCategory}</div>}
                  <div className="text-2xs text-[var(--text-muted)]">
                    {Math.round(getDistance(barrierHighlightLine.spot.latitude, barrierHighlightLine.spot.longitude, barrierHighlightLine.barrier.location.latitude, barrierHighlightLine.barrier.location.longitude))}m {t('spot.details.barrier.from_spot')}
                  </div>
                  <button
                    onClick={() => onClearBarrierHighlight?.()}
                    className="mt-1 text-3xs text-red-400 underline"
                  >
                    {t('spot.details.barrier.dismiss')}
                  </button>
                </div>
              </Popup>
            </Marker>
            <Marker
              position={[barrierHighlightLine.spot.latitude, barrierHighlightLine.spot.longitude]}
              icon={L.divIcon({
                className: 'barrier-spot-pin',
                html: `<div style="background: ${BRAND}; color: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(16,185,129,0.5); border: 3px solid white;">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3" fill="white"/></svg>
                </div>`,
                iconSize: [28, 28],
                iconAnchor: [14, 14]
              })}
              zIndexOffset={3900}
            />
          </>
        )}

        {polylinePositions && (
          <Polyline positions={polylinePositions} pathOptions={{ color: BRAND, weight: 4, opacity: 0.8, className: 'route-path-spark' }} />
        )}

        {activeTrailCoords && (
          // Own pane above the hiking WMS tiles (z 650) so the highlighted trail is
          // clearly visible on top of the route layer it was picked from.
          <Pane name="active-trail" style={{ zIndex: 655 }}>
            {/* White casing under the coloured dash for contrast against map lines */}
            <Polyline
              positions={activeTrailCoords}
              pathOptions={{ color: '#ffffff', weight: 10, opacity: 0.9, lineJoin: 'round', lineCap: 'round' }}
              interactive={false}
            />
            <Polyline
              positions={activeTrailCoords}
              pathOptions={{
                color: routeColor(activeTrail?.routeType),
                weight: 6,
                opacity: 0.9,
                dashArray: '10, 10',
                lineJoin: 'round',
                className: 'hiking-trail-path'
              }}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stop(e);
                  setShowTrailInfo(true);
                }
              }}
            />
            {/* Invisible hit-area */}
            <Polyline
              positions={activeTrailCoords}
              pathOptions={{ color: 'transparent', weight: 30, opacity: 0, className: 'leaflet-interactive' }}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stop(e);
                  setShowTrailInfo(true);
                }
              }}
            />
            {activeTrailStart && (
              <Marker position={activeTrailStart} icon={trailEndpointIcon('start')} zIndexOffset={2200}>
                <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                  {t("auto.map.start", "Start")}
                </Tooltip>
              </Marker>
            )}
            {activeTrailEnd && (
              <Marker position={activeTrailEnd} icon={trailEndpointIcon('end')} zIndexOffset={2200}>
                <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                  {t("auto.map.slutt", "Slutt")}
                </Tooltip>
              </Marker>
            )}
          </Pane>
        )}
        {activeTrailPeak && (
          <Marker
            position={[activeTrailPeak.coord.latitude, activeTrailPeak.coord.longitude]}
            icon={L.divIcon({
              className: 'peak-metric-icon',
              html: `<div style="background: white; color: #f97316; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.25); border: 2px solid #f97316;">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"/></svg>
              </div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12]
            })}
            zIndexOffset={2250}
          >
            <Tooltip direction="top" offset={[0, -12]} opacity={1}>
              <div className="text-xs font-bold text-orange-600">Høyeste punkt: {Math.round(activeTrailPeak.ele)} moh</div>
            </Tooltip>
          </Marker>
        )}
        {/* Follow-rute: kjørt (dus) + gjenstående (tydelig) — render whenever route coords available */}
        {followRouteCoords && followRouteCoords.length > 1 && (
          <Pane name="follow-route-line" style={{ zIndex: 650 }}>
            {followRouteDriven && followRouteDriven.length > 1 && (
              <Polyline positions={followRouteDriven} color="#9ca3af" weight={5} opacity={0.4} dashArray="8 6" />
            )}
            {followRouteRemaining && followRouteRemaining.length > 1 && (
              <Polyline positions={followRouteRemaining} color={BRAND} weight={5} opacity={0.9} />
            )}
          </Pane>
        )}
        {/* Live turlogg-spor — skjul under rutenavigasjon */}
        {trackPoints.length > 1 && !isFollowingRoute && (
          <Pane name="track-recording" style={{ zIndex: 700 }}>
            <Polyline
              positions={trackPoints.map(p => [p.latitude, p.longitude] as [number, number])}
              color="#ef4444"
              weight={4}
              opacity={0.9}
            />
          </Pane>
        )}
        {/* Bilikon — alltid synlig under rutenavigasjon */}
        {isFollowingRoute && (() => {
          const hasGps = trackPoints.length > 0;
          const pos = hasGps
            ? { lat: trackPoints[trackPoints.length - 1].latitude, lon: trackPoints[trackPoints.length - 1].longitude }
            : userPositionProp
              ? { lat: userPositionProp.latitude, lon: userPositionProp.longitude }
              : null;
          if (!pos) return null;
          const heading = hasGps ? trackPoints[trackPoints.length - 1].heading : null;
          const rotation = heading != null && !isNaN(heading) ? Math.round(heading) : 0;
          const carColor = hasGps ? BRAND : '#ef4444';
          const glassColor = hasGps ? '#6ee7b7' : '#fca5a5';
          const wheelColor = hasGps ? '#064e3b' : '#7f1d1d';
          return (
            <Marker
              position={[pos.lat, pos.lon]}
              icon={L.divIcon({
                className: 'follow-car-icon',
                html: `<div style="transform: rotate(${rotation}deg); width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; filter: drop-shadow(0 2px 6px rgba(0,0,0,0.35));">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <rect x="5" y="2" width="14" height="20" rx="3" fill="${carColor}" stroke="white" stroke-width="1.5"/>
                    <rect x="7" y="4" width="10" height="6" rx="1.5" fill="${glassColor}"/>
                    <circle cx="8" cy="18" r="1.5" fill="${wheelColor}"/>
                    <circle cx="16" cy="18" r="1.5" fill="${wheelColor}"/>
                    <rect x="7" y="12" width="4" height="2" rx="0.5" fill="${glassColor}" opacity="0.7"/>
                    <rect x="13" y="12" width="4" height="2" rx="0.5" fill="${glassColor}" opacity="0.7"/>
                  </svg>
                </div>`,
                iconSize: [36, 36],
                iconAnchor: [18, 18],
              })}
              zIndexOffset={3000}
            />
          );
        })()}
        {trackPoints.length > 0 && !isFollowingRoute && (() => {
          const lastPt = trackPoints[trackPoints.length - 1];
          const heading = lastPt.heading;
          const rotation = heading != null && !isNaN(heading) ? Math.round(heading) : 0;
          const showArrow = heading != null && !isNaN(heading);
          if (showArrow) {
            return (
              <Marker
                position={[lastPt.latitude, lastPt.longitude]}
                icon={L.divIcon({
                  className: 'follow-route-arrow',
                  html: `<div style="transform: rotate(${rotation}deg); width: 28px; height: 28px; display: flex; align-items: center; justify-content: center;">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="#3b82f6" stroke="white" stroke-width="1.5">
                      <path d="M12 2 L20 20 L12 16 L4 20 Z"/>
                    </svg>
                  </div>`,
                  iconSize: [28, 28],
                  iconAnchor: [14, 14],
                })}
                zIndexOffset={3000}
              />
            );
          }
          return (
            <CircleMarker
              center={[lastPt.latitude, lastPt.longitude]}
              radius={9}
              fillColor="#ef4444"
              color="white"
              fillOpacity={1}
              weight={2.5}
            />
          );
        })()}
        {isFollowingRoute && nearestRoutePoint && (
          <Marker
            position={[nearestRoutePoint.latitude, nearestRoutePoint.longitude]}
            icon={followNearestPointIcon}
            zIndexOffset={2900}
            interactive={false}
          />
        )}
        {isFollowingRoute && activeFollowTarget && (
          <Marker
            key={activeFollowTarget.id}
            position={[activeFollowTarget.location.latitude, activeFollowTarget.location.longitude]}
            icon={activeFollowTargetIcon}
            zIndexOffset={2950}
            interactive={false}
          />
        )}
        {/* Ruteplanlegging */}
        {routePlanAlternatives && routePlanAlternatives.length > 0 && (
          <RoutePlanPreview
            alternatives={routePlanAlternatives}
            selectedIndex={routePlanSelectedIndex ?? null}
            onSelectRoute={onRoutePlanSelectIndex}
            startCoords={routePlanStartCoords ? [routePlanStartCoords.longitude, routePlanStartCoords.latitude] : undefined}
            endCoords={routePlanEndCoords ? [routePlanEndCoords.longitude, routePlanEndCoords.latitude] : undefined}
            stopCoords={routePlanStopCoords?.map(c => [c.longitude, c.latitude]) || []}
            onRemoveStop={onRemoveRouteStop}
            onSelectStop={onSelectRouteStop}
          />
        )}

        {/* Kandidat-fangst under auto-nav: allerede lagrede kandidater som diskrete prikker */}
        {autonavCandidates.map((c) => (
          <CircleMarker
            key={`autonav-cand-${c.id}`}
            center={[c.location.latitude, c.location.longitude]}
            radius={5}
            pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#10b981', fillOpacity: 0.9 }}
          />
        ))}

        {/* Efemer «Fricamp?»-boble — trykk for å lagre kandidat */}
        {pendingCandidate && (
          <Marker
            position={[pendingCandidate.latitude, pendingCandidate.longitude]}
            icon={candidateBubbleIcon}
            eventHandlers={{ click: () => confirmCandidate() }}
            zIndexOffset={2000}
          />
        )}

      </MapContainer>
      </div>

      {activeLayerErrors.length > 0 && (
        <ExternalSourceErrorBadge errors={activeLayerErrors} />
      )}

      {/* Combined terrain + fishing legend — default collapsed */}
      {(() => {
        const activeRoute = routes?.find(r => activeRouteIds?.has(r.id) && r.terrainTypes && r.terrainTypes.length > 1);
        const hasTerrainLegend = !!activeRoute?.terrainTypes;
        if (!hasTerrainLegend && !fishingActive) return null;
        // Hide while the mobile list/card view covers the map — there's no map to
        // explain, and the legend would otherwise sit on top of the spot card.
        if (isMobileListOpen) return null;
        return (
          <div className="absolute left-2 sm:left-4 sm:bottom-4 z-[2500] bg-stone-900/90 backdrop-blur-sm rounded-xl border border-stone-700/50 shadow-lg max-w-[190px] select-none" style={{ top: isMobile ? 'calc(var(--safe-area-top, 0px) + 80px)' : undefined, bottom: isMobile ? undefined : '16px', left: !isMobile && sidebarWidthPx ? `${sidebarWidthPx + 16}px` : undefined }}>
            {/* Toggle header */}
            <button
              onClick={() => setMapLegendOpen(prev => !prev)}
              className="flex items-center justify-between w-full px-3 py-2 gap-2"
            >
              <div className="flex items-center gap-1.5">
                {hasTerrainLegend && <Mountain size={10} className="text-emerald-400" />}
                {fishingActive && <Fish size={10} className="text-blue-400" />}
                <span className="text-4xs font-black uppercase tracking-widest text-stone-400">{t('map.legend')}</span>
              </div>
              <ChevronDown size={10} className={`text-stone-500 transition-transform ${mapLegendOpen ? 'rotate-180' : ''}`} />
            </button>

            {mapLegendOpen && (
              <>
                {/* Terrain section */}
                {hasTerrainLegend && (
                  <div className="px-3 pb-2">
                    <div className="text-4xs font-black uppercase tracking-widest text-stone-400 mb-1.5">{t("auto.map.terreng", "Terreng")}</div>
                    <div className="flex flex-col gap-1">
                      {activeRoute!.terrainTypes!.map(t => {
                        const m = TERRAIN_META[t];
                        if (!m) return null;
                        return (
                          <div key={t} className="flex items-center gap-2">
                            <span className="w-5 h-1.5 rounded-full" style={{ backgroundColor: m.color, opacity: 0.7 }} />
                            <span className="text-3xs text-stone-300 font-bold">{m.emoji} {m.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Fishing section */}
                {fishingActive && (
                  <>
                    {hasTerrainLegend && <div className="border-t border-stone-700/50" />}
                    <div className="flex flex-col gap-0.5 px-3 py-2">
                      {hasTerrainLegend && (
                        <div className="flex items-center gap-1.5 mb-1">
                          <Fish size={10} className="text-blue-400" />
                          <span className="text-4xs font-black uppercase tracking-widest text-stone-400">{t('map.fishing.title')}</span>
                        </div>
                      )}
                      <div className="text-[7px] font-bold uppercase tracking-wider text-stone-500 mt-0.5">{t('map.fishing.freshwater_title')}</div>
                      {([
                        { key: 'rare' as const, color: '#93c5fd', size: 'w-3 h-3', label: t('map.fishing.score_rare') },
                        { key: 'registered' as const, color: '#3b82f6', size: 'w-3.5 h-3.5', label: t('map.fishing.score_registered') },
                        { key: 'good' as const, color: '#1e3a5f', size: 'w-4 h-4', label: t('map.fishing.score_good') },
                      ]).map(item => (
                        <button
                          key={item.key}
                          onClick={() => setFishingFilters(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                          className={`flex items-center gap-2 py-0.5 px-1 -mx-1 rounded-md transition-all hover:bg-white/10 ${fishingFilters[item.key] ? '' : 'opacity-30'}`}
                        >
                          <span className={`${item.size} rounded-full shrink-0`} style={{ backgroundColor: item.color, opacity: fishingFilters[item.key] ? 0.9 : 0.3, border: `1.5px solid ${item.color}` }} />
                          <span className={`text-3xs font-medium ${fishingFilters[item.key] ? 'text-stone-300' : 'text-stone-500 line-through'}`}>{item.label}</span>
                        </button>
                      ))}
                      {viewportMayShowSea && (<>
                      <div className="text-[7px] font-bold uppercase tracking-wider text-stone-500 mt-1">{t('map.fishing.source_marine')}</div>
                      <button
                        onClick={() => setFishingFilters(prev => ({ ...prev, spawning: !prev.spawning }))}
                        className={`flex items-center gap-2 py-0.5 px-1 -mx-1 rounded-md transition-all hover:bg-white/10 ${fishingFilters.spawning ? '' : 'opacity-30'}`}
                      >
                        <span className="w-4 h-2.5 rounded-sm shrink-0 overflow-hidden flex" style={{ opacity: fishingFilters.spawning ? 1 : 0.2 }}>
                          <span className="flex-1" style={{ backgroundColor: '#e74c3c' }} />
                          <span className="flex-1" style={{ backgroundColor: '#4ade80' }} />
                          <span className="flex-1" style={{ backgroundColor: '#f59e0b' }} />
                        </span>
                        <span className={`text-3xs font-medium ${fishingFilters.spawning ? 'text-stone-300' : 'text-stone-500 line-through'}`}>{t('map.fishing.spawning_title')}</span>
                      </button>
                      <button
                        onClick={() => setFishingFilters(prev => ({ ...prev, protected: !prev.protected }))}
                        className={`flex items-center gap-2 py-0.5 px-1 -mx-1 rounded-md transition-all hover:bg-white/10 ${fishingFilters.protected ? '' : 'opacity-30'}`}
                      >
                        <span className="w-4 h-2.5 rounded-sm shrink-0 overflow-hidden flex" style={{ opacity: fishingFilters.protected ? 1 : 0.2 }}>
                          <span className="flex-1" style={{ backgroundColor: '#c0392b' }} />
                          <span className="flex-1" style={{ backgroundColor: '#e67e22' }} />
                          <span className="flex-1" style={{ backgroundColor: '#8e44ad' }} />
                        </span>
                        <span className={`text-3xs font-medium ${fishingFilters.protected ? 'text-stone-300' : 'text-stone-500 line-through'}`}>{t('map.fishing.protected_title')}</span>
                      </button>
                      </>)}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Gjensentrer-knapp under navigasjon når brukeren har panert bort */}
      {isFollowingRoute && userPannedAway && (
        <div className="absolute bottom-36 left-1/2 -translate-x-1/2 z-[3100] pointer-events-auto animate-in fade-in zoom-in-95 duration-200">
          <button
            onClick={handleRecenterNavigation}
            className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white shadow-2xl active:scale-95 transition-all"
          >
            <Crosshair size={16} />
            <span className="text-tiny font-black uppercase tracking-widest">{t("auto.map.sentrer", "Sentrer")}</span>
          </button>
        </div>
      )}

      {showTrailInfo && activeTrail && (
        <div className="absolute left-0 right-0 bottom-0 max-h-[42vh] rounded-t-3xl md:left-auto md:right-16 md:bottom-auto md:top-24 md:w-[360px] md:max-h-[calc(100vh-180px)] md:rounded-2xl z-[3200] bg-[var(--bg-base)]/95 backdrop-blur-xl border border-orange-500/30 shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 md:slide-in-from-right-4">
          <div className="md:hidden pt-2 flex justify-center flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-[var(--border-secondary)]" />
          </div>
          <div className="px-4 py-1.5 md:py-3 border-b border-[var(--border-secondary)] flex items-center justify-between flex-shrink-0">
            <div className="min-w-0">
              <p className="hidden md:block text-3xs font-black uppercase tracking-widest text-orange-400">{t('spot.details.hiking.title')}</p>
              <h3 className="text-sm font-black text-[var(--text-primary)] truncate">{activeTrail.name}</h3>
            </div>
            <div className="flex items-center gap-1">
              {!isMobile && activeTrailProfile && activeTrailProfile.length >= 2 && (
                <button
                  onClick={handleTrailFlyover}
                  disabled={loadingTrailFlyover}
                  className="p-2 rounded-lg bg-orange-500/15 text-orange-400 hover:bg-orange-500/25 hover:text-orange-300 transition-colors border border-orange-500/20 disabled:opacity-50"
                  aria-label={t('route.flyover_3d')}
                  title={t('route.flyover_3d')}
                >
                  {loadingTrailFlyover ? <Loader2 size={14} className="animate-spin" /> : <Orbit size={14} />}
                </button>
              )}
              <button
                onClick={() => { setShowTrailInfo(false); setClickedTrails([]); setClickedTrailId(null); }}
                className="p-2 rounded-lg bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)] transition-colors"
                aria-label={t('common.close')}
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="px-4 py-2 space-y-1.5 md:p-4 md:space-y-2 overflow-y-auto custom-scrollbar pb-[calc(var(--safe-area-bottom,0px)+0.5rem)] md:pb-4">

            {clickedTrails.length > 1 && (
              <div className="flex flex-wrap gap-1.5 pb-1">
                {clickedTrails.map(tr => (
                  <button
                    key={tr.id}
                    onClick={() => setClickedTrailId(tr.id)}
                    className={`px-2.5 py-1 rounded-full text-3xs font-black uppercase tracking-wider border transition-colors ${
                      tr.id === clickedTrailId
                        ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                        : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    {tr.name}
                  </button>
                ))}
              </div>
            )}
            {/* Compact one-line metadata chips so the elevation profile stays above the fold */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                clickedTrailId ? activeTrail.type : null,
                activeTrail.difficulty,
                activeTrail.symbol,
                trailDistanceKm ? `${trailDistanceKm} km` : null,
                activeTrail.estimatedTime,
                typeof activeTrail.distanceToStart === 'number' && !clickedTrailId
                  ? t('spot.details.hiking.distance_away', { count: Math.round(activeTrail.distanceToStart) })
                  : null,
              ].filter(Boolean).map((chip, i) => (
                <span key={i} className="text-3xs font-bold text-[var(--text-secondary)] bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] px-2 py-0.5 rounded-full whitespace-nowrap">
                  {chip}
                </span>
              ))}
              {activeTrail.externalUrl && (
                <a
                  href={activeTrail.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-400 hover:bg-orange-500/25 transition-colors text-3xs font-bold whitespace-nowrap"
                >
                  <ExternalLink size={10} />
                  {activeTrail.externalLabel || t('spot.details.hiking.read_more')}
                </a>
              )}
            </div>
            {activeTrail.maintainer && (
              <p className="text-3xs text-[var(--text-muted)] truncate">
                {t('map.trail_info.maintained_by', { name: activeTrail.maintainer })}
              </p>
            )}
            {loadingTrailElevationId === activeTrail.id && (
              <div className="pt-1 text-2xs text-[var(--text-muted)] font-bold uppercase tracking-wider flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" />
                {t('map.loading_elevation')}
              </div>
            )}
            {activeTrailProfile && activeTrailProfile.length >= 2 && (
              <div className="pt-1">
                <ElevationProfile profile={activeTrailProfile} />
              </div>
            )}
          </div>
        </div>
      )}

      {showTrailFlyover && activeTrail && trailFlyoverProfile && (
        <Suspense fallback={
          <div className="fixed inset-0 z-[10003] bg-[var(--bg-surface-alt)] flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={32} className="animate-spin text-orange-500" />
              <span className="text-xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
                {t('route.flyover_loading')}
              </span>
            </div>
          </div>
        }>
          <Route3DFlyover
            trailCoordinates={activeTrail.geometry.map(c => [c.longitude, c.latitude] as [number, number])}
            trailElevationProfile={trailFlyoverProfile}
            trailName={activeTrail.name}
            onClose={() => { setShowTrailFlyover(false); setTrailFlyoverProfile(null); }}
          />
        </Suspense>
      )}

      {!isMobile && (
        <div className="absolute bottom-24 right-4 z-[2500] bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-xl border border-[var(--border-secondary)] shadow-sm pointer-events-none">
          <span className="text-sm font-black text-[var(--text-muted)]">{zoom}</span>
        </div>
      )}

      {showSearchButton && !isLoading && !isMobileListOpen && !isMyContentOpen && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-[2500] animate-in slide-in-from-top-4 pointer-events-auto"
          style={{ top: isMobile ? 'calc(var(--safe-area-top, 0px) + 72px)' : `${24 + topUiOffsetPx}px` }}
        >
          <button
            onClick={() => { if (!isTooZoomedOut) { onSearchThisArea(); setShowSearchButton(false); } }}
            className={`flex items-center gap-2 md:gap-3 px-4 py-2 md:px-6 md:py-3 rounded-full shadow-2xl transition-all active:scale-95 ${isTooZoomedOut
                ? 'bg-white/95 dark:bg-stone-900/95 text-[var(--text-secondary)] border border-stone-200 dark:border-stone-700 cursor-not-allowed'
                : (isFetchingBounds || anyLayerLoading)
                  ? 'bg-amber-50 dark:bg-amber-950/80 text-amber-800 dark:text-amber-200 border border-amber-300/50'
                  : 'bg-white/95 dark:bg-stone-900/95 text-stone-900 dark:text-stone-100 border border-emerald-500/20 hover:bg-emerald-50 dark:hover:bg-stone-800 backdrop-blur-md'
              }`}
          >
            {isTooZoomedOut ? (
              <><ZoomIn size={16} /><span className="text-2xs md:text-tiny font-black uppercase tracking-widest">{t('map.zoom_in')}</span></>
            ) : (isFetchingBounds || anyLayerLoading) ? (
              <><Loader2 size={16} className="animate-spin text-amber-500" /><span className="text-2xs md:text-tiny font-black uppercase tracking-widest">{t('map.search_here')}</span></>
            ) : (
              <><RefreshCcw size={16} className="text-emerald-600 dark:text-emerald-400" /><span className="text-2xs md:text-tiny font-black uppercase tracking-widest text-emerald-900 dark:text-emerald-200">{t('map.search_here')}</span></>
            )}
          </button>
        </div>
      )}

      {anyLayerLoading && !isAnalyzingRoute && !isMobileListOpen && (
        <div
          className="absolute left-0 right-0 z-[2500] pointer-events-auto"
          style={{ top: `${topUiOffsetPx}px` }}
        >
          <div className="bg-stone-900/90 backdrop-blur-xl border-b border-emerald-500/30 text-white px-4 py-1.5 flex items-center gap-3 animate-in slide-in-from-top-2 fade-in duration-200">
            <Loader2 size={14} className="animate-spin text-emerald-500 shrink-0" />
            <span className="text-2xs font-black uppercase tracking-widest leading-none truncate flex-1 min-w-0">
              {loadingText}
            </span>
            {scanProgress && (
              <span className="text-3xs font-bold text-emerald-400 shrink-0">
                {displayPercent}%{estimatedTimeLeft ? ` · ${estimatedTimeLeft}` : ''}
              </span>
            )}
            {!scanProgress && (
              <span className="text-3xs font-bold text-[var(--text-secondary)] shrink-0">
                {t('map.updating')}
              </span>
            )}
            {onCancelLoading && (
              <button
                onClick={(e) => { e.stopPropagation(); onCancelLoading(); }}
                className="p-1 hover:bg-white/10 rounded-full text-[var(--text-secondary)] hover:text-white transition-colors shrink-0"
                title={t("auto.map.title.avbryt_lasting", "Avbryt lasting")}
              >
                <X size={12} />
              </button>
            )}
          </div>
          {scanProgress && (
            <div className="w-full h-1 bg-stone-800/50 overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all duration-1000 ease-out"
                style={{ width: `${displayPercent}%` }}
              />
            </div>
          )}
        </div>
      )}

      {isFetchingBounds && !anyLayerLoading && (
        <div className="absolute top-0 left-0 right-0 z-[2501] h-[3px] overflow-hidden pointer-events-none">
          <div className="h-full w-1/3 bg-emerald-500/70 rounded-full animate-[shimmer_1.2s_ease-in-out_infinite]"
            style={{ animation: 'shimmer 1.2s ease-in-out infinite' }} />
        </div>
      )}

      <div
        className={`absolute right-2 md:right-3 z-[3100] ${isMobileListOpen ? 'hidden md:flex' : 'flex'} flex-col items-end gap-1.5 md:gap-2 pointer-events-auto`}
        style={{ top: isNavigatingMobile ? 'calc(var(--safe-area-top, 0px) + 12px)' : shouldCollapseControls ? 'calc(var(--safe-area-top, 0px) + 96px)' : `calc(var(--safe-area-top, 0px) + ${88 + (topUiOffsetPx || 0) + 8}px)` }}
      >
        {showMapControlsHint && (
          <div className="absolute right-0 top-14 md:top-12 w-64 rounded-2xl border border-emerald-500/30 bg-stone-900/95 text-white p-3 shadow-2xl">
            <p className="text-2xs leading-relaxed">
              {t("auto.map.tips", "Tips:")} <span className="font-black">{t("auto.map.del", "Del")}</span> lager lenke til akkurat kartutsnittet du ser.
            </p>
            <div className="mt-2 flex justify-end">
              <button
                onClick={dismissMapControlsHint}
                className="px-2 py-1 rounded-lg bg-[var(--bg-surface)] hover:bg-[var(--bg-surface)] text-3xs font-black uppercase tracking-widest text-[var(--text-primary)]"
              >
                {t("auto.map.skjonner", "Skjønner")}
              </button>
            </div>
          </div>
        )}

        {/* Mobile controls (zen mode): kun 3 knapper: Posisjon, Kartlag, Verktøysheet */}
        {isMobile && !isNavigatingMobile ? (
          <div className="flex flex-col items-end gap-2">
            {/* 1. Min posisjon */}
            <button
              onClick={handleGoToMyLocation}
              className={`w-11 h-11 rounded-xl border flex items-center justify-center shadow-xl active:scale-95 transition-all pointer-events-auto ${
                followUserPosition
                  ? 'bg-drab-2 border-accent text-accent'
                  : 'bg-drab border-drab-line text-[var(--text-secondary)]'
              }`}
              title={followUserPosition ? t('map.follow_position_stop', 'Slutt å følge posisjon') : t('map.follow_position', 'Følg min posisjon')}
              aria-label={t('map.follow_position', 'Følg min posisjon')}
            >
              <Crosshair size={18} />
            </button>

            {/* 2. Kartlag */}
            <div className="pointer-events-auto">
              <MapLayersControl
                layers={layers}
                onUpdateLayers={onUpdateLayers}
                loadingState={{
                  barriers: loadingBarriers,
                  mountainRoads: loadingMountain,
                  chargers: loadingChargers,
                  winterRoads: loadingWinterRoads,
                  wildlife: loadingWildlife,
                  fishing: loadingFishing,
                  webcams: loadingWebcams,
                  wasteStations: loadingWaste,
                  bathingPlaces: loadingBathing,
                  ...(loadingState || {})
                }}
                counts={layerCounts}
                errorState={errorState}
                isPaidUser={isPaidUser}
                userTier={userTier}
                onOpenUpgrade={onOpenUpgrade}
                onOpenOfflineManager={() => setShowOfflineManager(true)}
                viewportCountry={viewportCountry}
                activeVehicleLength={currentUser?.activeVehicleLength}
                is3DMode={is3DMode}
                onToggle3D={onToggle3D}
                vehicleWarningLayers={vehicleWarningLayers}
                onVehicleWarning={handleVehicleLayerWarning}
                showLayerTip={showLayerTip}
                onDismissLayerTip={onDismissLayerTip}
              />
            </div>

            {/* 3. Verktøy / Flere handlinger bunnark */}
            <button
              onClick={() => setShowMobileToolsSheet(true)}
              className="relative w-11 h-11 bg-drab border border-drab-line rounded-xl flex items-center justify-center text-[var(--text-secondary)] shadow-xl active:scale-95 transition-all pointer-events-auto"
              title={t('map.tools_heading', 'Handlinger og verktøy')}
              aria-label={t('map.tools_heading', 'Handlinger og verktøy')}
            >
              <MoreVertical size={18} />
              {(adminBadgeCount > 0 || (showFireDangerButton && fireDanger.isInDangerZone) || trackingStatus === 'recording') && (
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-accent border-2 border-drab animate-pulse" />
              )}
            </button>
          </div>
        ) : (
          /* Desktop / Tablet / Navigation Mode */
          <>
            {shouldCollapseControls && (
              <button
                onClick={() => setMobileControlsExpanded(!mobileControlsExpanded)}
                className="w-11 h-11 bg-drab border border-drab-line rounded-xl flex items-center justify-center text-[var(--text-secondary)] shadow-xl active:scale-95 transition-all pointer-events-auto"
              >
                {mobileControlsExpanded ? <ChevronUp size={14} /> : <MoreVertical size={14} />}
              </button>
            )}

            <div className={`${shouldCollapseControls && !mobileControlsExpanded ? 'hidden' : 'flex'} flex-col items-end gap-1.5 md:gap-2`}>
              {!isNavigatingMobile && (
                <button
                  onClick={handleShareCurrentView}
                  className={`w-11 h-11 rounded-xl border flex items-center justify-center shadow-xl active:scale-95 transition-all pointer-events-auto ${
                    shareCopied
                      ? 'bg-drab-2 border-accent text-accent'
                      : 'bg-drab border-drab-line text-[var(--text-secondary)]'
                  }`}
                  title={shareCopied ? t('map.share_view_copied', 'Kartlenke kopiert') : t('map.share_view', 'Del kartutsnitt')}
                >
                  <Share2 size={18} />
                </button>
              )}
              {isFollowingRoute && (
                <button
                  onClick={() => onToggleFollowMapOrientation?.()}
                  className={`w-11 h-11 rounded-xl border flex items-center justify-center shadow-xl active:scale-95 transition-all pointer-events-auto ${
                    followMapOrientation === 'heading_up'
                      ? 'bg-drab-2 border-accent text-accent'
                      : 'bg-drab border-drab-line text-[var(--text-secondary)]'
                  }`}
                  title={followMapOrientation === 'heading_up' ? 'Kart folger kjoreretning' : 'Kart nord opp'}
                >
                  <Compass size={18} />
                </button>
              )}
              <button
                onClick={handleGoToMyLocation}
                className={`w-11 h-11 rounded-xl border flex items-center justify-center shadow-xl active:scale-95 transition-all pointer-events-auto ${
                  followUserPosition
                    ? 'bg-drab-2 border-accent text-accent'
                    : 'bg-drab border-drab-line text-[var(--text-secondary)]'
                }`}
                title={followUserPosition ? t('map.follow_position_stop', 'Slutt å følge posisjon') : t('map.follow_position', 'Følg min posisjon')}
              >
                <Crosshair size={18} />
              </button>
              {showFireDangerButton && !isNavigatingMobile && (() => {
                const HazardIcon = getHazardIcon(fireDanger.dominantHazard);
                const buttonTitleKey = fireDanger.dominantHazard && fireDanger.dominantHazard !== 'forestFire'
                  ? `fire_danger.hazard.${fireDanger.dominantHazard}`
                  : 'fire_danger.button_title';
                return (
                  <button
                    onClick={() => setShowFireDangerModal(true)}
                    className={`w-11 h-11 rounded-xl border flex items-center justify-center shadow-xl active:scale-95 transition-all pointer-events-auto ${
                      fireDanger.isInDangerZone
                        ? 'bg-danger border-danger text-white'
                        : 'bg-warn border-warn text-drab-2'
                    }`}
                    title={t(buttonTitleKey)}
                  >
                    <HazardIcon size={20} fill="currentColor" strokeWidth={2} />
                  </button>
                );
              })()}
              {onCreateFromPhoto && !isNavigatingMobile && (
                <button
                  onClick={async () => {
                    try {
                      const result = await pickPhoto();
                      if (result) onCreateFromPhoto([result.file], result.coords);
                    } catch (e) {
                      console.warn('Photo pick error:', e);
                      toast(t('toast.photo_pick_error', 'Kunne ikke laste bildet. Prøv igjen.'), 'error');
                    }
                  }}
                  className="w-11 h-11 rounded-xl border bg-drab border-drab-line text-[var(--text-secondary)] flex items-center justify-center shadow-xl active:scale-95 transition-all pointer-events-auto"
                  title={t('add_spot.from_photo', 'Opprett fra bilde')}
                >
                  <Camera size={18} />
                </button>
              )}
              <div className="pointer-events-auto">
                <MapLayersControl
                  layers={layers}
                  onUpdateLayers={onUpdateLayers}
                  loadingState={{
                    barriers: loadingBarriers,
                    mountainRoads: loadingMountain,
                    chargers: loadingChargers,
                    winterRoads: loadingWinterRoads,
                    wildlife: loadingWildlife,
                    fishing: loadingFishing,
                    webcams: loadingWebcams,
                    wasteStations: loadingWaste,
                    bathingPlaces: loadingBathing,
                    ...(loadingState || {})
                  }}
                  counts={layerCounts}
                  errorState={errorState}
                  isPaidUser={isPaidUser}
                  userTier={userTier}
                  onOpenUpgrade={onOpenUpgrade}
                  onOpenOfflineManager={() => setShowOfflineManager(true)}
                  viewportCountry={viewportCountry}
                  activeVehicleLength={currentUser?.activeVehicleLength}
                  is3DMode={is3DMode}
                  onToggle3D={onToggle3D}
                  vehicleWarningLayers={vehicleWarningLayers}
                  onVehicleWarning={handleVehicleLayerWarning}
                  showLayerTip={showLayerTip}
                  onDismissLayerTip={onDismissLayerTip}
                />
              </div>
              {/* Position sharing — visible during navigation */}
              {isNavigatingMobile && (
                <div className="relative pointer-events-auto">
                  <button
                    onClick={() => {
                      if (isSharing) {
                        onStopSharing?.();
                        setShowNavSharePicker(false);
                      } else {
                        setShowNavSharePicker(!showNavSharePicker);
                      }
                    }}
                    className={`w-11 h-11 rounded-xl border flex items-center justify-center shadow-xl active:scale-95 transition-all ${
                      isSharing
                        ? 'bg-drab-2 border-accent text-accent'
                        : showNavSharePicker
                          ? 'bg-drab-2 border-drab-line text-[var(--text-primary)]'
                          : 'bg-drab border-drab-line text-[var(--text-secondary)]'
                    }`}
                    title={isSharing ? 'Stopp posisjonsdeling' : 'Del posisjon'}
                  >
                    <Users size={18} />
                  </button>
                  {showNavSharePicker && !isSharing && (
                    <div className="absolute bottom-full right-0 mb-2 w-64 bg-white border border-stone-200 rounded-2xl shadow-2xl z-[3500] overflow-hidden animate-in slide-in-from-bottom-2">
                      <div className="px-3 py-2 border-b border-stone-100">
                        <p className="text-2xs font-black text-stone-400 uppercase tracking-widest">{t("auto.map.del_posisjon_med", "Del posisjon med")}</p>
                      </div>
                      {collaborativeMaps.length === 0 ? (
                        <div className="px-3 py-4 text-center">
                          <p className="text-tiny text-stone-400">{t("auto.map.ingen_grupper_enna", "Ingen grupper ennå")}</p>
                          <p className="text-2xs text-stone-300 mt-1">{t("auto.map.opprett_en_gruppe_via_tur_meny", "Opprett en gruppe via Tur-menyen")}</p>
                        </div>
                      ) : (
                        <div className="max-h-64 overflow-y-auto">
                          {collaborativeMaps.map(m => {
                            const isExpanded = navExpandedGroupId === m.id;
                            return (
                              <div key={m.id} className="border-b border-stone-50 last:border-b-0">
                                <div className="flex items-stretch hover:bg-stone-50 transition-colors">
                                  <button
                                    onClick={() => {
                                      onStartSharing?.(m.id, m.name);
                                      setShowNavSharePicker(false);
                                    }}
                                    className="flex-1 flex items-center gap-3 px-3 py-2.5 text-left"
                                  >
                                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                                      <Users size={14} className="text-emerald-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-tiny font-bold text-stone-700 truncate">{m.name}</p>
                                      <p className="text-3xs text-stone-400">{m.members.length} {m.members.length === 1 ? 'medlem' : 'medlemmer'}</p>
                                    </div>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setNavExpandedGroupId(isExpanded ? null : m.id);
                                    }}
                                    aria-expanded={isExpanded}
                                    aria-label={t('profile.live_sharing.toggle_members', 'Vis medlemmer')}
                                    className="px-2 flex items-center text-stone-400 hover:text-stone-600 active:bg-stone-100"
                                  >
                                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                  </button>
                                </div>
                                {isExpanded && (
                                  <div className="px-3 pb-2 pt-1 space-y-0.5 bg-stone-50/60">
                                    {m.members.map(mb => (
                                      <div key={mb.uid} className="flex items-center gap-2 px-1 py-1">
                                        <div className="w-5 h-5 rounded-full bg-stone-200 flex items-center justify-center text-3xs font-black text-stone-500 overflow-hidden flex-shrink-0">
                                          {mb.photoURL
                                            ? <img src={mb.photoURL} className="w-full h-full object-cover" alt="" />
                                            : (mb.displayName || '?').charAt(0).toUpperCase()
                                          }
                                        </div>
                                        <span className="text-tiny text-stone-600 flex-1 truncate">
                                          {mb.displayName}
                                          {mb.uid === currentUser?.uid && (
                                            <span className="text-stone-400"> ({t('profile.live_sharing.you', 'du')})</span>
                                          )}
                                        </span>
                                        {mb.role === 'owner' && (
                                          <span className="text-4xs font-black text-amber-600 uppercase tracking-widest">{t('profile.live_sharing.role_owner', 'Eier')}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* Tur-knapp med undermeny (turlogg + posisjonsdeling) — hidden during navigation */}
              {!isNavigatingMobile && (
                <TurMenu
                  isPaidUser={isPaidUser}
                  hasGpsAccess={isPaidUser || hasTierAccess({ tier: userTier }, 'gps_tracking')}
                  trackingStatus={trackingStatus}
                  onStartTracking={() => (isPaidUser || hasTierAccess({ tier: userTier }, 'gps_tracking')) ? onStartTracking?.() : showNudge('gps_tracking')}
                  onOpenMyTracks={() => onOpenMyTracks?.()}
                  isSharing={isSharing}
                  nearbyUsers={nearbyUsers}
                  onStartSharing={onStartSharing}
                  onStopSharing={onStopSharing}
                  shareError={shareError}
                  currentUser={currentUser}
                  collaborativeMaps={collaborativeMaps}
                  activeGroupId={activeGroupId}
                  activeGroupName={activeGroupName}
                  groupViewers={groupViewers}
                />
              )}
              {/* Admin — desktop only, same icon row */}
              {isAdmin && !isMobile && onOpenAdmin && (
                <button
                  onClick={onOpenAdmin}
                  className="relative w-11 h-11 rounded-xl bg-drab border border-drab-line flex items-center justify-center text-[var(--text-secondary)] shadow-xl active:scale-95 transition-all pointer-events-auto"
                  title={t("auto.map.title.admin", "Admin")}
                >
                  <ShieldAlert size={18} />
                  {adminBadgeCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full bg-accent border-2 border-drab-2 flex items-center justify-center">
                      <span className="text-[7px] font-black text-[var(--ground)]">{adminBadgeCount}</span>
                    </span>
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {showFireDangerModal && (
        <FireDangerModal
          status={fireDanger}
          onClose={() => setShowFireDangerModal(false)}
        />
      )}
      {isMobile && (
        <MobileMapToolsSheet
          isOpen={showMobileToolsSheet}
          onClose={() => setShowMobileToolsSheet(false)}
          onShareView={handleShareCurrentView}
          shareCopied={shareCopied}
          onCreateFromPhoto={onCreateFromPhoto ? async () => {
            try {
              const result = await pickPhoto();
              if (result) onCreateFromPhoto([result.file], result.coords);
            } catch (e) {
              console.warn('Photo pick error:', e);
              toast(t('toast.photo_pick_error', 'Kunne ikke laste bildet. Prøv igjen.'), 'error');
            }
          } : undefined}
          trackingStatus={trackingStatus}
          onStartTracking={() => (isPaidUser || hasTierAccess({ tier: userTier }, 'gps_tracking')) ? onStartTracking?.() : showNudge('gps_tracking')}
          onOpenMyTracks={() => onOpenMyTracks?.()}
          showSearchButton={showSearchButton && !isTooZoomedOut}
          onSearchThisArea={() => { onSearchThisArea(); setShowSearchButton(false); }}
          showFireDangerButton={showFireDangerButton}
          onOpenFireDanger={() => setShowFireDangerModal(true)}
          isAdmin={!!isAdmin}
          adminBadgeCount={adminBadgeCount}
          onOpenAdminInbox={onOpenAdminInbox}
        />
      )}
      {isPaidUser && (
        <OfflineSuggestNudge
          enabled={TILE_CACHE_ENABLED && TILE_PREFETCH_ENABLED}
          onAccept={(bounds) => {
            const map = leafletMapRef.current;
            if (map && (map as any)._loaded) {
              map.fitBounds(
                [[bounds.latMin, bounds.lonMin], [bounds.latMax, bounds.lonMax]],
                { padding: [40, 40], animate: true, duration: 1.2 }
              );
            }
            // La kartet ramme området før nedlastings-panelet (som bruker currentViewBounds) åpnes.
            setTimeout(() => setShowOfflineManager(true), 1300);
          }}
        />
      )}

      {showOfflineManager && (
        <OfflineMapManager
          currentViewBounds={currentViewBounds}
          onClose={() => setShowOfflineManager(false)}
          onFlyTo={(bounds) => {
            if ((leafletMapRef.current as any)?._loaded) leafletMapRef.current.fitBounds(
              [[bounds.latMin, bounds.lonMin], [bounds.latMax, bounds.lonMax]],
              { padding: [40, 40], animate: true, duration: 1.5 }
            );
          }}
          isPaidUser={isPaidUser}
          onOpenUpgrade={() => { setShowOfflineManager(false); showNudge('offline_maps'); }}
        />
      )}

      {/* Floating nearest-spot mini-card — phone only during nav (tablet/desktop uses FollowSpotOverlay) */}
      {isMobilePhone && isFollowingRoute && nearestSpot && !is3DMode && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-[3100] bg-white border border-stone-200 rounded-2xl shadow-2xl pointer-events-auto"
          style={{ minWidth: 260, maxWidth: isPhoneLandscape ? 300 : 360, bottom: isPhoneLandscape ? 12 : 72 }}
          onClick={() => onSelectSpot(nearestSpot.id)}
        >
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className="shrink-0 w-11 h-11 rounded-xl overflow-hidden bg-stone-200">
              <img
                src={nearestSpot.photoUrls?.[0] || nearestSpot.auto_image_url || getSatelliteUrl(nearestSpot.location.latitude, nearestSpot.location.longitude)}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {getSpotMiniIcon(nearestSpot)}
                <span className="text-tiny font-black text-stone-800 truncate">{nearestSpot.name || 'Ukjent plass'}</span>
                {nearestSpot.isVerified && <Star size={10} className="text-amber-400 shrink-0" fill="currentColor" />}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                {nearestSpotDistanceM != null && !Number.isNaN(nearestSpotDistanceM) && (
                  <span className="text-2xs font-bold text-stone-500 flex items-center gap-1">
                    <Navigation size={9} className="text-emerald-500" />
                    {nearestSpotDistanceM < 1000 ? `${Math.round(nearestSpotDistanceM)} m` : `${(nearestSpotDistanceM / 1000).toFixed(1)} km`}
                  </span>
                )}
                {nearestSpot.score != null && nearestSpot.score > 0 && (
                  <span className="text-2xs font-bold text-stone-400">{nearestSpot.score.toFixed(1)}/10</span>
                )}
              </div>
            </div>
            <button
              className="shrink-0 px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-3xs font-bold uppercase tracking-wider transition-colors active:scale-95"
              onClick={(e) => { e.stopPropagation(); setShow2dSpotPreview(true); }}
            >
              {t("auto.map.vis", "Vis")}
            </button>
          </div>
        </div>
      )}

      {/* SpotPreviewOverlay for 2D mobile */}
      {show2dSpotPreview && nearestSpot && (
        <SpotPreviewOverlay
          spot={nearestSpot}
          distanceM={nearestSpotDistanceM ?? undefined}
          onClose={() => setShow2dSpotPreview(false)}
          onDetails={(id) => { setShow2dSpotPreview(false); if (onShowSpotDetails) onShowSpotDetails(id); else onSelectSpot(id); }}
          userPosition={userPositionProp}
          onDownloadArea={() => { if (onDownloadSpotArea) onDownloadSpotArea(nearestSpot.id); }}
          vehicleMarkerEnabled={currentUser?.vehicleMarkerEnabled}
          vehicleMarkerCategory={currentUser?.activeVehicleCategory}
          vehicleMarkerPhotoUrl={currentUser?.activeVehiclePhotoUrl}
          vehicleMarkerAiAvatarSvg={currentUser?.activeVehicleAiAvatarSvg}
        />
      )}
    </div>
  );
};

export default React.memo(MapComponent);

function activeTrailIdOrCoords(activeHikingTrailId: string | null, activeTrailCoords: [number, number][] | null): boolean {
  return !!activeHikingTrailId && !!activeTrailCoords && activeTrailCoords.length > 1;
}
